import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase } from "./supabaseClient";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Scroll-reveal wrapper — animates children when they enter viewport ─── */
function ScrollReveal({ children, delay, direction }) {
  var d = delay || 0;
  var dir = direction || "up";
  var yStart = dir === "up" ? 40 : dir === "down" ? -40 : 0;
  var xStart = dir === "left" ? 40 : dir === "right" ? -40 : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: yStart, x: xStart }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: d, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Page transition variants ─── */
var pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};
var pageTransition = { duration: 0.25, ease: [0.16, 1, 0.3, 1] };

/* ─── Font Loader — DM Sans (modern) + JetBrains Mono ─── */
function FontLoader() {
  useEffect(() => {
    if (document.getElementById("hl-fonts")) return;
    const l = document.createElement("link");
    l.id = "hl-fonts";
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap";
    document.head.appendChild(l);

    /* Premium global styles */
    if (!document.getElementById("hl-premium")) {
      const s = document.createElement("style");
      s.id = "hl-premium";
      s.textContent = [
        "*, *::before, *::after { transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); }",
        "html { scroll-behavior: smooth; }",
        "::-webkit-scrollbar { width: 6px; }",
        "::-webkit-scrollbar-track { background: transparent; }",
        "::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.15); border-radius: 999px; }",
        "::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.3); }",
        "::selection { background: rgba(6,182,212,0.2); color: inherit; }",
        "@keyframes premiumFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }",
        "@keyframes premiumScale { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }",
        "@keyframes shimmerSlide { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }",
        "@keyframes subtlePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.7; } }",
        "@keyframes pageSlideIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }",
        "@keyframes morphBlob { 0% { border-radius: 40% 60% 60% 40% / 60% 30% 70% 40%; } 25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; } 50% { border-radius: 60% 40% 30% 70% / 40% 70% 60% 30%; } 75% { border-radius: 40% 60% 50% 40% / 60% 30% 60% 50%; } 100% { border-radius: 40% 60% 60% 40% / 60% 30% 70% 40%; } }",
        "@keyframes borderGlow { 0% { opacity: 0.3; } 50% { opacity: 0.7; } 100% { opacity: 0.3; } }",
        "@keyframes floatSlow { 0%,100% { transform: translateY(0) rotate(0deg); } 33% { transform: translateY(-8px) rotate(1deg); } 66% { transform: translateY(4px) rotate(-1deg); } }",
        "@keyframes scaleIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }",
        "@keyframes glowPulse { 0%,100% { box-shadow: 0 0 20px rgba(6,182,212,0.05); } 50% { box-shadow: 0 0 40px rgba(6,182,212,0.12); } }",
        "@keyframes sidebarItemIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }",
        ".premium-card { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s, border-color 0.4s; }",
        ".premium-card:hover { transform: translateY(-3px) scale(1.01); }",
        ".motion-card { transition: all 0.5s cubic-bezier(0.34,1.56,0.64,1); }",
        ".motion-card:hover { transform: translateY(-4px) scale(1.02); }",
      ].join("\n");
      document.head.appendChild(s);
    }
  }, []);
  return null;
}

const hd = "'DM Sans', system-ui, sans-serif";
const bd = "'DM Sans', system-ui, sans-serif";
const mono = "'JetBrains Mono', monospace";

/* ─── Theme Context ─── */
var ThemeCtx = createContext({ dark: true, toggle: function() {} });
function useTheme() { return useContext(ThemeCtx); }

/* ─── Toast Notification System ─── */
var toastList = [];
var toastListeners = [];
function addToast(msg, type) {
  var id = Date.now() + Math.random();
  toastList = [{ id: id, msg: msg, type: type || "info" }].concat(toastList).slice(0, 5);
  toastListeners.forEach(function(fn) { fn(toastList.slice()); });
  setTimeout(function() {
    toastList = toastList.filter(function(t) { return t.id !== id; });
    toastListeners.forEach(function(fn) { fn(toastList.slice()); });
  }, 4000);
}
function ToastContainer() {
  var s = useState([]);
  var toasts = s[0];
  var setToasts = s[1];
  useEffect(function() {
    toastListeners.push(setToasts);
    return function() { toastListeners = toastListeners.filter(function(fn) { return fn !== setToasts; }); };
  }, []);
  var cols = { info: "#22d3ee", success: "#06b6d4", warning: "#fbbf24", error: "#ef4444" };
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2" style={{ pointerEvents: "none" }}>
      {toasts.map(function(t) {
        return (
          <div key={t.id} className="flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg" style={{
            pointerEvents: "auto", fontFamily: bd, fontSize: 13, fontWeight: 400,
            background: "rgba(6,11,17,0.92)", border: "1px solid " + (cols[t.type] || cols.info) + "25",
            color: "white", backdropFilter: "blur(20px) saturate(1.8)", animation: "slideIn 0.35s cubic-bezier(0.16,1,0.3,1)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.03), 0 0 30px " + (cols[t.type] || cols.info) + "12",
            letterSpacing: "0.01em",
          }}>
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: cols[t.type] || cols.info, boxShadow: "0 0 8px " + (cols[t.type] || cols.info) + "60" }} />
            <span style={{ lineHeight: 1.4 }}>{t.msg}</span>
          </div>
        );
      })}
      <style>{"@keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }"}</style>
    </div>
  );
}

/* ─── Grid background ─── */
function GridBG() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ opacity: 0.03 }}>
      <div style={{
        width: "100%", height: "100%",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />
    </div>
  );
}

/* ─── Glow orbs (used by signup page) ─── */
function GlowOrbs() {
  return (
    <>
      <div className="absolute rounded-full" style={{ width: 600, height: 600, top: -200, left: "20%", background: "radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)" }} />
      <div className="absolute rounded-full" style={{ width: 500, height: 500, bottom: -150, right: "10%", background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)" }} />
      <div className="absolute rounded-full" style={{ width: 300, height: 300, top: "40%", left: -100, background: "radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)" }} />
    </>
  );
}

/* ─── Quantum Shield Illustration (animated SVG art) ─── */
function QuantumShieldArt() {
  var timeState = useState(0);
  var time = timeState[0];
  var setTime = timeState[1];

  useEffect(function() {
    var raf;
    var start = 0;
    function tick(t) {
      if (!start) start = t;
      setTime((t - start) / 1000);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return function() { cancelAnimationFrame(raf); };
  }, []);

  // Orbiting particles
  var particles = [];
  for (var i = 0; i < 12; i++) {
    var a = (i / 12) * Math.PI * 2 + time * 0.5;
    var r = 140 + Math.sin(time * 0.8 + i) * 20;
    particles.push({
      x: 200 + Math.cos(a) * r,
      y: 200 + Math.sin(a) * r * 0.6,
      size: 2 + Math.sin(time + i * 0.5) * 1,
      op: 0.4 + Math.sin(time * 1.2 + i) * 0.3,
    });
  }

  // Inner ring particles
  var inner = [];
  for (var j = 0; j < 8; j++) {
    var a2 = (j / 8) * Math.PI * 2 - time * 0.7;
    inner.push({
      x: 200 + Math.cos(a2) * 80,
      y: 200 + Math.sin(a2) * 80 * 0.6,
      op: 0.5 + Math.sin(time * 1.5 + j) * 0.3,
    });
  }

  // Quantum bit connections (entanglement lines)
  var connections = [];
  for (var k = 0; k < 6; k++) {
    var p1 = particles[k * 2];
    var p2 = inner[k % 8];
    if (p1 && p2) {
      connections.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, op: 0.1 + Math.sin(time + k) * 0.08 });
    }
  }

  return (
    <div className="relative" style={{ width: 400, height: 400 }}>
      <svg viewBox="0 0 400 400" className="w-full h-full">
        <defs>
          <radialGradient id="qGlow1">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.05" />
            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="qGlow2">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.15" />
            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="shieldGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="shieldFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.03" />
          </linearGradient>
          <filter id="qBlur"><feGaussianBlur stdDeviation="3" /></filter>
          <filter id="qBlur2"><feGaussianBlur stdDeviation="6" /></filter>
        </defs>

        {/* Background glow */}
        <circle cx="200" cy="200" r="180" fill="url(#qGlow1)" />
        <circle cx="200" cy="180" r="100" fill="url(#qGlow2)" />

        {/* Outer orbit ring */}
        <ellipse cx="200" cy="200" rx="140" ry="84" fill="none" stroke="#7c3aed" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 8" />
        <ellipse cx="200" cy="200" rx="142" ry="85" fill="none" stroke="#a78bfa" strokeWidth="0.3" opacity="0.1" filter="url(#qBlur)" />

        {/* Inner orbit ring */}
        <ellipse cx="200" cy="200" rx="80" ry="48" fill="none" stroke="#06b6d4" strokeWidth="0.5" opacity="0.25" strokeDasharray="3 6" />

        {/* Entanglement connection lines */}
        {connections.map(function(c, idx) {
          return <line key={"cn" + idx} x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} stroke="#a78bfa" strokeWidth="0.5" opacity={c.op} />;
        })}

        {/* Shield shape */}
        <path d="M200 100 L260 130 Q270 200 200 290 Q130 200 140 130 Z" fill="url(#shieldFill)" stroke="url(#shieldGrad)" strokeWidth="1.5" />
        {/* Shield glow */}
        <path d="M200 100 L260 130 Q270 200 200 290 Q130 200 140 130 Z" fill="none" stroke="#a78bfa" strokeWidth="3" opacity="0.15" filter="url(#qBlur)" />

        {/* Shield inner lines */}
        <path d="M200 120 L200 260" stroke="#a78bfa" strokeWidth="0.5" opacity="0.2" />
        <path d="M160 150 L240 150" stroke="#06b6d4" strokeWidth="0.5" opacity="0.15" />
        <path d="M155 180 L245 180" stroke="#06b6d4" strokeWidth="0.5" opacity="0.12" />
        <path d="M160 210 L240 210" stroke="#06b6d4" strokeWidth="0.5" opacity="0.1" />

        {/* Quantum lock icon in shield center */}
        <rect x="185" y="165" width="30" height="24" rx="4" fill="none" stroke="#a78bfa" strokeWidth="1.2" opacity="0.6" />
        <path d="M192 165 L192 157 Q192 150 200 150 Q208 150 208 157 L208 165" fill="none" stroke="#a78bfa" strokeWidth="1.2" opacity="0.6" />
        <circle cx="200" cy="177" r="3" fill="#a78bfa" opacity="0.7" />
        <line x1="200" y1="177" x2="200" y2="183" stroke="#a78bfa" strokeWidth="1.2" opacity="0.5" />

        {/* Outer orbiting particles */}
        {particles.map(function(p, idx) {
          return (
            <g key={"op" + idx}>
              <circle cx={p.x} cy={p.y} r={p.size + 3} fill="#7c3aed" opacity={p.op * 0.3} filter="url(#qBlur)" />
              <circle cx={p.x} cy={p.y} r={p.size} fill="#c4b5fd" opacity={p.op} />
            </g>
          );
        })}

        {/* Inner orbiting particles */}
        {inner.map(function(p, idx) {
          return (
            <g key={"ip" + idx}>
              <circle cx={p.x} cy={p.y} r="4" fill="#06b6d4" opacity={p.op * 0.2} filter="url(#qBlur)" />
              <circle cx={p.x} cy={p.y} r="1.5" fill="#67e8f9" opacity={p.op} />
            </g>
          );
        })}

        {/* Pulsing center beacon */}
        <circle cx="200" cy="195" r={12 + Math.sin(time * 2) * 4} fill="none" stroke="#a78bfa" strokeWidth="0.5" opacity={0.15 + Math.sin(time * 2) * 0.1} />
        <circle cx="200" cy="195" r={20 + Math.sin(time * 1.5) * 8} fill="none" stroke="#6366f1" strokeWidth="0.3" opacity={0.08 + Math.sin(time * 1.5) * 0.05} />

        {/* Corner decorative elements — hex grid dots */}
        {[
          [40, 60], [70, 40], [100, 55], [300, 55], [330, 40], [360, 60],
          [40, 340], [70, 360], [100, 345], [300, 345], [330, 360], [360, 340],
        ].map(function(pos, idx) {
          return <circle key={"hx" + idx} cx={pos[0]} cy={pos[1]} r="1" fill="#6366f1" opacity={0.15 + Math.sin(time * 0.8 + idx) * 0.1} />;
        })}

        {/* Data stream lines — top and bottom */}
        {[0, 1, 2, 3, 4].map(function(i) {
          var xOff = 80 + i * 60;
          var yStart = 30 + Math.sin(time * 0.6 + i) * 5;
          return <line key={"ds" + i} x1={xOff} y1={yStart} x2={xOff} y2={yStart + 15} stroke="#06b6d4" strokeWidth="0.5" opacity={0.1 + Math.sin(time + i) * 0.06} />;
        })}
      </svg>
    </div>
  );
}

/* ─── Animated Floating Particles (Canvas) ─── */
function FloatingParticles() {
  var canvasRef = useState(null);
  var setCanvas = canvasRef[1];

  useEffect(function() {
    var canvas = document.getElementById("gloom-particles");
    if (!canvas) return;
    var ctx = canvas.getContext("2d");
    var w = canvas.width = window.innerWidth;
    var h = canvas.height = window.innerHeight * 3;
    var particles = [];
    for (var i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.2 - 0.1,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.3 + 0.05,
        c: i % 3 === 0 ? "139,92,246" : i % 3 === 1 ? "6,182,212" : "167,139,250",
      });
    }
    var raf;
    function draw() {
      ctx.clearRect(0, 0, w, h);
      for (var j = 0; j < particles.length; j++) {
        var p = particles[j];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(" + p.c + "," + p.o + ")";
        ctx.fill();
        // Draw faint connections to nearby particles
        for (var k = j + 1; k < particles.length; k++) {
          var p2 = particles[k];
          var dx = p.x - p2.x;
          var dy = p.y - p2.y;
          var dist = dx * dx + dy * dy;
          if (dist < 25000) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = "rgba(" + p.c + "," + (0.03 * (1 - dist / 25000)) + ")";
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    }
    draw();
    function onResize() { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight * 3; }
    window.addEventListener("resize", onResize);
    return function() { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);

  return <canvas id="gloom-particles" className="absolute inset-0 pointer-events-none z-0" style={{ opacity: 0.8 }} />;
}

/* ─── Scroll-triggered fade-in hook ─── */
function useScrollReveal(threshold) {
  var ref = useState(null);
  var visible = useState(false);

  useEffect(function() {
    var el = ref[0];
    if (!el) return;
    var obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) {
          visible[1](true);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: threshold || 0.15 });
    obs.observe(el);
    return function() { obs.disconnect(); };
  }, [ref[0]]);

  return { ref: ref, visible: visible[0], setRef: ref[1] };
}

/* ─── Animated counter ─── */
function AnimatedValue({ value, suffix }) {
  var display = useState(value);
  useEffect(function() {
    display[1](value);
  }, [value]);
  return <span>{display[0]}{suffix || ""}</span>;
}

/* ═══════════════════════════════════════════════
   LANDING PAGE — Quantum Proof Security Theme
   ═══════════════════════════════════════════════ */
function LandingPage({ onGetStarted, onLogin }) {
  /* ── State ── */
  var entered = useState(false);
  var show = entered[0];
  useEffect(function() {
    var t = setTimeout(function() { entered[1](true); }, 100);
    return function() { clearTimeout(t); };
  }, []);

  var landingThemeState = useState(false);
  var lt = landingThemeState[0];
  var setLt = landingThemeState[1];

  function scrollTo(id) {
    var el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /* Mouse parallax tracking */
  var mouseState = useState({ x: 0, y: 0 });
  var mouse = mouseState[0];
  var setMouse = mouseState[1];
  useEffect(function() {
    function handleMouse(e) {
      var cx = (e.clientX / window.innerWidth - 0.5) * 2;
      var cy = (e.clientY / window.innerHeight - 0.5) * 2;
      setMouse({ x: cx, y: cy });
    }
    window.addEventListener("mousemove", handleMouse);
    return function() { window.removeEventListener("mousemove", handleMouse); };
  }, []);

  /* Scroll progress + planet split */
  var scrollState = useState(0);
  var scrollProgress = scrollState[0];
  var setScrollProgress = scrollState[1];
  var splitState = useState(0);
  var planetSplit = splitState[0];
  var setPlanetSplit = splitState[1];
  useEffect(function() {
    function onScroll() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) setScrollProgress(scrollTop / docHeight);
      var el = document.getElementById("section-airdrop");
      if (!el) return;
      var rect = el.getBoundingClientRect();
      var winH = window.innerHeight;
      var raw = 1 - (rect.top / winH);
      /* 0–1 = split phase, 1–2 = shatter phase */
      var clamped = Math.max(0, Math.min(2, raw));
      setPlanetSplit(clamped);
    }
    window.addEventListener("scroll", onScroll, true);
    onScroll();
    return function() { window.removeEventListener("scroll", onScroll, true); };
  }, []);

  /* ── Canvas Particle Constellation ── */
  var canvasRef = useRef(null);
  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext("2d");
    var particles = [];
    var mousePos = { x: -1000, y: -1000 };
    var animId;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * 2;
    }
    resize();
    window.addEventListener("resize", resize);

    var count = 80;
    for (var i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.8 + 0.4,
        baseOpacity: Math.random() * 0.4 + 0.15,
        hue: Math.random() > 0.7 ? 190 : 265,
      });
    }

    function onMouse(e) {
      mousePos.x = e.clientX;
      mousePos.y = e.clientY + (window.pageYOffset || 0);
    }
    window.addEventListener("mousemove", onMouse);

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;

        var dx = p.x - mousePos.x;
        var dy = p.y - mousePos.y;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var glow = dist < 180 ? (1 - dist / 180) * 0.7 : 0;
        var finalR = p.r + glow * 3;
        var finalOp = Math.min(1, p.baseOpacity + glow);

        ctx.beginPath();
        ctx.arc(p.x, p.y, finalR, 0, Math.PI * 2);
        if (p.hue === 190) {
          ctx.fillStyle = "rgba(6,182,212," + finalOp + ")";
        } else {
          ctx.fillStyle = "rgba(139,92,246," + finalOp + ")";
        }
        ctx.fill();

        if (glow > 0.1) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, finalR + 4, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(139,92,246," + (glow * 0.15) + ")";
          ctx.fill();
        }

        for (var j = i + 1; j < particles.length; j++) {
          var p2 = particles[j];
          var ddx = p.x - p2.x;
          var ddy = p.y - p2.y;
          var d = Math.sqrt(ddx * ddx + ddy * ddy);
          if (d < 130) {
            var lineOp = 0.06 * (1 - d / 130);
            var midX = (p.x + p2.x) / 2;
            var midY = (p.y + p2.y) / 2;
            var mouseDist = Math.sqrt((midX - mousePos.x) * (midX - mousePos.x) + (midY - mousePos.y) * (midY - mousePos.y));
            if (mouseDist < 200) lineOp += (1 - mouseDist / 200) * 0.12;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = "rgba(139,92,246," + lineOp + ")";
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    }
    draw();

    return function() {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  /* ── Animated Counters ── */
  var counterState = useState({ tvl: 0, users: 0, apy: 0 });
  var counters = counterState[0];
  var setCounters = counterState[1];
  var counterStarted = useRef(false);

  /* Fetch stat targets from Supabase site_stats table */
  var statTargetsState = useState({ tvl: 3100000, users: 1809, apy: 6.2, security: "A+", audits: "3" });
  var statTargets = statTargetsState[0];
  var setStatTargets = statTargetsState[1];
  useEffect(function() {
    supabase.from("site_stats").select("*").single()
      .then(function(result) {
        if (result.data) {
          setStatTargets({
            tvl: result.data.tvl || 3100000,
            users: result.data.active_users || 1809,
            apy: result.data.base_apy || 6.2,
            security: result.data.security_grade || "A+",
            audits: result.data.security_audits || "3"
          });
        }
      });
  }, []);

  useEffect(function() {
    var observer = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting && !counterStarted.current) {
        counterStarted.current = true;
        var duration = 2000;
        var start = performance.now();
        function tick(now) {
          var elapsed = now - start;
          var progress = Math.min(elapsed / duration, 1);
          /* Ease out cubic */
          var ease = 1 - Math.pow(1 - progress, 3);
          setCounters({
            tvl: Math.floor(statTargets.tvl * ease),
            users: Math.floor(statTargets.users * ease),
            apy: parseFloat((statTargets.apy * ease).toFixed(1)),
          });
          if (progress < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    var el = document.getElementById("stats-counter-section");
    if (el) observer.observe(el);
    return function() { observer.disconnect(); };
  }, [statTargets]);

  /* ── Navbar scroll state ── */
  var navSolid = useState(false);
  var isNavSolid = navSolid[0];
  useEffect(function() {
    function checkNav() {
      navSolid[1]((window.pageYOffset || 0) > 60);
    }
    window.addEventListener("scroll", checkNav);
    return function() { window.removeEventListener("scroll", checkNav); };
  }, []);

  var NAV_SECTIONS = { Protocol: "section-protocol", Docs: "section-docs", Security: "section-security" };

  /* ── Format helpers ── */
  function formatTVL(n) {
    if (n >= 1000000) return "$" + (n / 1000000).toFixed(1) + "M";
    if (n >= 1000) return "$" + (n / 1000).toFixed(0) + "K";
    return "$" + n;
  }

  return (
    <div className="relative flex flex-col min-h-screen w-full overflow-x-hidden overflow-y-auto" style={{ fontFamily: bd, background: lt ? "#f8f9fc" : "#030108" }}>

      {/* ── KEYFRAMES ── */}
      <style>{"\n@keyframes heroFadeUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }\n@keyframes heroFadeScale { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }\n@keyframes navSlideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }\n@keyframes cardReveal { from { opacity: 0; transform: translateY(40px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }\n@keyframes pulseGlow { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.9; transform: scale(1.08); } }\n@keyframes borderShimmer { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }\n@keyframes subtitleReveal { from { opacity: 0; transform: translateY(12px); filter: blur(6px); } to { opacity: 1; transform: translateY(0); filter: blur(0); } }\n@keyframes logoReveal { from { opacity: 0; transform: scale(0.8) rotate(-10deg); filter: blur(10px); } to { opacity: 1; transform: scale(1) rotate(0deg); filter: blur(0); } }\n@keyframes shimmerText { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }\n@keyframes floatOrb1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(60px,-40px) scale(1.1); } 66% { transform: translate(-30px,30px) scale(0.95); } }\n@keyframes floatOrb2 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-50px,30px) scale(0.9); } 66% { transform: translate(40px,-50px) scale(1.05); } }\n@keyframes floatOrb3 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(30px,40px) scale(1.1); } }\n@keyframes planetRotate { 0% { transform: translate(-50%,-50%) rotate(0deg); } 100% { transform: translate(-50%,-50%) rotate(360deg); } }\n@keyframes planetPulse { 0%,100% { box-shadow: 0 0 80px rgba(120,80,200,0.3), 0 0 160px rgba(80,50,180,0.15); } 50% { box-shadow: 0 0 120px rgba(120,80,200,0.45), 0 0 200px rgba(80,50,180,0.25); } }\n@keyframes beamRotate { 0% { transform: translate(-50%,-50%) rotate(0deg); } 100% { transform: translate(-50%,-50%) rotate(360deg); } }\n@keyframes crackPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }\n@keyframes twinkle { 0%,100% { opacity: 0.15; } 50% { opacity: 0.9; } }\n@keyframes floatCrystal1 { 0%,100% { transform: translate(0,0) rotate(0deg); } 25% { transform: translate(15px,-20px) rotate(45deg); } 50% { transform: translate(-10px,-35px) rotate(90deg); } 75% { transform: translate(20px,-15px) rotate(135deg); } }\n@keyframes floatCrystal2 { 0%,100% { transform: translate(0,0) rotate(0deg); } 25% { transform: translate(-20px,15px) rotate(-30deg); } 50% { transform: translate(15px,-20px) rotate(-60deg); } 75% { transform: translate(-10px,10px) rotate(-90deg); } }\n@keyframes floatCrystal3 { 0%,100% { transform: translate(0,0) rotate(0deg); } 33% { transform: translate(10px,20px) rotate(60deg); } 66% { transform: translate(-15px,-10px) rotate(120deg); } }\n@keyframes nebulaFlow { 0% { transform: translateX(-5%) scaleY(1); } 50% { transform: translateX(5%) scaleY(1.1); } 100% { transform: translateX(-5%) scaleY(1); } }\n@keyframes ringRotate { 0% { transform: translate(-50%,-50%) rotate(0deg); } 100% { transform: translate(-50%,-50%) rotate(360deg); } }\n@keyframes pulseRing { 0% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.05); opacity: 0.8; } 100% { transform: scale(1); opacity: 0.5; } }\n@keyframes gradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }\n@keyframes lineGrow { from { width: 0; } to { width: 100%; } }\n@keyframes rotateGradient { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }\n@keyframes iconFloat { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-6px) rotate(5deg); } }\n@keyframes iconPulse { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 6px rgba(139,92,246,0.3)); } 50% { transform: scale(1.12); filter: drop-shadow(0 0 14px rgba(139,92,246,0.6)); } }\n@keyframes cardGlow { 0%,100% { box-shadow: 0 0 20px rgba(139,92,246,0.05), 0 4px 30px rgba(0,0,0,0.3); } 50% { box-shadow: 0 0 40px rgba(139,92,246,0.12), 0 4px 30px rgba(0,0,0,0.3); } }\n@keyframes cardGlowCyan { 0%,100% { box-shadow: 0 0 20px rgba(6,182,212,0.05), 0 4px 30px rgba(0,0,0,0.3); } 50% { box-shadow: 0 0 40px rgba(6,182,212,0.12), 0 4px 30px rgba(0,0,0,0.3); } }\n@keyframes scanLine { 0% { top: -2px; } 100% { top: 100%; } }\n@keyframes borderFlow { 0% { background-position: 0% 0%; } 100% { background-position: 200% 200%; } }\n@keyframes floatUp { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }\n@keyframes floatDown { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(10px); } }\n@keyframes orbitalSpin { 0% { transform: rotate(0deg) translateX(150px) rotate(0deg); } 100% { transform: rotate(360deg) translateX(150px) rotate(-360deg); } }\n@keyframes orbitalSpin2 { 0% { transform: rotate(0deg) translateX(200px) rotate(0deg); } 100% { transform: rotate(-360deg) translateX(200px) rotate(360deg); } }\n@keyframes gridFade { 0%,100% { opacity: 0.03; } 50% { opacity: 0.07; } }\n@keyframes glowTrail { 0% { left: -20%; opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { left: 120%; opacity: 0; } }\n@keyframes breatheCard { 0%,100% { border-color: rgba(139,92,246,0.08); } 50% { border-color: rgba(139,92,246,0.2); } }\n@keyframes breatheCardCyan { 0%,100% { border-color: rgba(6,182,212,0.08); } 50% { border-color: rgba(6,182,212,0.2); } }\n.glow-card { transition: all 0.4s cubic-bezier(0.16,1,0.3,1); }\n.glow-card:hover { box-shadow: 0 0 50px rgba(139,92,246,0.15), 0 8px 40px rgba(0,0,0,0.4) !important; border-color: rgba(139,92,246,0.25) !important; transform: translateY(-8px) scale(1.02); }\n.glow-card-cyan:hover { box-shadow: 0 0 50px rgba(6,182,212,0.15), 0 8px 40px rgba(0,0,0,0.4) !important; border-color: rgba(6,182,212,0.25) !important; }\n"}</style>

      {/* ── SCROLL PROGRESS BAR ── */}
      <div style={{ position: "fixed", top: 0, left: 0, height: 2, zIndex: 9999, background: "linear-gradient(90deg, #8b5cf6, #06b6d4, #8b5cf6)", width: (scrollProgress * 100) + "%", transition: "width 0.1s linear", boxShadow: "0 0 10px rgba(139,92,246,0.5)" }} />

      {/* ── CANVAS PARTICLE CONSTELLATION ── */}
      {!lt && (
        <canvas ref={canvasRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "200%", pointerEvents: "none", zIndex: 1 }} />
      )}

      {/* ── FLOATING GRADIENT ORBS ── */}
      {!lt && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{ position: "absolute", top: "10%", left: "15%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0.02) 40%, transparent 70%)", filter: "blur(60px)", animation: "floatOrb1 25s ease-in-out infinite" }} />
          <div style={{ position: "absolute", top: "30%", right: "10%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(6,182,212,0.06) 0%, rgba(6,182,212,0.015) 40%, transparent 70%)", filter: "blur(50px)", animation: "floatOrb2 30s ease-in-out infinite" }} />
          <div style={{ position: "absolute", top: "60%", left: "40%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 60%)", filter: "blur(70px)", animation: "floatOrb3 35s ease-in-out infinite" }} />
          <div style={{ position: "absolute", top: "5%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.04) 0%, transparent 60%)", filter: "blur(40px)", animation: "floatOrb2 20s ease-in-out 5s infinite" }} />
        </div>
      )}

      {/* ── STAR FIELD ── */}
      {!lt && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div style={{ position: "absolute", inset: 0, transform: "translate(" + (mouse.x * -8) + "px, " + (mouse.y * -8) + "px)", transition: "transform 0.3s ease-out" }}>
            {[
              { x: "5%", y: "8%", s: 1.5, d: 3 }, { x: "12%", y: "22%", s: 1, d: 4.5 }, { x: "20%", y: "5%", s: 2, d: 2.5 },
              { x: "28%", y: "35%", s: 1, d: 5 }, { x: "35%", y: "12%", s: 1.5, d: 3.5 }, { x: "42%", y: "28%", s: 1, d: 6 },
              { x: "55%", y: "8%", s: 2, d: 2 }, { x: "62%", y: "18%", s: 1, d: 4 }, { x: "70%", y: "32%", s: 1.5, d: 5.5 },
              { x: "78%", y: "10%", s: 1, d: 3 }, { x: "85%", y: "25%", s: 2, d: 4 }, { x: "92%", y: "15%", s: 1, d: 6 },
              { x: "8%", y: "45%", s: 1.5, d: 3.5 }, { x: "18%", y: "55%", s: 1, d: 5 }, { x: "30%", y: "48%", s: 2, d: 2 },
              { x: "45%", y: "42%", s: 1, d: 4.5 }, { x: "60%", y: "50%", s: 1.5, d: 3 }, { x: "75%", y: "45%", s: 1, d: 5.5 },
              { x: "88%", y: "40%", s: 2, d: 2.5 }, { x: "95%", y: "52%", s: 1, d: 4 },
              { x: "15%", y: "65%", s: 1.5, d: 6 }, { x: "25%", y: "72%", s: 1, d: 3 }, { x: "40%", y: "68%", s: 2, d: 4.5 },
              { x: "52%", y: "75%", s: 1, d: 5 }, { x: "68%", y: "62%", s: 1.5, d: 2.5 }, { x: "82%", y: "70%", s: 1, d: 4 },
            ].map(function(star, si) {
              return <div key={"s" + si} className="absolute rounded-full" style={{ left: star.x, top: star.y, width: star.s, height: star.s, background: "white", animation: "twinkle " + star.d + "s ease-in-out " + (si * 0.3) + "s infinite" }} />;
            })}
          </div>
          <div style={{ position: "absolute", inset: 0, transform: "translate(" + (mouse.x * -15) + "px, " + (mouse.y * -15) + "px)", transition: "transform 0.25s ease-out" }}>
            {[
              { x: "8%", y: "15%", s: 2.5, d: 2 }, { x: "22%", y: "30%", s: 2, d: 3 }, { x: "48%", y: "18%", s: 3, d: 2.5 },
              { x: "72%", y: "22%", s: 2, d: 4 }, { x: "88%", y: "35%", s: 2.5, d: 3 },
              { x: "15%", y: "50%", s: 2, d: 3.5 }, { x: "55%", y: "55%", s: 3, d: 2 }, { x: "80%", y: "58%", s: 2, d: 4.5 },
            ].map(function(star, si) {
              return <div key={"s2" + si} className="absolute rounded-full" style={{ left: star.x, top: star.y, width: star.s, height: star.s, background: "rgba(180,160,255,0.9)", boxShadow: "0 0 6px rgba(180,160,255,0.5)", animation: "twinkle " + star.d + "s ease-in-out " + (si * 0.5) + "s infinite" }} />;
            })}
          </div>
        </div>
      )}

      {/* ── PLANET with scroll-triggered split + shatter ── */}
      {!lt && (function() {
        /* Phase 1: 0–1 = split apart. Phase 2: 1–2 = shatter into shards */
        var split = Math.min(planetSplit, 1);
        var shatter = Math.max(0, planetSplit - 1); /* 0 to 1 */
        var planetOpacity = shatter > 0 ? Math.max(0, 1 - shatter * 2.5) : 1;

        /* Shard definitions — each flies to a screen edge */
        var shards = [
          { x: -120, y: -80, rot: -25, endX: -900, endY: -500, endRot: -180, w: 65, h: 80, color: "#3a1d7a" },
          { x: 60, y: -90, rot: 15, endX: 850, endY: -450, endRot: 200, w: 55, h: 70, color: "#2a1660" },
          { x: -80, y: 40, rot: -40, endX: -800, endY: 400, endRot: -220, w: 50, h: 65, color: "#4a2d8a" },
          { x: 90, y: 50, rot: 30, endX: 750, endY: 500, endRot: 190, w: 60, h: 75, color: "#180e40" },
          { x: -40, y: -120, rot: 10, endX: -400, endY: -600, endRot: -150, w: 45, h: 55, color: "#2d1565" },
          { x: 100, y: -30, rot: -20, endX: 900, endY: -200, endRot: 240, w: 40, h: 50, color: "#3a1d7a" },
          { x: -110, y: 80, rot: 35, endX: -700, endY: 550, endRot: -260, w: 55, h: 60, color: "#1a0845" },
          { x: 50, y: 100, rot: -15, endX: 600, endY: 600, endRot: 170, w: 48, h: 58, color: "#2a1660" },
          { x: 0, y: -110, rot: 5, endX: 200, endY: -650, endRot: 130, w: 42, h: 52, color: "#4a2d8a" },
          { x: -60, y: -50, rot: -50, endX: -950, endY: -100, endRot: -300, w: 38, h: 48, color: "#180e40" },
          { x: 30, y: 80, rot: 45, endX: 500, endY: 650, endRot: 280, w: 35, h: 45, color: "#251260" },
          { x: -20, y: 110, rot: -10, endX: -300, endY: 700, endRot: -160, w: 50, h: 55, color: "#2d1565" },
        ];

        return (
          <>
            {/* Main planet (fades out during shatter) */}
            <div className="absolute pointer-events-none" style={{ zIndex: 2, top: "42%", left: "50%", width: 420, height: 420, transform: "translate(calc(-50% + " + (mouse.x * -20) + "px), calc(-50% + " + (mouse.y * -20) + "px))", transition: "transform 0.4s ease-out", opacity: planetOpacity }}>
              {/* Glow behind planet */}
              <div style={{ position: "absolute", top: "50%", left: "50%", width: 420, height: 420, transform: "translate(-50%,-50%)", borderRadius: "50%", boxShadow: "0 0 80px rgba(120,80,200,0.3), 0 0 160px rgba(80,50,180,0.15)", animation: "planetPulse 6s ease-in-out infinite", pointerEvents: "none" }} />
              {/* Left half */}
              <div style={{ position: "absolute", top: 0, left: 0, width: "50%", height: "100%", overflow: "hidden", transform: "translateX(" + (split * -38) + "px) rotate(" + (split * -3) + "deg)", transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)" }}>
                <div style={{ width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #4a2d8a 0%, #2a1660 25%, #180e40 50%, #0c0820 75%, #050510 100%)", overflow: "hidden", position: "relative" }}>
                  <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "2px solid rgba(140,100,255,0.15)", background: "radial-gradient(circle at 30% 30%, rgba(140,100,255,0.08) 0%, transparent 50%)" }} />
                  <div style={{ position: "absolute", top: "30%", left: 0, width: "100%", height: "8%", background: "linear-gradient(90deg, transparent, rgba(100,60,200,0.12), rgba(140,90,255,0.08), transparent)", transform: "rotate(-5deg)" }} />
                  <div style={{ position: "absolute", top: "45%", left: 0, width: "100%", height: "12%", background: "linear-gradient(90deg, transparent, rgba(80,40,180,0.15), rgba(120,70,240,0.1), rgba(80,40,180,0.08), transparent)", transform: "rotate(-3deg)" }} />
                  <div style={{ position: "absolute", top: "62%", left: 0, width: "100%", height: "6%", background: "linear-gradient(90deg, transparent, rgba(60,30,140,0.1), transparent)", transform: "rotate(-7deg)" }} />
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "linear-gradient(105deg, transparent 45%, rgba(0,0,0,0.6) 100%)" }} />
                </div>
              </div>
              {/* Right half */}
              <div style={{ position: "absolute", top: 0, right: 0, width: "50%", height: "100%", overflow: "hidden", transform: "translateX(" + (split * 38) + "px) rotate(" + (split * 3) + "deg)", transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)" }}>
                <div style={{ width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #4a2d8a 0%, #2a1660 25%, #180e40 50%, #0c0820 75%, #050510 100%)", overflow: "hidden", position: "relative", marginLeft: "-100%" }}>
                  <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "2px solid rgba(140,100,255,0.15)", background: "radial-gradient(circle at 30% 30%, rgba(140,100,255,0.08) 0%, transparent 50%)" }} />
                  <div style={{ position: "absolute", top: "30%", left: 0, width: "100%", height: "8%", background: "linear-gradient(90deg, transparent, rgba(100,60,200,0.12), rgba(140,90,255,0.08), transparent)", transform: "rotate(-5deg)" }} />
                  <div style={{ position: "absolute", top: "45%", left: 0, width: "100%", height: "12%", background: "linear-gradient(90deg, transparent, rgba(80,40,180,0.15), rgba(120,70,240,0.1), rgba(80,40,180,0.08), transparent)", transform: "rotate(-3deg)" }} />
                  <div style={{ position: "absolute", top: "62%", left: 0, width: "100%", height: "6%", background: "linear-gradient(90deg, transparent, rgba(60,30,140,0.1), transparent)", transform: "rotate(-7deg)" }} />
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "linear-gradient(105deg, transparent 45%, rgba(0,0,0,0.6) 100%)" }} />
                </div>
              </div>
              {/* Crack line */}
              <div style={{ position: "absolute", top: "5%", left: "50%", width: 2, height: "90%", transform: "translateX(-50%)", background: "linear-gradient(180deg, transparent 0%, rgba(220,180,255," + (split * 0.9) + ") 15%, rgba(255,200,255," + split + ") 50%, rgba(220,180,255," + (split * 0.9) + ") 85%, transparent 100%)", boxShadow: "0 0 " + (split * 20) + "px rgba(200,150,255," + (split * 0.6) + "), 0 0 " + (split * 40) + "px rgba(160,100,255," + (split * 0.3) + ")", opacity: split > 0.05 ? 1 : 0 }} />
              {/* Inner light */}
              <div style={{ position: "absolute", top: "50%", left: "50%", width: 420, height: 420, transform: "translate(-50%,-50%)", background: "radial-gradient(circle, rgba(220,180,255," + (split * 0.35) + ") 0%, rgba(160,100,255," + (split * 0.2) + ") 20%, transparent 60%)", borderRadius: "50%", opacity: split > 0.05 ? 1 : 0 }} />
              {/* Light beams */}
              {split > 0.1 && [0, 45, 90, 135, 180, 225, 270, 315].map(function(angle, bi) {
                var beamOpacity = Math.max(0, (split - 0.1) * 1.1) * (bi % 2 === 0 ? 0.7 : 0.4);
                var beamLen = 80 + split * 200;
                return <div key={"beam" + bi} style={{ position: "absolute", top: "50%", left: "50%", width: 2 + split * 3, height: beamLen, background: "linear-gradient(180deg, rgba(220,180,255," + beamOpacity + ") 0%, transparent 100%)", transformOrigin: "top center", transform: "translate(-50%, 0) rotate(" + angle + "deg)", filter: "blur(" + (1 + split * 2) + "px)", animation: split > 0.3 ? "crackPulse 2s ease-in-out " + (bi * 0.25) + "s infinite" : "none" }} />;
              })}
              {split > 0.3 && <div style={{ position: "absolute", top: "50%", left: "50%", width: 300 + split * 120, height: 300 + split * 120, border: "1px solid rgba(200,160,255," + ((split - 0.3) * 0.4) + ")", borderRadius: "50%", animation: "beamRotate 12s linear infinite" }} />}
            </div>

            {/* ── SHATTER SHARDS — fly outward when planetSplit > 1 ── */}
            {shatter > 0 && (
              <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 3 }}>
                {/* Explosion flash */}
                <div style={{
                  position: "absolute", top: "42%", left: "50%", width: 600, height: 600,
                  transform: "translate(-50%, -50%)",
                  background: "radial-gradient(circle, rgba(220,180,255," + (Math.max(0, 0.6 - shatter * 1.5)) + ") 0%, rgba(139,92,246," + (Math.max(0, 0.3 - shatter)) + ") 30%, transparent 60%)",
                  borderRadius: "50%",
                }} />

                {/* Shockwave ring */}
                <div style={{
                  position: "absolute", top: "42%", left: "50%",
                  width: 100 + shatter * 800, height: 100 + shatter * 800,
                  transform: "translate(-50%, -50%)",
                  borderRadius: "50%",
                  border: "2px solid rgba(200,160,255," + (Math.max(0, 0.5 - shatter * 0.5)) + ")",
                  boxShadow: "0 0 " + (30 - shatter * 30) + "px rgba(200,160,255," + (Math.max(0, 0.3 - shatter * 0.3)) + ")",
                  transition: "all 0.3s ease-out",
                }} />

                {/* Flying shards */}
                {shards.map(function(shard, si) {
                  /* Ease: cubic out for natural deceleration */
                  var ease = 1 - Math.pow(1 - shatter, 3);
                  var curX = shard.x + (shard.endX - shard.x) * ease;
                  var curY = shard.y + (shard.endY - shard.y) * ease;
                  var curRot = shard.rot + (shard.endRot - shard.rot) * ease;
                  var shardOpacity = Math.max(0, 1 - shatter * 1.2);

                  /* Shard shape — irregular polygon via clip-path */
                  var clips = [
                    "polygon(15% 0%, 85% 5%, 100% 40%, 90% 100%, 10% 95%, 0% 50%)",
                    "polygon(20% 0%, 100% 10%, 85% 90%, 30% 100%, 0% 60%)",
                    "polygon(10% 5%, 90% 0%, 100% 50%, 80% 100%, 0% 85%)",
                    "polygon(0% 15%, 70% 0%, 100% 35%, 95% 100%, 20% 90%)",
                  ];

                  return (
                    <div key={"shard" + si} style={{
                      position: "absolute",
                      top: "42%", left: "50%",
                      width: shard.w, height: shard.h,
                      transform: "translate(calc(-50% + " + curX + "px), calc(-50% + " + curY + "px)) rotate(" + curRot + "deg)",
                      background: "linear-gradient(135deg, " + shard.color + ", #0c0820)",
                      clipPath: clips[si % clips.length],
                      opacity: shardOpacity,
                      boxShadow: "0 0 20px rgba(139,92,246," + (shardOpacity * 0.4) + "), inset 0 0 15px rgba(200,160,255,0.1)",
                      border: "1px solid rgba(160,120,255," + (shardOpacity * 0.2) + ")",
                      transition: "all 0.15s ease-out",
                    }}>
                      {/* Inner glow edge on shard */}
                      <div style={{
                        position: "absolute", inset: 0,
                        background: "linear-gradient(135deg, rgba(200,160,255,0.15) 0%, transparent 50%)",
                      }} />
                    </div>
                  );
                })}

                {/* Dust particles — tiny glowing dots that scatter */}
                {shatter > 0.05 && Array.from({ length: 20 }).map(function(_, di) {
                  var dustAngle = (di / 20) * Math.PI * 2 + di * 0.3;
                  var dustDist = 50 + shatter * (300 + di * 30);
                  var dustX = Math.cos(dustAngle) * dustDist;
                  var dustY = Math.sin(dustAngle) * dustDist;
                  var dustOp = Math.max(0, 1 - shatter * 1.5);
                  var dustSize = 2 + (di % 3);
                  return (
                    <div key={"dust" + di} style={{
                      position: "absolute", top: "42%", left: "50%",
                      width: dustSize, height: dustSize, borderRadius: "50%",
                      transform: "translate(calc(-50% + " + dustX + "px), calc(-50% + " + dustY + "px))",
                      background: di % 2 === 0 ? "#a78bfa" : "#22d3ee",
                      boxShadow: "0 0 " + (dustSize * 3) + "px " + (di % 2 === 0 ? "rgba(167,139,250,0.6)" : "rgba(34,211,238,0.6)"),
                      opacity: dustOp,
                      transition: "all 0.1s ease-out",
                    }} />
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {lt && (
        <div className="absolute pointer-events-none" style={{ zIndex: 2, top: "42%", left: "50%", width: 380, height: 380, transform: "translate(calc(-50% + " + (mouse.x * -15) + "px), calc(-50% + " + (mouse.y * -15) + "px))", transition: "transform 0.4s ease-out" }}>
          {/* Tesseract wireframe */}
          <svg viewBox="0 0 380 380" fill="none" style={{ width: "100%", height: "100%", animation: "planetRotate 30s linear infinite" }}>
            {/* Outer cube */}
            <rect x="60" y="60" width="260" height="260" rx="8" stroke="rgba(139,92,246,0.2)" strokeWidth="1.5" fill="none" />
            {/* Inner cube */}
            <rect x="120" y="120" width="140" height="140" rx="4" stroke="rgba(99,102,241,0.3)" strokeWidth="1.5" fill="none" />
            {/* Connecting edges */}
            <line x1="60" y1="60" x2="120" y2="120" stroke="rgba(139,92,246,0.15)" strokeWidth="1" />
            <line x1="320" y1="60" x2="260" y2="120" stroke="rgba(139,92,246,0.15)" strokeWidth="1" />
            <line x1="60" y1="320" x2="120" y2="260" stroke="rgba(139,92,246,0.15)" strokeWidth="1" />
            <line x1="320" y1="320" x2="260" y2="260" stroke="rgba(139,92,246,0.15)" strokeWidth="1" />
            {/* Diagonal cross beams */}
            <line x1="60" y1="60" x2="320" y2="320" stroke="rgba(139,92,246,0.06)" strokeWidth="0.5" />
            <line x1="320" y1="60" x2="60" y2="320" stroke="rgba(139,92,246,0.06)" strokeWidth="0.5" />
          </svg>
          {/* Energy core */}
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 80 + planetSplit * 40, height: 80 + planetSplit * 40, transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246," + (0.15 + planetSplit * 0.25) + ") 0%, rgba(99,102,241," + (0.08 + planetSplit * 0.12) + ") 40%, transparent 70%)", boxShadow: "0 0 " + (30 + planetSplit * 50) + "px rgba(139,92,246," + (0.2 + planetSplit * 0.3) + ")", animation: "planetPulse 4s ease-in-out infinite", transition: "all 0.3s ease-out" }} />
          {/* Pulsing rings that grow with scroll */}
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 140 + planetSplit * 60, height: 140 + planetSplit * 60, transform: "translate(-50%,-50%)", borderRadius: "50%", border: "1px solid rgba(139,92,246," + (0.1 + planetSplit * 0.15) + ")", animation: "pulseRing 3s ease-in-out infinite", transition: "all 0.3s ease-out" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 200 + planetSplit * 80, height: 200 + planetSplit * 80, transform: "translate(-50%,-50%)", borderRadius: "50%", border: "1px solid rgba(99,102,241," + (0.06 + planetSplit * 0.1) + ")", animation: "pulseRing 3s ease-in-out 0.8s infinite", transition: "all 0.3s ease-out" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 260 + planetSplit * 100, height: 260 + planetSplit * 100, transform: "translate(-50%,-50%)", borderRadius: "50%", border: "1px solid rgba(139,92,246," + (0.04 + planetSplit * 0.08) + ")", animation: "pulseRing 3s ease-in-out 1.6s infinite", transition: "all 0.3s ease-out" }} />
          {/* Electric arcs that appear with scroll */}
          {planetSplit > 0.2 && [0, 72, 144, 216, 288].map(function(angle, ai) {
            var arcOp = Math.min(1, (planetSplit - 0.2) * 1.5);
            var arcLen = 40 + planetSplit * 80;
            return <div key={"arc" + ai} style={{ position: "absolute", top: "50%", left: "50%", width: 2, height: arcLen, background: "linear-gradient(180deg, rgba(139,92,246," + (arcOp * 0.6) + ") 0%, rgba(99,102,241," + (arcOp * 0.3) + ") 50%, transparent 100%)", transformOrigin: "top center", transform: "translate(-50%, 0) rotate(" + angle + "deg)", filter: "blur(1px)", animation: "crackPulse 1.5s ease-in-out " + (ai * 0.3) + "s infinite" }} />;
          })}
          {/* Power burst at high scroll */}
          {planetSplit > 1 && (
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 300 + (planetSplit - 1) * 400, height: 300 + (planetSplit - 1) * 400, transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246," + (Math.max(0, 0.3 - (planetSplit - 1) * 0.5)) + ") 0%, rgba(99,102,241," + (Math.max(0, 0.15 - (planetSplit - 1) * 0.3)) + ") 30%, transparent 60%)", transition: "all 0.2s ease-out" }} />
          )}
        </div>
      )}

      {/* ── Floating Crystals ── */}
      {!lt && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
          <div style={{ position: "absolute", top: "35%", left: "12%", transform: "translate(" + (mouse.x * -30) + "px, " + (mouse.y * -25) + "px)", transition: "transform 0.35s ease-out", animation: "floatCrystal1 12s ease-in-out infinite" }}>
            <svg width="70" height="90" viewBox="0 0 70 90" fill="none" style={{ filter: "drop-shadow(0 0 15px rgba(120,80,220,0.4))" }}>
              <path d="M35 5 L55 35 L45 85 L25 85 L15 35 Z" fill="url(#cg1)" stroke="rgba(160,120,255,0.3)" strokeWidth="0.5" />
              <path d="M35 5 L55 35 L35 30 Z" fill="rgba(180,150,255,0.15)" />
              <defs><linearGradient id="cg1" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3a1d7a" /><stop offset="100%" stopColor="#1a0845" /></linearGradient></defs>
            </svg>
          </div>
          <div style={{ position: "absolute", top: "40%", right: "10%", transform: "translate(" + (mouse.x * -35) + "px, " + (mouse.y * -20) + "px)", transition: "transform 0.3s ease-out", animation: "floatCrystal2 15s ease-in-out infinite" }}>
            <svg width="55" height="70" viewBox="0 0 55 70" fill="none" style={{ filter: "drop-shadow(0 0 12px rgba(100,60,200,0.5))" }}>
              <path d="M28 3 L50 28 L38 67 L18 67 L5 28 Z" fill="url(#cg2)" stroke="rgba(140,100,255,0.25)" strokeWidth="0.5" />
              <defs><linearGradient id="cg2" x1="0.5" y1="0" x2="0.5" y2="1"><stop offset="0%" stopColor="#2d1565" /><stop offset="100%" stopColor="#160a38" /></linearGradient></defs>
            </svg>
          </div>
          <div style={{ position: "absolute", top: "60%", left: "22%", transform: "translate(" + (mouse.x * -25) + "px, " + (mouse.y * -30) + "px)", transition: "transform 0.4s ease-out", animation: "floatCrystal3 18s ease-in-out infinite" }}>
            <svg width="40" height="55" viewBox="0 0 40 55" fill="none" style={{ filter: "drop-shadow(0 0 10px rgba(80,50,180,0.4))" }}>
              <path d="M20 2 L36 22 L28 52 L12 52 L4 22 Z" fill="#251260" stroke="rgba(120,80,220,0.2)" strokeWidth="0.5" />
            </svg>
          </div>
          <div style={{ position: "absolute", top: "20%", right: "25%", transform: "translate(" + (mouse.x * -40) + "px, " + (mouse.y * -35) + "px)", transition: "transform 0.3s ease-out", animation: "floatCrystal2 20s ease-in-out 2s infinite" }}>
            <svg width="30" height="40" viewBox="0 0 30 40" fill="none" style={{ filter: "drop-shadow(0 0 8px rgba(100,70,200,0.3))" }}>
              <path d="M15 2 L27 16 L21 38 L9 38 L3 16 Z" fill="#1e0e50" stroke="rgba(120,80,220,0.15)" strokeWidth="0.5" />
            </svg>
          </div>
        </div>
      )}

      {/* ── Nebula / Aurora ── */}
      {!lt && (
        <>
          <div className="absolute pointer-events-none" style={{ zIndex: 1, bottom: -50, left: "-10%", width: "120%", height: 350, background: "linear-gradient(180deg, transparent 0%, rgba(100,40,180,0.08) 20%, rgba(140,60,220,0.12) 40%, rgba(180,80,255,0.15) 55%, rgba(200,120,255,0.08) 70%, transparent 100%)", filter: "blur(40px)", animation: "nebulaFlow 15s ease-in-out infinite", transform: "translate(" + (mouse.x * -10) + "px, 0)", transition: "transform 0.5s ease-out" }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, bottom: -20, left: "20%", width: "60%", height: 200, background: "radial-gradient(ellipse at 50% 80%, rgba(220,100,255,0.1) 0%, transparent 70%)", filter: "blur(30px)", animation: "nebulaFlow 20s ease-in-out 3s infinite" }} />
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, background: "radial-gradient(ellipse at 50% 30%, transparent 20%, rgba(3,1,8,0.7) 100%)" }} />
        </>
      )}

      {/* ── ANIMATED GRID BACKGROUND ── */}
      {!lt && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, opacity: 0.04, animation: "gridFade 8s ease-in-out infinite", backgroundImage: "linear-gradient(rgba(139,92,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.5) 1px, transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at 50% 50%, black 20%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 20%, transparent 70%)" }} />
      )}

      {/* ── ORBITAL FLOATING DOTS ── */}
      {!lt && (
        <div className="absolute pointer-events-none" style={{ zIndex: 2, top: "50%", left: "50%", width: 0, height: 0 }}>
          <div style={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", background: "#8b5cf6", boxShadow: "0 0 12px rgba(139,92,246,0.6), 0 0 30px rgba(139,92,246,0.3)", animation: "orbitalSpin 20s linear infinite" }} />
          <div style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: "#06b6d4", boxShadow: "0 0 10px rgba(6,182,212,0.6), 0 0 25px rgba(6,182,212,0.3)", animation: "orbitalSpin2 28s linear infinite" }} />
          <div style={{ position: "absolute", width: 3, height: 3, borderRadius: "50%", background: "#a78bfa", boxShadow: "0 0 8px rgba(167,139,250,0.5)", animation: "orbitalSpin 35s linear reverse infinite" }} />
        </div>
      )}

      {/* ── EXTRA FLOATING GLOW ORBS ── */}
      {!lt && (
        <>
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "15%", left: "8%", width: 8, height: 8, borderRadius: "50%", background: "#8b5cf6", boxShadow: "0 0 20px rgba(139,92,246,0.5), 0 0 40px rgba(139,92,246,0.2)", animation: "floatUp 6s ease-in-out infinite", opacity: 0.7 }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "25%", right: "12%", width: 5, height: 5, borderRadius: "50%", background: "#06b6d4", boxShadow: "0 0 15px rgba(6,182,212,0.5), 0 0 30px rgba(6,182,212,0.2)", animation: "floatDown 8s ease-in-out 1s infinite", opacity: 0.6 }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "55%", left: "5%", width: 6, height: 6, borderRadius: "50%", background: "#818cf8", boxShadow: "0 0 18px rgba(129,140,248,0.5)", animation: "floatUp 10s ease-in-out 2s infinite", opacity: 0.5 }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "70%", right: "8%", width: 7, height: 7, borderRadius: "50%", background: "#a78bfa", boxShadow: "0 0 20px rgba(167,139,250,0.4)", animation: "floatDown 7s ease-in-out 3s infinite", opacity: 0.6 }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "40%", left: "45%", width: 4, height: 4, borderRadius: "50%", background: "#c084fc", boxShadow: "0 0 12px rgba(192,132,252,0.5)", animation: "floatUp 12s ease-in-out infinite", opacity: 0.4 }} />
          <div className="absolute pointer-events-none" style={{ zIndex: 1, top: "85%", left: "30%", width: 5, height: 5, borderRadius: "50%", background: "#22d3ee", boxShadow: "0 0 15px rgba(34,211,238,0.4)", animation: "floatDown 9s ease-in-out 4s infinite", opacity: 0.5 }} />
        </>
      )}

      {lt && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          {/* Soft gradient mesh */}
          <div style={{ position: "absolute", top: "-10%", right: "-5%", width: "60%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0.02) 40%, transparent 70%)", filter: "blur(80px)" }} />
          <div style={{ position: "absolute", bottom: "-5%", left: "-5%", width: "50%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 60%)", filter: "blur(70px)" }} />
          <div style={{ position: "absolute", top: "30%", left: "40%", width: "40%", height: "40%", borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 60%)", filter: "blur(60px)" }} />
          {/* Light geometric pattern */}
          <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: "linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
        </div>
      )}

      {/* ═══════ SPREAD NAVBAR (Bloom-style) ═══════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-10 py-5 transition-all" style={{
        animation: show ? "navSlideDown 0.8s cubic-bezier(0.16,1,0.3,1) both" : "none",
        background: isNavSolid ? (lt ? "rgba(255,255,255,0.85)" : "rgba(3,1,8,0.85)") : "transparent",
        backdropFilter: isNavSolid ? "blur(20px) saturate(1.6)" : "none",
        borderBottom: isNavSolid ? (lt ? "1px solid rgba(139,92,246,0.08)" : "1px solid rgba(255,255,255,0.04)") : "none",
      }}>
        {/* Left: Logo + name */}
        <div className="flex items-center gap-2.5">
          <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
            <clipPath id="gloomClipNav"><circle cx="20" cy="20" r="17" /></clipPath>
            <circle cx="20" cy="20" r="17" stroke={lt ? "#8b5cf6" : "white"} strokeWidth="1.8" fill="none" />
            <g clipPath="url(#gloomClipNav)">
              <path d="M0 24 Q8 14 20 10 Q32 14 40 24" stroke={lt ? "#8b5cf6" : "white"} strokeWidth="1.4" fill="none" opacity="0.7" />
              <path d="M2 28 Q10 18 20 14 Q30 18 38 28" stroke={lt ? "#8b5cf6" : "white"} strokeWidth="1.4" fill="none" />
              <path d="M4 32 Q12 22 20 18 Q28 22 36 32" stroke={lt ? "#8b5cf6" : "white"} strokeWidth="1.2" fill="none" opacity="0.8" />
            </g>
          </svg>
          <span className="font-semibold text-base tracking-tight" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>gloom</span>
        </div>

        {/* Center: Nav links */}
        <div className="flex items-center gap-8">
          {["Home", "Protocol", "Docs", "Security"].map(function(link, li) {
            return <button key={link} onClick={function() { if (link === "Home") window.scrollTo({ top: 0, behavior: "smooth" }); else scrollTo(NAV_SECTIONS[link] || "section-protocol"); }} className="text-sm font-medium transition-all hover:opacity-100" style={{ fontFamily: bd, color: lt ? (li === 0 ? "#0f172a" : "#64748b") : (li === 0 ? "white" : "rgba(180,190,205,0.6)"), opacity: li === 0 ? 1 : 0.8 }}>{link}</button>;
          })}
        </div>

        {/* Right: Theme toggle + CTA */}
        <div className="flex items-center gap-3">
          <button onClick={function() { setLt(!lt); }} className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:bg-white/10" style={{ color: lt ? "#475569" : "rgba(203,213,225,1)" }}>
            {lt ? "☾" : "☀"}
          </button>
          <button onClick={onLogin} className="text-sm font-medium transition-all hover:opacity-80" style={{ fontFamily: bd, color: lt ? "#475569" : "rgba(203,213,225,0.8)" }}>Log In</button>
          <button onClick={onGetStarted} className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105 active:scale-95" style={{ fontFamily: hd, background: lt ? "#0f172a" : "white", color: lt ? "white" : "#0f172a" }}>
            Launch App →
          </button>
        </div>
      </nav>

      {/* ═══════ HERO SECTION (Bloom-style: left text, right visual) ═══════ */}
      <section className="relative z-10 flex items-center px-10 pt-28 pb-8" style={{ minHeight: "100vh" }}>

        {/* Left side: Text content */}
        <div className="flex-1 flex flex-col items-start max-w-xl" style={{ animation: show ? "heroFadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.2s both" : "none" }}>

          {/* Social proof badge */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: lt ? "rgba(139,92,246,0.06)" : "rgba(255,255,255,0.05)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.08)") }}>
            <div className="flex -space-x-1.5">
              {["#8b5cf6", "#06b6d4", "#a78bfa"].map(function(c, ci) {
                return <div key={ci} className="w-5 h-5 rounded-full border-2" style={{ background: c, borderColor: lt ? "#f8f9fc" : "#030108" }} />;
              })}
            </div>
            <span className="text-xs font-medium" style={{ fontFamily: bd, color: lt ? "#475569" : "rgba(255,255,255,0.7)" }}>Backed by <span style={{ fontWeight: 700, color: lt ? "#0f172a" : "white" }}>leading Web3 investors</span></span>
          </div>

          {/* Big title — multi-line like Bloom */}
          <h1 style={{
            fontFamily: hd, fontSize: "clamp(48px, 6vw, 72px)", fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.03em", marginBottom: 24,
            color: lt ? "#0f172a" : "white",
          }}>
            {"Funding the future of tech."}
          </h1>

          {/* Description */}
          <p className="text-lg leading-relaxed font-light mb-10" style={{
            fontFamily: bd, color: lt ? "rgba(71,85,105,0.8)" : "rgba(180,190,205,0.6)", maxWidth: 440,
          }}>
            Gloom Network funds multiple tech sectors — expanding across DeFi, quantum computing, and emerging technologies, so you can invest in tomorrow's breakthroughs today.
          </p>

          {/* CTA Buttons — Bloom style */}
          <div className="flex items-center gap-4 mb-12">
            <button onClick={onGetStarted} className="px-8 py-4 rounded-full text-sm font-semibold transition-all hover:scale-105 active:scale-95" style={{
              fontFamily: hd,
              background: lt ? "#0f172a" : "rgba(255,255,255,0.95)",
              color: lt ? "white" : "#0f172a",
              boxShadow: lt ? "0 8px 30px rgba(15,23,42,0.2)" : "0 8px 30px rgba(255,255,255,0.1)",
            }}>
              Start Earning
            </button>
            <button onClick={function() { scrollTo("section-docs"); }} className="flex items-center gap-2 px-6 py-4 rounded-full text-sm font-medium transition-all hover:opacity-80" style={{ fontFamily: hd, color: lt ? "#475569" : "rgba(255,255,255,0.7)", border: "1px solid " + (lt ? "rgba(15,23,42,0.15)" : "rgba(255,255,255,0.12)") }}>
              See it unfold →
            </button>
          </div>
        </div>

        {/* Right side: Planet visual */}
        <div className="flex-1 flex items-center justify-center relative" style={{ minHeight: 500 }}>
          {/* Glowing orb behind */}
          <div className="absolute" style={{ width: 400, height: 400, borderRadius: "50%", background: lt ? "radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 60%)" : "radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(80,50,180,0.05) 40%, transparent 70%)", filter: "blur(40px)" }} />

          {/* Large Gloom logo sphere */}
          <div className="relative" style={{ animation: show ? "logoReveal 1.2s cubic-bezier(0.16,1,0.3,1) 0.4s both" : "none" }}>
            <div style={{ width: 320, height: 320, borderRadius: "50%", background: "linear-gradient(135deg, #1a0845 0%, #2a1660 30%, #4a2d8a 60%, #2a1660 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 80px rgba(139,92,246,0.3), 0 0 160px rgba(80,50,180,0.15), inset 0 0 60px rgba(139,92,246,0.1)", animation: "planetPulse 6s ease-in-out infinite", transform: "translate(" + (mouse.x * -15) + "px, " + (mouse.y * -15) + "px)", transition: "transform 0.4s ease-out" }}>
              <svg width="180" height="180" viewBox="0 0 40 40" fill="none">
                <clipPath id="gloomClipHero"><circle cx="20" cy="20" r="17" /></clipPath>
                <circle cx="20" cy="20" r="17" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" fill="none" />
                <circle cx="20" cy="20" r="17" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5" fill="none" />
                <g clipPath="url(#gloomClipHero)">
                  <path d="M-2 20 Q6 10 20 6 Q34 10 42 20" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" fill="none" />
                  <path d="M0 24 Q8 14 20 10 Q32 14 40 24" stroke="rgba(255,255,255,0.6)" strokeWidth="0.7" fill="none" />
                  <path d="M2 28 Q10 18 20 14 Q30 18 38 28" stroke="rgba(255,255,255,0.8)" strokeWidth="0.7" fill="none" />
                  <path d="M4 32 Q12 22 20 18 Q28 22 36 32" stroke="rgba(255,255,255,0.6)" strokeWidth="0.6" fill="none" />
                  <path d="M6 36 Q14 26 20 22 Q26 26 34 36" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" fill="none" />
                </g>
              </svg>
            </div>
            {/* Animated rings */}
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 380, height: 380, borderRadius: "50%", border: "1px solid rgba(139,92,246,0.1)", transform: "translate(-50%,-50%)", animation: "pulseRing 4s ease-in-out infinite" }} />
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 440, height: 440, borderRadius: "50%", border: "1px solid rgba(139,92,246,0.05)", transform: "translate(-50%,-50%)", animation: "pulseRing 4s ease-in-out 1s infinite" }} />
            <div style={{ position: "absolute", top: "50%", left: "50%", width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(139,92,246,0.03)", transform: "translate(-50%,-50%)", animation: "pulseRing 4s ease-in-out 2s infinite" }} />
          </div>
        </div>
      </section>

      {/* ── ANIMATED STATS BAR ── */}
      <div id="stats-counter-section" className="relative z-10 px-10 pb-12">
        <div className="max-w-5xl mx-auto flex items-center justify-between py-6 px-8 rounded-2xl" style={{ background: lt ? "rgba(139,92,246,0.03)" : "rgba(255,255,255,0.02)", border: "1px solid " + (lt ? "rgba(139,92,246,0.08)" : "rgba(255,255,255,0.04)") }}>
          {[
            { label: "Total Value Locked", value: formatTVL(counters.tvl) },
            { label: "Active Users", value: counters.users.toLocaleString() },
            { label: "Base APY", value: counters.apy + "%" },
            { label: "Security", value: statTargets.security },
          ].map(function(stat, idx) {
            return (
              <div key={stat.label} className="flex items-center gap-4">
                {idx > 0 && <div className="w-px h-10" style={{ background: lt ? "rgba(139,92,246,0.1)" : "rgba(255,255,255,0.06)" }} />}
                <div className="text-center" style={{ marginLeft: idx > 0 ? 16 : 0 }}>
                  <p className="text-[10px] uppercase tracking-widest mb-1" style={{ fontFamily: mono, color: lt ? "rgba(71,85,105,0.5)" : "rgba(180,190,205,0.35)" }}>{stat.label}</p>
                  <p className="text-xl font-bold" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>{stat.value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══════ SOCIAL PROOF / PARTNERS ═══════ */}
      <ScrollReveal>
      <section className="relative z-10 px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.3em] mb-8" style={{ fontFamily: mono, color: lt ? "rgba(71,85,105,0.5)" : "rgba(180,190,205,0.3)" }}>Backed by leading Web3 investors</p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            {["Quantum Ventures", "CryptoFund", "DeFi Capital", "Lattice Labs", "ChainVault"].map(function(name, pi) {
              return (
                <div key={name} className="px-6 py-3 rounded-xl transition-all hover:bg-white/5" style={{ border: "1px solid " + (lt ? "rgba(139,92,246,0.08)" : "rgba(255,255,255,0.04)") }}>
                  <span className="text-xs font-semibold tracking-wide" style={{ fontFamily: hd, color: lt ? "rgba(71,85,105,0.4)" : "rgba(180,190,205,0.25)" }}>{name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ═══════ FEATURES — Glassmorphism Cards ═══════ */}
      <ScrollReveal>
      <section id="section-protocol" className="relative z-10 px-6 pb-20" style={{ scrollMarginTop: 80 }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.12)" }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#8b5cf6", animation: "pulseGlow 2s ease-in-out infinite" }} />
              <span className="text-[11px] font-medium tracking-wide" style={{ fontFamily: mono, color: "rgba(167,139,250,0.7)" }}>Core Technology</span>
            </div>
            <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>Built for the Quantum Age</h2>
            <p className="text-sm font-light max-w-lg mx-auto" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>Three pillars of next-generation security protecting every transaction on the Gloom network.</p>
          </div>
          <div className="grid grid-cols-3 gap-5">
            {[
              { title: "Lattice Encryption", desc: "NIST-approved post-quantum algorithms protect every transaction against both classical and quantum attacks.", color: "#a78bfa", icon: "◈", glowAnim: "cardGlow", breatheAnim: "breatheCard", delay: 0 },
              { title: "Zero-Knowledge Proofs", desc: "Privacy-preserving verification ensures your financial data stays confidential without sacrificing transparency.", color: "#22d3ee", icon: "✦", glowAnim: "cardGlowCyan", breatheAnim: "breatheCardCyan", delay: 0.5 },
              { title: "Quantum Key Distribution", desc: "Future-proof key exchange mechanism resistant to Shor's algorithm and next-gen quantum computing threats.", color: "#818cf8", icon: "⚡", glowAnim: "cardGlow", breatheAnim: "breatheCard", delay: 1 },
            ].map(function(feat, fi) {
              return (
                <motion.div key={feat.title} whileHover={{ y: -8, scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="glow-card rounded-2xl p-6 relative overflow-hidden group cursor-default" style={{
                  background: lt ? "rgba(255,255,255,0.7)" : "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.06)"),
                  backdropFilter: "blur(12px)",
                  animation: feat.glowAnim + " 4s ease-in-out " + feat.delay + "s infinite, " + feat.breatheAnim + " 4s ease-in-out " + feat.delay + "s infinite",
                }}>
                  {/* Always-visible glow behind card */}
                  <div className="absolute -inset-1 rounded-2xl" style={{ background: "radial-gradient(circle at 50% 0%, " + feat.color + "08 0%, transparent 60%)", animation: "pulseGlow 4s ease-in-out " + feat.delay + "s infinite" }} />
                  {/* Scanning light line */}
                  <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, " + feat.color + "20, transparent)", animation: "scanLine 4s linear " + feat.delay + "s infinite", opacity: 0.6 }} />
                  {/* Top accent line - always visible */}
                  <div className="absolute top-0 left-[10%] right-[10%] h-px" style={{ background: "linear-gradient(90deg, transparent, " + feat.color + "25, transparent)" }} />
                  {/* Icon with animation */}
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 relative" style={{ background: feat.color + "0a", border: "1px solid " + feat.color + "20", animation: "iconFloat 4s ease-in-out " + (fi * 0.3) + "s infinite" }}>
                    <span style={{ color: feat.color, fontSize: 22, animation: "iconPulse 3s ease-in-out " + (fi * 0.5) + "s infinite" }}>{feat.icon}</span>
                    {/* Icon glow ring */}
                    <div className="absolute inset-0 rounded-xl" style={{ boxShadow: "inset 0 0 15px " + feat.color + "08, 0 0 20px " + feat.color + "06" }} />
                  </div>
                  <h3 className="text-sm font-semibold mb-2 relative" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>{feat.title}</h3>
                  <p className="text-xs font-light leading-relaxed relative" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{feat.desc}</p>
                  {/* Corner accents - always visible */}
                  <div className="absolute top-3 left-3 w-4 h-4">
                    <div style={{ width: "100%", height: 1, background: feat.color + "15", position: "absolute", top: 0 }} />
                    <div style={{ width: 1, height: "100%", background: feat.color + "15", position: "absolute", left: 0 }} />
                  </div>
                  <div className="absolute bottom-3 right-3 w-4 h-4">
                    <div style={{ width: "100%", height: 1, background: feat.color + "15", position: "absolute", bottom: 0 }} />
                    <div style={{ width: 1, height: "100%", background: feat.color + "15", position: "absolute", right: 0 }} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ── Animated Section Divider ── */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-4">
        <div className="relative h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.15), transparent)" }}>
          <div className="absolute top-0 h-px" style={{ width: "20%", background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.5), transparent)", animation: "glowTrail 4s linear infinite" }} />
        </div>
      </div>

      {/* ═══════ AIRDROP PHASES ═══════ */}
      <ScrollReveal delay={0.1}>
      <section id="section-airdrop" className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.12)" }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#22d3ee", animation: "pulseGlow 2s ease-in-out infinite" }} />
              <span className="text-[11px] text-cyan-300 font-medium tracking-wide" style={{ fontFamily: mono }}>Airdrop Program</span>
            </div>
            <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>Three-Phase Airdrop</h2>
            <p className="text-sm font-light leading-relaxed max-w-lg mx-auto" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>
              Early supporters earn the most. Each phase rewards participants based on deposit activity and referrals.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-5">
            {[
              { phase: "1", name: "Genesis (Beta)", desc: "Highest point multiplier. Early depositors receive", highlight: "3x points", hCol: "#22d3ee", pct: 30, bg: "rgba(6,182,212,0.04)", border: "rgba(6,182,212,0.15)", active: true, grad: "#06b6d4, #22d3ee" },
              { phase: "2", name: "Growth", desc: "Standard point rate at", highlight: "1.5x points", hCol: "#a78bfa", pct: 0, bg: "rgba(139,92,246,0.03)", border: "rgba(139,92,246,0.1)", active: false, grad: "#8b5cf6, #a78bfa" },
              { phase: "3", name: "Distribution", desc: "Final snapshot and", highlight: "token allocation", hCol: "#818cf8", pct: 0, bg: "rgba(99,102,241,0.03)", border: "rgba(99,102,241,0.08)", active: false, grad: "#6366f1, #818cf8" },
            ].map(function(p, pi) {
              return (
                <motion.div key={p.phase} whileHover={{ y: -8, scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="glow-card rounded-2xl p-6 relative overflow-hidden" style={{ background: p.bg, border: "1px solid " + p.border, animation: (p.active ? "cardGlowCyan" : "cardGlow") + " 4s ease-in-out " + (pi * 0.5) + "s infinite" }}>
                  {/* Scanning line */}
                  <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, " + p.hCol + "18, transparent)", animation: "scanLine 5s linear " + (pi * 0.7) + "s infinite", opacity: 0.5 }} />
                  {/* Top glow */}
                  <div className="absolute -top-10 left-1/4 right-1/4 h-20 pointer-events-none" style={{ background: "radial-gradient(ellipse, " + p.hCol + "08 0%, transparent 70%)", animation: "pulseGlow 4s ease-in-out " + (pi * 0.5) + "s infinite" }} />
                  {p.active && <div className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "rgba(6,182,212,0.15)", color: "#22d3ee", fontFamily: mono, animation: "pulseGlow 2s ease-in-out infinite", boxShadow: "0 0 10px rgba(6,182,212,0.2)" }}>ACTIVE</div>}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: p.hCol + "0c", border: "1px solid " + p.hCol + "20", animation: "iconFloat 5s ease-in-out " + (pi * 0.3) + "s infinite" }}>
                    <span className="text-xl font-bold" style={{ color: p.hCol, fontFamily: hd, animation: "iconPulse 3s ease-in-out " + (pi * 0.4) + "s infinite" }}>{p.phase}</span>
                  </div>
                  <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>Phase {p.phase} — {p.name}</h3>
                  <p className="text-xs font-light leading-relaxed mb-4" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>
                    {p.desc} <span style={{ color: p.hCol, fontWeight: 600 }}>{p.highlight}</span> per dollar deposited.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full overflow-hidden" style={{ background: p.hCol + "10" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: p.pct + "%", background: "linear-gradient(90deg, " + p.grad + ")", boxShadow: p.pct > 0 ? "0 0 10px " + p.hCol + "40" : "none" }} />
                    </div>
                    <span className="text-[10px] font-medium" style={{ color: p.hCol, fontFamily: mono }}>{p.pct}%</span>
                  </div>
                  {/* Corner accents */}
                  <div className="absolute top-3 left-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: p.hCol + "12", position: "absolute", top: 0 }} /><div style={{ width: 1, height: "100%", background: p.hCol + "12", position: "absolute", left: 0 }} /></div>
                  <div className="absolute bottom-3 right-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: p.hCol + "12", position: "absolute", bottom: 0 }} /><div style={{ width: 1, height: "100%", background: p.hCol + "12", position: "absolute", right: 0 }} /></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ── Animated Section Divider ── */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-4">
        <div className="relative h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(6,182,212,0.15), transparent)" }}>
          <div className="absolute top-0 h-px" style={{ width: "20%", background: "linear-gradient(90deg, transparent, rgba(6,182,212,0.5), transparent)", animation: "glowTrail 5s linear 1s infinite" }} />
        </div>
      </div>

      {/* ═══════ HOW IT WORKS — Timeline ═══════ */}
      <ScrollReveal delay={0.1}>
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.12)" }}>
              <span className="text-[11px] font-medium tracking-wide" style={{ fontFamily: mono, color: "rgba(167,139,250,0.7)" }}>Getting Started</span>
            </div>
            <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>How It Works</h2>
            <p className="text-sm font-light max-w-md mx-auto" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>Start earning in three simple steps — no complex setup required.</p>
          </div>
          <div className="grid grid-cols-3 gap-5">
            {[
              { step: "01", title: "Create Account", desc: "Sign up with your email in seconds. Your wallet and referral code are generated automatically.", icon: "◈", col: "#a78bfa" },
              { step: "02", title: "Deposit Stablecoins", desc: "Send USDT, USDC, or DAI to your deposit address. Every dollar earns 1 point per day.", icon: "✦", col: "#22d3ee" },
              { step: "03", title: "Earn & Multiply", desc: "Farm points daily, level up tiers for higher multipliers, and invite friends for bonus rewards.", icon: "⚡", col: "#a78bfa" },
            ].map(function(s, si) {
              return (
                <motion.div key={s.step} whileHover={{ y: -8, scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="glow-card rounded-2xl p-6 relative overflow-hidden group" style={{ background: lt ? "rgba(255,255,255,0.7)" : "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.06)"), animation: "cardGlow 4s ease-in-out " + (si * 0.5) + "s infinite, breatheCard 4s ease-in-out " + (si * 0.5) + "s infinite" }}>
                  {/* Always-visible top glow */}
                  <div className="absolute -top-8 left-1/4 right-1/4 h-16 pointer-events-none" style={{ background: "radial-gradient(ellipse, " + s.col + "08 0%, transparent 70%)", animation: "pulseGlow 4s ease-in-out " + (si * 0.5) + "s infinite" }} />
                  {/* Scanning line */}
                  <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, " + s.col + "18, transparent)", animation: "scanLine 5s linear " + (si * 0.8) + "s infinite", opacity: 0.5 }} />
                  {/* Step number with glow */}
                  <div className="text-[42px] font-black leading-none mb-3" style={{ fontFamily: hd, color: s.col + "15", textShadow: "0 0 30px " + s.col + "10" }}>{s.step}</div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: s.col + "0a", border: "1px solid " + s.col + "20", animation: "iconFloat 4s ease-in-out " + (si * 0.3) + "s infinite" }}>
                    <span style={{ color: s.col, fontSize: 18, animation: "iconPulse 3s ease-in-out " + (si * 0.4) + "s infinite" }}>{s.icon}</span>
                  </div>
                  <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>{s.title}</h3>
                  <p className="text-xs font-light leading-relaxed" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{s.desc}</p>
                  {/* Animated connector arrow between cards */}
                  {si < 2 && <div className="absolute top-1/2 -right-3 hidden md:flex items-center" style={{ transform: "translateY(-50%)" }}>
                    <div style={{ width: 20, height: 1, background: "linear-gradient(90deg, " + s.col + "30, " + s.col + "08)", animation: "pulseGlow 3s ease-in-out infinite" }} />
                    <div style={{ width: 0, height: 0, borderTop: "4px solid transparent", borderBottom: "4px solid transparent", borderLeft: "5px solid " + s.col + "25" }} />
                  </div>}
                  {/* Corner accents */}
                  <div className="absolute top-3 left-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: s.col + "12", position: "absolute", top: 0 }} /><div style={{ width: 1, height: "100%", background: s.col + "12", position: "absolute", left: 0 }} /></div>
                  <div className="absolute bottom-3 right-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: s.col + "12", position: "absolute", bottom: 0 }} /><div style={{ width: 1, height: "100%", background: s.col + "12", position: "absolute", right: 0 }} /></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ── Animated Section Divider ── */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-4">
        <div className="relative h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.12), transparent)" }}>
          <div className="absolute top-0 h-px" style={{ width: "20%", background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.5), transparent)", animation: "glowTrail 6s linear 2s infinite" }} />
        </div>
      </div>

      {/* ═══════ PROTOCOL STATS ═══════ */}
      <ScrollReveal delay={0.05}>
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-10 relative overflow-hidden" style={{ background: lt ? "rgba(255,255,255,0.7)" : "linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(6,182,212,0.04) 100%)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(139,92,246,0.1)"), animation: "cardGlow 5s ease-in-out infinite" }}>
            {/* Scanning line across stats */}
            <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.2), transparent)", animation: "scanLine 6s linear infinite" }} />
            {/* Animated corner accents */}
            <div className="absolute top-0 left-0 w-20 h-20">
              <div style={{ position: "absolute", top: 0, left: 0, width: 40, height: 1, background: "linear-gradient(90deg, rgba(139,92,246,0.4), transparent)", animation: "pulseGlow 3s ease-in-out infinite" }} />
              <div style={{ position: "absolute", top: 0, left: 0, width: 1, height: 40, background: "linear-gradient(180deg, rgba(139,92,246,0.4), transparent)", animation: "pulseGlow 3s ease-in-out 0.5s infinite" }} />
            </div>
            <div className="absolute top-0 right-0 w-20 h-20">
              <div style={{ position: "absolute", top: 0, right: 0, width: 40, height: 1, background: "linear-gradient(270deg, rgba(6,182,212,0.4), transparent)", animation: "pulseGlow 3s ease-in-out 1s infinite" }} />
              <div style={{ position: "absolute", top: 0, right: 0, width: 1, height: 40, background: "linear-gradient(0deg, rgba(6,182,212,0.4), transparent)", animation: "pulseGlow 3s ease-in-out 1.5s infinite" }} />
            </div>
            <div className="absolute bottom-0 left-0 w-20 h-20">
              <div style={{ position: "absolute", bottom: 0, left: 0, width: 40, height: 1, background: "linear-gradient(90deg, rgba(99,102,241,0.4), transparent)", animation: "pulseGlow 3s ease-in-out 2s infinite" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, width: 1, height: 40, background: "linear-gradient(180deg, transparent, rgba(99,102,241,0.4))", animation: "pulseGlow 3s ease-in-out 2.5s infinite" }} />
            </div>
            <div className="absolute bottom-0 right-0 w-20 h-20">
              <div style={{ position: "absolute", bottom: 0, right: 0, width: 40, height: 1, background: "linear-gradient(270deg, rgba(6,182,212,0.4), transparent)", animation: "pulseGlow 3s ease-in-out 3s infinite" }} />
              <div style={{ position: "absolute", bottom: 0, right: 0, width: 1, height: 40, background: "linear-gradient(0deg, transparent, rgba(6,182,212,0.4))", animation: "pulseGlow 3s ease-in-out 3.5s infinite" }} />
            </div>
            {/* Background gradient pulse */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(139,92,246,0.04) 0%, transparent 60%)", animation: "pulseRing 5s ease-in-out infinite" }} />
            <div className="grid grid-cols-4 gap-6 text-center">
              {[
                { label: "Total Value Locked", value: formatTVL(counters.tvl), sub: "across all pools" },
                { label: "Active Users", value: counters.users.toLocaleString() + "+", sub: "and growing daily" },
                { label: "Avg. APY", value: counters.apy + "%", sub: "risk-adjusted returns" },
                { label: "Security Audits", value: statTargets.audits, sub: "by top-tier firms" },
              ].map(function(st) {
                return (
                  <div key={st.label}>
                    <p className="text-2xl font-bold mb-1" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>{st.value}</p>
                    <p className="text-xs font-medium mb-0.5" style={{ fontFamily: hd, color: lt ? "#475569" : "#cbd5e1" }}>{st.label}</p>
                    <p className="text-[10px]" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{st.sub}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ── Animated Section Divider ── */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-4">
        <div className="relative h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.12), transparent)" }}>
          <div className="absolute top-0 h-px" style={{ width: "20%", background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent)", animation: "glowTrail 5s linear 3s infinite" }} />
        </div>
      </div>

      {/* ═══════ DOCS / WHITEPAPER ═══════ */}
      <ScrollReveal delay={0.05}>
      <section id="section-docs" className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: hd, background: lt ? "linear-gradient(90deg, #0f172a, #6b3fa0, #0f172a)" : "linear-gradient(90deg, #ffffff, #a78bfa, #ffffff)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmerText 8s linear infinite" }}>Documentation</h2>
          <div className="grid grid-cols-3 gap-5">
            {[
              { emoji: "📄", title: "Whitepaper", desc: "Dive deep into the Gloom protocol architecture, tokenomics, and quantum-resistant cryptographic foundations.", col: "#a78bfa" },
              { emoji: "🔧", title: "Developer Docs", desc: "API references, smart contract documentation, and integration guides for building on Gloom.", col: "#22d3ee" },
              { emoji: "📊", title: "Tokenomics", desc: "$GLOOM token distribution, vesting schedules, airdrop allocation, and governance model.", col: "#818cf8" },
            ].map(function(doc, di) {
              return (
                <motion.div key={doc.title} whileHover={{ y: -8, scale: 1.02 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="glow-card rounded-2xl p-6 group relative overflow-hidden" style={{ background: lt ? "rgba(255,255,255,0.7)" : "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.06)"), animation: "cardGlow 4s ease-in-out " + (di * 0.5) + "s infinite, breatheCard 4s ease-in-out " + (di * 0.5) + "s infinite" }}>
                  {/* Scanning line */}
                  <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, " + doc.col + "18, transparent)", animation: "scanLine 5s linear " + (di * 0.8) + "s infinite", opacity: 0.5 }} />
                  {/* Top glow */}
                  <div className="absolute -top-8 left-1/4 right-1/4 h-16 pointer-events-none" style={{ background: "radial-gradient(ellipse, " + doc.col + "08 0%, transparent 70%)", animation: "pulseGlow 4s ease-in-out " + (di * 0.5) + "s infinite" }} />
                  <div className="text-2xl mb-3" style={{ animation: "iconFloat 5s ease-in-out " + (di * 0.3) + "s infinite" }}>{doc.emoji}</div>
                  <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>{doc.title}</h3>
                  <p className="text-xs font-light leading-relaxed mb-3" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{doc.desc}</p>
                  <span className="text-[11px] font-medium" style={{ color: doc.col, fontFamily: mono, animation: "pulseGlow 3s ease-in-out infinite" }}>Coming Soon</span>
                  {/* Corner accents */}
                  <div className="absolute top-3 left-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: doc.col + "12", position: "absolute", top: 0 }} /><div style={{ width: 1, height: "100%", background: doc.col + "12", position: "absolute", left: 0 }} /></div>
                  <div className="absolute bottom-3 right-3 w-3 h-3"><div style={{ width: "100%", height: 1, background: doc.col + "12", position: "absolute", bottom: 0 }} /><div style={{ width: 1, height: "100%", background: doc.col + "12", position: "absolute", right: 0 }} /></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ═══════ FAQ ═══════ */}
      <ScrollReveal delay={0.05}>
      <section id="section-security" className="relative z-10 px-6 pb-20" style={{ scrollMarginTop: 80 }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>Frequently Asked Questions</h2>
          <div className="flex flex-col gap-3">
            {[
              { q: "What are Gloom Points?", a: "Points represent your participation in the Gloom ecosystem. Every $1 deposited earns 1 point per day, multiplied by your tier bonus. Points will convert to $GLOOM tokens at TGE." },
              { q: "How does the tier system work?", a: "There are 6 tiers from Bronze to Divine. Higher tiers require larger deposits and more referrals, but reward you with significantly higher point multipliers — up to 2x at the highest tier." },
              { q: "Is my deposit safe?", a: "Gloom uses quantum-resistant lattice-based cryptography and non-custodial smart contracts. Your funds are always under your control and protected against both classical and quantum attacks." },
              { q: "When is the token launch?", a: "The $GLOOM token generation event (TGE) is planned after Season 2 concludes. 20% of total supply will be distributed to airdrop participants based on accumulated points." },
            ].map(function(faq, fi) {
              return (
                <motion.div key={fi} whileHover={{ x: 4 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="rounded-2xl p-6 group" style={{ background: lt ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.015)", border: "1px solid " + (lt ? "rgba(139,92,246,0.1)" : "rgba(255,255,255,0.04)") }}>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-3" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{ background: "rgba(139,92,246,0.08)", color: "#a78bfa", fontFamily: mono }}>{fi + 1}</span>
                    {faq.q}
                  </h3>
                  <p className="text-xs font-light leading-relaxed pl-9" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{faq.a}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ═══════ CTA SECTION ═══════ */}
      <ScrollReveal delay={0.1}>
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-14 text-center relative overflow-hidden" style={{ background: lt ? "rgba(255,255,255,0.7)" : "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(6,182,212,0.06) 50%, rgba(99,102,241,0.08) 100%)", border: "1px solid " + (lt ? "rgba(139,92,246,0.12)" : "rgba(139,92,246,0.15)"), animation: "cardGlow 5s ease-in-out infinite" }}>
            {/* Multiple animated background glows */}
            <div className="absolute inset-0 pointer-events-none">
              <div style={{ position: "absolute", top: "50%", left: "50%", width: 400, height: 400, transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 60%)", filter: "blur(60px)", animation: "pulseRing 4s ease-in-out infinite" }} />
              <div style={{ position: "absolute", top: "30%", left: "20%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 60%)", filter: "blur(40px)", animation: "floatOrb1 10s ease-in-out infinite" }} />
              <div style={{ position: "absolute", top: "40%", right: "15%", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 60%)", filter: "blur(50px)", animation: "floatOrb2 12s ease-in-out infinite" }} />
            </div>
            {/* Scanning line */}
            <div className="absolute left-0 right-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.2), transparent)", animation: "scanLine 5s linear infinite" }} />
            {/* Animated corner accents */}
            <div className="absolute top-4 left-4 w-8 h-8"><div style={{ width: "100%", height: 1, background: "rgba(139,92,246,0.25)", position: "absolute", top: 0, animation: "pulseGlow 3s ease-in-out infinite" }} /><div style={{ width: 1, height: "100%", background: "rgba(139,92,246,0.25)", position: "absolute", left: 0, animation: "pulseGlow 3s ease-in-out 0.5s infinite" }} /></div>
            <div className="absolute top-4 right-4 w-8 h-8"><div style={{ width: "100%", height: 1, background: "rgba(6,182,212,0.25)", position: "absolute", top: 0, animation: "pulseGlow 3s ease-in-out 1s infinite" }} /><div style={{ width: 1, height: "100%", background: "rgba(6,182,212,0.25)", position: "absolute", right: 0, animation: "pulseGlow 3s ease-in-out 1.5s infinite" }} /></div>
            <div className="absolute bottom-4 left-4 w-8 h-8"><div style={{ width: "100%", height: 1, background: "rgba(99,102,241,0.25)", position: "absolute", bottom: 0, animation: "pulseGlow 3s ease-in-out 2s infinite" }} /><div style={{ width: 1, height: "100%", background: "rgba(99,102,241,0.25)", position: "absolute", left: 0, animation: "pulseGlow 3s ease-in-out 2.5s infinite" }} /></div>
            <div className="absolute bottom-4 right-4 w-8 h-8"><div style={{ width: "100%", height: 1, background: "rgba(139,92,246,0.25)", position: "absolute", bottom: 0, animation: "pulseGlow 3s ease-in-out 3s infinite" }} /><div style={{ width: 1, height: "100%", background: "rgba(139,92,246,0.25)", position: "absolute", right: 0, animation: "pulseGlow 3s ease-in-out 3.5s infinite" }} /></div>
            {/* Animated gradient title */}
            <h2 className="text-4xl font-bold mb-4 relative" style={{ fontFamily: hd, background: lt ? "linear-gradient(90deg, #0f172a 0%, #6b3fa0 30%, #0891b2 60%, #0f172a 100%)" : "linear-gradient(90deg, #ffffff 0%, #a78bfa 30%, #22d3ee 60%, #ffffff 100%)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmerText 5s linear infinite" }}>Ready to Start Earning?</h2>
            <p className="text-sm font-light mb-10 max-w-md mx-auto relative" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>Join thousands of early adopters securing their position in the Gloom ecosystem. No minimum deposit required.</p>
            <button onClick={onGetStarted} className="px-14 py-4.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-110 active:scale-95 relative overflow-hidden" style={{ fontFamily: hd, background: "linear-gradient(135deg, #6b3fa0, #8b5cf6)", boxShadow: "0 12px 50px rgba(139,92,246,0.35), 0 0 0 1px rgba(139,92,246,0.15) inset, inset 0 1px 0 rgba(255,255,255,0.12)" }}>
              <span className="relative z-10">Launch App Now</span>
              <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)", backgroundSize: "200% 100%", animation: "borderShimmer 2s linear infinite" }} />
            </button>
            <p className="text-[10px] mt-5 relative" style={{ fontFamily: mono, color: lt ? "#64748b" : "#94a3b8" }}>No KYC required  •  Instant deposits  •  Locked staking periods</p>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ═══════ PREMIUM FOOTER ═══════ */}
      <ScrollReveal delay={0.05}>
      <footer className="relative z-10 px-6 pb-10 pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="h-px mb-12" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.15), transparent)" }} />
          <div className="flex justify-between items-start mb-12">
            <div className="max-w-xs">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #6b3fa0, #8b5cf6)" }}>
                  <svg width="14" height="14" viewBox="0 0 40 40" fill="none"><clipPath id="gloomClipFt"><circle cx="20" cy="20" r="17" /></clipPath><circle cx="20" cy="20" r="17" stroke="white" strokeWidth="1.8" fill="none" /><g clipPath="url(#gloomClipFt)"><path d="M2 28 Q10 18 20 14 Q30 18 38 28" stroke="white" strokeWidth="1.3" fill="none" /><path d="M4 32 Q12 22 20 18 Q28 22 36 32" stroke="white" strokeWidth="1.1" fill="none" opacity="0.8" /></g></svg>
                </div>
                <span className="font-bold text-sm" style={{ fontFamily: hd, color: lt ? "#0f172a" : "white" }}>Gloom Protocol</span>
              </div>
              <p className="text-xs font-light leading-relaxed mb-6" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>Next-generation DeFi protocol with quantum-resistant security. Farm points, earn rewards, and be part of the future of finance.</p>
              {/* Social icons */}
              <div className="flex gap-2">
                {["T", "D", "TG", "M"].map(function(s) {
                  return (
                    <div key={s} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:scale-110 hover:bg-white/5" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
                      <span className="text-[10px] text-slate-500 font-bold" style={{ fontFamily: mono }}>{s}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-14">
              <div>
                <h4 className="text-[10px] uppercase tracking-widest font-semibold mb-4" style={{ fontFamily: mono, color: lt ? "#64748b" : "#94a3b8" }}>Protocol</h4>
                <div className="flex flex-col gap-2.5">
                  {["Dashboard", "Deposit", "Referrals", "Airdrop"].map(function(l) {
                    return <span key={l} className="text-xs font-light cursor-pointer hover:text-white transition-colors" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{l}</span>;
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-widest font-semibold mb-4" style={{ fontFamily: mono, color: lt ? "#64748b" : "#94a3b8" }}>Resources</h4>
                <div className="flex flex-col gap-2.5">
                  {["Whitepaper", "Documentation", "Tokenomics", "Security"].map(function(l) {
                    return <span key={l} className="text-xs font-light cursor-pointer hover:text-white transition-colors" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{l}</span>;
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-widest font-semibold mb-4" style={{ fontFamily: mono, color: lt ? "#64748b" : "#94a3b8" }}>Community</h4>
                <div className="flex flex-col gap-2.5">
                  {["Twitter / X", "Discord", "Telegram", "Medium"].map(function(l) {
                    return <span key={l} className="text-xs font-light cursor-pointer hover:text-white transition-colors" style={{ fontFamily: bd, color: lt ? "#64748b" : "#94a3b8" }}>{l}</span>;
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="h-px mb-6" style={{ background: lt ? "rgba(139,92,246,0.1)" : "rgba(255,255,255,0.04)" }} />
          <div className="flex justify-between items-center">
            <p className="text-[11px] font-light" style={{ fontFamily: bd, color: lt ? "#94a3b8" : "#7f8493" }}>© 2026 Gloom Protocol. All rights reserved.</p>
            <div className="flex gap-6">
              {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(function(l) {
                return <span key={l} className="text-[10px] font-light cursor-pointer hover:text-slate-400 transition-colors" style={{ fontFamily: bd, color: lt ? "#94a3b8" : "#7f8493" }}>{l}</span>;
              })}
            </div>
          </div>
        </div>
      </footer>
      </ScrollReveal>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none" style={{ zIndex: 5, background: lt ? "linear-gradient(to top, #f8f9fc 0%, transparent 100%)" : "linear-gradient(to top, #030108 0%, transparent 100%)" }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SIGNUP PAGE — robust form with no async issues
   ═══════════════════════════════════════════════ */
function SignupPage({ onSignUp, onBack, onSwitchToLogin }) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle"); // idle | submitting | done
  const [focus, setFocus] = useState(null);
  const [authError, setAuthError] = useState("");

  function doSubmit(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    const errs = {};
    const u = username.trim();
    const e = email.trim();
    const p = password;

    if (!u) errs.username = "Username is required";
    else if (u.length < 3) errs.username = "Minimum 3 characters";
    else if (!/^[a-zA-Z0-9_]+$/.test(u)) errs.username = "Letters, numbers & underscores only";

    if (!e) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) errs.email = "Enter a valid email";

    if (!p) errs.password = "Password is required";
    else if (p.length < 6) errs.password = "Minimum 6 characters";

    setErrors(errs);
    setAuthError("");

    if (Object.keys(errs).length === 0) {
      setPhase("submitting");
      supabase.auth.signUp({ email: e, password: p, options: { data: { username: u } } })
        .then(function(result) {
          if (result.error) {
            setAuthError(result.error.message);
            setPhase("idle");
          } else {
            // Update username in profiles table
            var userId = result.data.user && result.data.user.id;
            if (userId) {
              supabase.from("profiles").update({ username: u }).eq("id", userId).then(function() {});
            }
            setPhase("done");
            onSignUp({ email: e, username: u, id: userId });
          }
        })
        .catch(function(err) {
          setAuthError(err.message || "Something went wrong");
          setPhase("idle");
        });
    }
  }

  const iBorder = (field) =>
    errors[field]
      ? "1px solid rgba(239,68,68,0.5)"
      : focus === field
      ? "1px solid rgba(74,222,128,0.4)"
      : "1px solid rgba(255,255,255,0.07)";

  const iShadow = (field) =>
    focus === field ? "0 0 0 3px rgba(74,222,128,0.08)" : "none";

  return (
    <div className="relative flex min-h-screen w-full" style={{ fontFamily: bd, background: "#060b11" }}>
      <GridBG />
      <GlowOrbs />

      {/* Left branding */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative z-10">
        <div>
          <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors mb-10" style={{ fontFamily: bd }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Back
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center relative overflow-hidden" style={{
              background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
              boxShadow: "0 4px 16px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.15)",
            }}>
              <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><clipPath id="gloomClipMd"><circle cx="20" cy="20" r="17" /></clipPath><circle cx="20" cy="20" r="17" stroke="white" strokeWidth="1.6" fill="none" /><g clipPath="url(#gloomClipMd)"><path d="M0 24 Q8 14 20 10 Q32 14 40 24" stroke="white" strokeWidth="1.3" fill="none" opacity="0.7" /><path d="M2 28 Q10 18 20 14 Q30 18 38 28" stroke="white" strokeWidth="1.3" fill="none" /><path d="M4 32 Q12 22 20 18 Q28 22 36 32" stroke="white" strokeWidth="1.1" fill="none" opacity="0.8" /><path d="M6 36 Q14 26 20 22 Q26 26 34 36" stroke="white" strokeWidth="0.9" fill="none" opacity="0.5" /></g></svg>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%)" }} />
            </div>
            <span className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: hd }}>Gloom</span>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: hd }}>
            Start earning yield{" "}
            <span style={{ background: "linear-gradient(90deg, #a78bfa, #06b6d4, #22d3ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in minutes</span>
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed mb-8 font-light" style={{ fontFamily: bd }}>
            Create your account to access lending markets, vaults, and leveraged positions on Gloom Network.
          </p>
          <div className="flex flex-col gap-3">
            {["No minimum deposit required", "Locked staking periods", "Institutional-grade security"].map((t) => (
              <div key={t} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "rgba(74,222,128,0.1)" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <span className="text-sm text-slate-300 font-light" style={{ fontFamily: bd }}>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          {["Non-Custodial", "Audited", "Open Source"].map((t) => (
            <span key={t} className="text-slate-500 uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ border: "1px solid rgba(255,255,255,0.05)", fontFamily: mono, fontSize: 9 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-sm">
          <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors mb-8 lg:hidden" style={{ fontFamily: bd }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Back
          </button>

          <div className="rounded-3xl p-8 relative overflow-hidden" style={{
            background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.008) 100%)",
            border: "1px solid rgba(255,255,255,0.07)",
            boxShadow: "0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.02), inset 0 1px 0 rgba(255,255,255,0.05)",
            backdropFilter: "blur(20px)",
          }}>
            {/* Subtle card glow */}
            <div className="absolute pointer-events-none" style={{ top: -40, right: -40, width: 150, height: 150, background: "radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 70%)" }} />
            <h2 className="text-xl font-bold text-white mb-1 relative z-10" style={{ fontFamily: hd, letterSpacing: "-0.01em" }}>Create account</h2>
            <p className="text-xs text-slate-400 mb-7 font-light relative z-10" style={{ fontFamily: bd, letterSpacing: "0.02em" }}>Join the next generation of on-chain finance</p>

            <form onSubmit={doSubmit} className="flex flex-col gap-4" noValidate>
              {/* Username */}
              <div className="flex flex-col gap-1.5">
                <label className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 10 }}>Username</label>
                <input
                  type="text"
                  placeholder="Choose a username"
                  value={username}
                  autoComplete="off"
                  onChange={(evt) => { setUsername(evt.target.value); if (errors.username) setErrors(function(p) { var n = Object.assign({}, p); delete n.username; return n; }); }}
                  onFocus={() => setFocus("username")}
                  onBlur={() => setFocus(null)}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={{ background: "rgba(255,255,255,0.03)", border: iBorder("username"), fontFamily: bd, boxShadow: iShadow("username"), transition: "all 0.2s ease" }}
                />
                {errors.username && <span className="text-[11px] text-red-400" style={{ fontFamily: bd }}>{errors.username}</span>}
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 10 }}>Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  autoComplete="off"
                  onChange={(evt) => { setEmail(evt.target.value); if (errors.email) setErrors(function(p) { var n = Object.assign({}, p); delete n.email; return n; }); }}
                  onFocus={() => setFocus("email")}
                  onBlur={() => setFocus(null)}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={{ background: "rgba(255,255,255,0.03)", border: iBorder("email"), fontFamily: bd, boxShadow: iShadow("email"), transition: "all 0.2s ease" }}
                />
                {errors.email && <span className="text-[11px] text-red-400" style={{ fontFamily: bd }}>{errors.email}</span>}
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 10 }}>Password</label>
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={password}
                  autoComplete="new-password"
                  onChange={(evt) => { setPassword(evt.target.value); if (errors.password) setErrors(function(p) { var n = Object.assign({}, p); delete n.password; return n; }); }}
                  onFocus={() => setFocus("password")}
                  onBlur={() => setFocus(null)}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                  style={{ background: "rgba(255,255,255,0.03)", border: iBorder("password"), fontFamily: bd, boxShadow: iShadow("password"), transition: "all 0.2s ease" }}
                />
                {errors.password && <span className="text-[11px] text-red-400" style={{ fontFamily: bd }}>{errors.password}</span>}
              </div>

              {/* Auth error from Supabase */}
              {authError && <div className="text-[11px] text-red-400 text-center py-1" style={{ fontFamily: bd }}>{authError}</div>}

              {/* Submit */}
              <button
                type="submit"
                disabled={phase === "submitting"}
                className="w-full py-3.5 rounded-2xl text-sm font-semibold text-black transition-all hover:brightness-110 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2 relative overflow-hidden"
                style={{
                  fontFamily: hd,
                  background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                  boxShadow: "0 8px 28px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
                  color: "white",
                  letterSpacing: "0.02em",
                }}
              >
                {phase === "submitting" ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                    Creating…
                  </>
                ) : "Create Account"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
              <span className="text-slate-500 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 9 }}>or</span>
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
            </div>

            <button
              type="button"
              disabled
              className="w-full py-3 rounded-xl text-sm font-medium text-slate-500 flex items-center justify-center gap-2 cursor-not-allowed opacity-50"
              style={{ fontFamily: hd, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M22 10H18a2 2 0 000 4h4" /></svg>
              Connect Wallet
              <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-1" style={{ background: "rgba(255,255,255,0.06)", color: "#64748b", fontFamily: mono }}>COMING SOON</span>
            </button>

            <p className="text-center text-slate-500 mt-5 leading-relaxed" style={{ fontFamily: bd, fontSize: 10 }}>
              By continuing you agree to our{" "}
              <span className="text-green-400/70 cursor-pointer hover:text-green-300">Terms</span> &{" "}
              <span className="text-green-400/70 cursor-pointer hover:text-green-300">Privacy Policy</span>
            </p>

            <p className="text-center text-slate-500 mt-3" style={{ fontFamily: bd, fontSize: 11 }}>
              Already have an account?{" "}
              <span onClick={onSwitchToLogin} className="text-cyan-400 cursor-pointer hover:text-cyan-300 transition-colors">Log in</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LOGIN PAGE
   ═══════════════════════════════════════════════ */
function LoginPage({ onLogin, onBack, onSwitchToSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle");
  const [focus, setFocus] = useState(null);
  const [authError, setAuthError] = useState("");

  function doSubmit(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    const errs = {};
    const e = email.trim();
    const p = password;

    if (!e) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) errs.email = "Enter a valid email";
    if (!p) errs.password = "Password is required";

    setErrors(errs);
    setAuthError("");

    if (Object.keys(errs).length === 0) {
      setPhase("submitting");
      supabase.auth.signInWithPassword({ email: e, password: p })
        .then(function(result) {
          if (result.error) {
            setAuthError(result.error.message);
            setPhase("idle");
          } else {
            var userId = result.data.user && result.data.user.id;
            // Fetch profile
            supabase.from("profiles").select("*").eq("id", userId).single()
              .then(function(profileResult) {
                var profile = profileResult.data || {};
                setPhase("done");
                onLogin({ email: e, username: profile.username || "user", id: userId });
              })
              .catch(function() {
                // Profile fetch failed but auth succeeded — log in with defaults
                setPhase("done");
                onLogin({ email: e, username: "user", id: userId });
              });
          }
        })
        .catch(function(err) {
          setAuthError(err.message || "Something went wrong");
          setPhase("idle");
        });
    }
  }

  const iBorder = (field) =>
    errors[field]
      ? "1px solid rgba(239,68,68,0.5)"
      : focus === field
      ? "1px solid rgba(74,222,128,0.4)"
      : "1px solid rgba(255,255,255,0.07)";

  const iShadow = (field) =>
    focus === field ? "0 0 0 3px rgba(74,222,128,0.08)" : "none";

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center" style={{ fontFamily: bd, background: "#060b11" }}>
      <GridBG />
      <GlowOrbs />

      <div className="w-full max-w-sm p-8 relative z-10">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors mb-8" style={{ fontFamily: bd }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back
        </button>

        <div className="rounded-3xl p-8 relative overflow-hidden" style={{
          background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.008) 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.02), inset 0 1px 0 rgba(255,255,255,0.05)",
          backdropFilter: "blur(20px)",
        }}>
          <div className="absolute pointer-events-none" style={{ top: -40, right: -40, width: 150, height: 150, background: "radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 70%)" }} />
          <h2 className="text-xl font-bold text-white mb-1 relative z-10" style={{ fontFamily: hd, letterSpacing: "-0.01em" }}>Welcome back</h2>
          <p className="text-xs text-slate-400 mb-7 font-light relative z-10" style={{ fontFamily: bd, letterSpacing: "0.02em" }}>Log in to your Gloom account</p>

          <form onSubmit={doSubmit} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <label className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 10 }}>Email</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                autoComplete="email"
                onChange={(evt) => { setEmail(evt.target.value); if (errors.email) setErrors(function(p) { var n = Object.assign({}, p); delete n.email; return n; }); }}
                onFocus={() => setFocus("email")}
                onBlur={() => setFocus(null)}
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                style={{ background: "rgba(255,255,255,0.03)", border: iBorder("email"), fontFamily: bd, boxShadow: iShadow("email"), transition: "all 0.2s ease" }}
              />
              {errors.email && <span className="text-[11px] text-red-400" style={{ fontFamily: bd }}>{errors.email}</span>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 10 }}>Password</label>
              <input
                type="password"
                placeholder="Your password"
                value={password}
                autoComplete="current-password"
                onChange={(evt) => { setPassword(evt.target.value); if (errors.password) setErrors(function(p) { var n = Object.assign({}, p); delete n.password; return n; }); }}
                onFocus={() => setFocus("password")}
                onBlur={() => setFocus(null)}
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                style={{ background: "rgba(255,255,255,0.03)", border: iBorder("password"), fontFamily: bd, boxShadow: iShadow("password"), transition: "all 0.2s ease" }}
              />
              {errors.password && <span className="text-[11px] text-red-400" style={{ fontFamily: bd }}>{errors.password}</span>}
            </div>

            {authError && <div className="text-[11px] text-red-400 text-center py-1" style={{ fontFamily: bd }}>{authError}</div>}

            <button
              type="submit"
              disabled={phase === "submitting"}
              className="w-full py-3.5 rounded-2xl text-sm font-semibold transition-all hover:brightness-110 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2 relative overflow-hidden"
              style={{
                fontFamily: hd,
                background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                boxShadow: "0 8px 28px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
                color: "white",
                letterSpacing: "0.02em",
              }}
            >
              {phase === "submitting" ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                  Logging in…
                </>
              ) : "Log In"}
            </button>
          </form>

          <p className="text-center text-slate-500 mt-5" style={{ fontFamily: bd, fontSize: 11 }}>
            Don't have an account?{" "}
            <span onClick={onSwitchToSignup} className="text-cyan-400 cursor-pointer hover:text-cyan-300 transition-colors">Sign up</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════ */
const NAV_ITEMS = [
  { label: "Dashboard", icon: "◰" },
  { label: "Referrals", icon: "⊕" },
  { label: "Deposit", icon: "✦" },
  { label: "Check-in", icon: "☀" },
  { label: "Game", icon: "▶" },
  { label: "Information", icon: "◉" },
];

/* ─── Animated Tesseract (4D hypercube projected to 2D) ─── */
function Tesseract({ hovered }) {
  var angleState = useState(0);
  var angle = angleState[0];
  var setAngle = angleState[1];

  useEffect(function() {
    var raf;
    var last = 0;
    function tick(t) {
      if (!last) last = t;
      var dt = (t - last) / 1000;
      last = t;
      setAngle(function(a) { return a + dt * 0.6; });
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return function() { cancelAnimationFrame(raf); };
  }, []);

  // 4D tesseract vertices: all combinations of -1 and 1 in 4 dimensions
  var verts4D = [];
  for (var i = 0; i < 16; i++) {
    verts4D.push([
      (i & 1) ? 1 : -1,
      (i & 2) ? 1 : -1,
      (i & 4) ? 1 : -1,
      (i & 8) ? 1 : -1,
    ]);
  }

  // Edges: connect vertices that differ in exactly one coordinate
  var edges = [];
  for (var a = 0; a < 16; a++) {
    for (var b = a + 1; b < 16; b++) {
      var diff = 0;
      for (var d = 0; d < 4; d++) {
        if (verts4D[a][d] !== verts4D[b][d]) diff++;
      }
      if (diff === 1) edges.push([a, b]);
    }
  }

  // Rotation in XW and YZ planes
  var cosA = Math.cos(angle);
  var sinA = Math.sin(angle);
  var cosB = Math.cos(angle * 0.7);
  var sinB = Math.sin(angle * 0.7);

  // Project 4D → 3D → 2D
  var size = 110;
  var projected = verts4D.map(function(v) {
    // Rotate in XW plane
    var x1 = v[0] * cosA - v[3] * sinA;
    var y1 = v[1];
    var z1 = v[2];
    var w1 = v[0] * sinA + v[3] * cosA;
    // Rotate in YZ plane
    var x2 = x1;
    var y2 = y1 * cosB - z1 * sinB;
    var z2 = y1 * sinB + z1 * cosB;
    var w2 = w1;
    // Perspective projection from 4D to 3D
    var dist4 = 3;
    var s4 = 1 / (dist4 - w2);
    var x3 = x2 * s4;
    var y3 = y2 * s4;
    var z3 = z2 * s4;
    // Perspective projection from 3D to 2D
    var dist3 = 3;
    var s3 = 1 / (dist3 - z3);
    return {
      x: x3 * s3 * size,
      y: y3 * s3 * size,
      depth: z3 + w2,
    };
  });

  var cx = 200;
  var cy = 200;

  return (
    <svg viewBox="0 0 400 400" className="w-full h-full" style={{ transition: "filter 0.8s ease", filter: hovered ? "drop-shadow(0 0 15px rgba(167,139,250,0.35))" : "none" }}>
      <defs>
        <radialGradient id="tGlow">
          <stop offset="0%" stopColor={hovered ? "#818cf8" : "#60a5fa"} stopOpacity={hovered ? "0.3" : "0.15"} />
          <stop offset="50%" stopColor={hovered ? "#6366f1" : "#3b82f6"} stopOpacity={hovered ? "0.12" : "0.05"} />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tGlowOuter">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.15" />
          <stop offset="60%" stopColor="#7c3aed" stopOpacity="0.05" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </radialGradient>
        <filter id="tBlur">
          <feGaussianBlur stdDeviation={hovered ? 1 : 2} />
        </filter>
        <filter id="tShine">
          <feGaussianBlur stdDeviation={hovered ? 2 : 4} />
        </filter>
      </defs>

      {/* Outer aura glow on hover */}
      <circle cx={cx} cy={cy} r="195" fill="url(#tGlowOuter)" style={{ opacity: hovered ? 1 : 0, transition: "opacity 0.8s ease", animation: hovered ? "subtlePulse 2s ease-in-out infinite" : "none" }} />

      {/* Background glow */}
      <circle cx={cx} cy={cy} r="180" fill="url(#tGlow)" style={{ transition: "opacity 0.8s ease" }} />

      {/* Edges — outer glow layer */}
      {edges.map(function(e, idx) {
        var p1 = projected[e[0]];
        var p2 = projected[e[1]];
        var avgDepth = (p1.depth + p2.depth) / 2;
        var op = 0.15 + (avgDepth + 2) * 0.08;
        return (
          <line key={"g" + idx} x1={cx + p1.x} y1={cy + p1.y} x2={cx + p2.x} y2={cy + p2.y}
            stroke={hovered ? "#a78bfa" : "#60a5fa"} strokeWidth={hovered ? 3 : 3} opacity={Math.max(0.05, Math.min(hovered ? op * 1.5 : op, hovered ? 0.5 : 0.4))} filter="url(#tShine)"
            style={{ transition: "stroke 0.8s ease, stroke-width 0.8s ease, opacity 0.8s ease" }} />
        );
      })}

      {/* Edges — main lines */}
      {edges.map(function(e, idx) {
        var p1 = projected[e[0]];
        var p2 = projected[e[1]];
        var avgDepth = (p1.depth + p2.depth) / 2;
        var op = 0.3 + (avgDepth + 2) * 0.15;
        return (
          <line key={"e" + idx} x1={cx + p1.x} y1={cy + p1.y} x2={cx + p2.x} y2={cy + p2.y}
            stroke={hovered ? "#c4b5fd" : "#60a5fa"} strokeWidth={hovered ? 2 : 1} opacity={Math.max(0.15, Math.min(op, hovered ? 1 : 0.85))}
            style={{ transition: "stroke 0.8s ease, stroke-width 0.8s ease, opacity 0.8s ease" }} />
        );
      })}

      {/* Vertices — glow */}
      {projected.map(function(p, idx) {
        var op = 0.3 + (p.depth + 2) * 0.15;
        return (
          <circle key={"vg" + idx} cx={cx + p.x} cy={cy + p.y} r={hovered ? 5 : 4}
            fill={hovered ? "#a78bfa" : "#60a5fa"} opacity={Math.max(0.1, Math.min(hovered ? op * 0.7 : op * 0.5, hovered ? 0.6 : 0.5))} filter="url(#tBlur)"
            style={{ transition: "r 0.8s ease, fill 0.8s ease, opacity 0.8s ease" }} />
        );
      })}

      {/* Vertices — solid */}
      {projected.map(function(p, idx) {
        var op = 0.5 + (p.depth + 2) * 0.15;
        return (
          <circle key={"v" + idx} cx={cx + p.x} cy={cy + p.y} r={hovered ? 3.5 : 2.5}
            fill={hovered ? "#e9e5ff" : "#93c5fd"} opacity={Math.max(0.3, Math.min(op, 1))}
            style={{ transition: "r 0.8s ease, fill 0.8s ease, opacity 0.8s ease" }} />
        );
      })}

      {/* Center diamond shine */}
      <circle cx={cx} cy={cy} r={hovered ? 30 : 20} fill="none" stroke="#60a5fa" strokeWidth="0.5" opacity={hovered ? 0.25 : 0.1}>
        {hovered && <animate attributeName="r" values="20;35;20" dur="2s" repeatCount="indefinite" />}
        {hovered && <animate attributeName="opacity" values="0.25;0.05;0.25" dur="2s" repeatCount="indefinite" />}
      </circle>
    </svg>
  );
}

function DashboardPage({ user }) {
  var hoverState = useState(false);
  var isHovered = hoverState[0];
  var setIsHovered = hoverState[1];

  // Profile data from Supabase
  var profileState = useState(null);
  var profile = profileState[0];
  var setProfile = profileState[1];

  var pointsState = useState(0);
  var points = pointsState[0];
  var setPoints = pointsState[1];
  var { dark: dk } = useTheme();

  // Calculate daily rate: $1 deposited = 1 point/day, multiplied by tier
  var depositAmt = profile ? (profile.deposit_amount || 0) : 0;
  var tierName = profile ? profile.tier : "Bronze";
  var tierMult = tierName === "Divine" ? 2.0 : tierName === "Ruby" ? 1.55 : tierName === "Diamond" ? 1.25 : tierName === "Gold" ? 1.10 : tierName === "Silver" ? 1.05 : 1.02;
  var dailyRate = depositAmt * tierMult;

  // Season hover state (moved to top level to follow Rules of Hooks)
  var seasonHoverState = useState(-1);
  var hoveredSeason = seasonHoverState[0];
  var setHoveredSeason = seasonHoverState[1];

  // Fetch profile from Supabase on load
  useEffect(function() {
    if (!user || !user.id) return;
    supabase.from("profiles").select("*").eq("id", user.id).single()
      .then(function(result) {
        if (result.data) {
          setProfile(result.data);
          setPoints(result.data.total_points || 0);
        }
      });
  }, [user]);

  // Live points ticker (adds to base from database)
  // Re-creates interval when dailyRate changes (deposit or tier update)
  useEffect(function() {
    if (dailyRate <= 0) return;
    var rate = dailyRate;
    var iv = setInterval(function() {
      setPoints(function(p) { return p + rate / 86400; });
    }, 1000);
    return function() { clearInterval(iv); };
  }, [dailyRate]);

  // Save points to Supabase every 60 seconds so they persist across restarts
  useEffect(function() {
    if (!user || !user.id) return;
    var iv = setInterval(function() {
      var el = document.getElementById("points-save-data");
      var pts = el ? parseFloat(el.getAttribute("data-points")) || 0 : 0;
      if (pts > 0) {
        supabase.from("profiles")
          .update({ total_points: Math.floor(pts) })
          .eq("id", user.id)
          .then(function() {});
      }
    }, 60000);
    return function() { clearInterval(iv); };
  }, [user]);

  // Also save points when user leaves the page or component unmounts
  useEffect(function() {
    if (!user || !user.id) return;
    function saveOnLeave() {
      var el = document.getElementById("points-save-data");
      var pts = el ? parseFloat(el.getAttribute("data-points")) || 0 : 0;
      if (pts > 0) {
        supabase.from("profiles")
          .update({ total_points: Math.floor(pts) })
          .eq("id", user.id)
          .then(function() {});
      }
    }
    window.addEventListener("beforeunload", saveOnLeave);
    return function() {
      saveOnLeave();
      window.removeEventListener("beforeunload", saveOnLeave);
    };
  }, [user]);

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      {/* Hidden element for the save interval to read current points */}
      <div id="points-save-data" data-points={points} style={{ display: "none" }} />
      <header className="flex items-center justify-between px-8 py-5 sticky top-0 z-10" style={{
        background: dk ? "rgba(6,11,17,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px) saturate(1.5)",
        borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9",
        animation: "premiumFadeIn 0.5s cubic-bezier(0.16,1,0.3,1) both",
      }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Dashboard</h1>
          <p className="text-[11px] text-slate-500 font-light mt-0.5" style={{ fontFamily: bd, letterSpacing: "0.02em" }}>Points farming overview</p>
        </div>
        <div className="flex items-center gap-2.5 text-sm font-semibold px-4 py-2 rounded-xl" style={{
          background: "rgba(6,182,212,0.06)",
          border: "1px solid rgba(6,182,212,0.1)",
          color: dk ? "#22d3ee" : "#0891b2", fontFamily: hd,
        }}>
          <div className="w-2 h-2 rounded-full" style={{ background: "#22d3ee", boxShadow: "0 0 8px rgba(34,211,238,0.4)" }} />{user.username}
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center px-7 py-6 relative">
        {/* Ambient background glow */}
        {dk && <div className="absolute pointer-events-none" style={{ width: 600, height: 600, top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "radial-gradient(circle, rgba(6,182,212,0.05) 0%, rgba(99,102,241,0.02) 40%, transparent 65%)" }} />}
        {dk && <div className="absolute pointer-events-none" style={{ width: 300, height: 300, top: "30%", left: "30%", transform: "translate(-50%, -50%)", background: "radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 60%)" }} />}

        {/* ── Season 1 Announcement Banner ── */}
        <div className="w-full max-w-xl mb-6 rounded-2xl p-4 flex items-center gap-4 relative overflow-hidden" style={{
          background: dk ? "linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(139,92,246,0.06) 100%)" : "linear-gradient(135deg, rgba(6,182,212,0.06) 0%, rgba(139,92,246,0.04) 100%)",
          border: dk ? "1px solid rgba(6,182,212,0.15)" : "1px solid rgba(6,182,212,0.12)",
          animation: "premiumFadeIn 0.5s cubic-bezier(0.16,1,0.3,1) both",
        }}>
          <div className="absolute pointer-events-none" style={{ top: -20, right: -20, width: 100, height: 100, background: "radial-gradient(circle, rgba(6,182,212,0.15) 0%, transparent 70%)" }} />
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 relative z-10" style={{ background: "rgba(6,182,212,0.15)", border: "1px solid rgba(6,182,212,0.25)" }}>
            <span style={{ fontSize: 16, color: "#22d3ee" }}>◈</span>
          </div>
          <div className="flex-1 relative z-10">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-white" style={{ fontFamily: hd }}>Beta Season: Genesis is Live</span>
              <span className="text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold" style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.25)", fontFamily: mono }}>Active</span>
            </div>
            <p className="text-[11px] text-slate-400 font-light mt-0.5" style={{ fontFamily: bd }}>Deposit now to earn 3x points. Early supporters receive the highest allocation rate.</p>
          </div>
        </div>

        {/* Beta Notice */}
        <div className="rounded-xl px-4 py-3 flex items-start gap-3" style={{ background: dk ? "rgba(251,191,36,0.06)" : "rgba(251,191,36,0.08)", border: dk ? "1px solid rgba(251,191,36,0.12)" : "1px solid rgba(251,191,36,0.2)" }}>
          <span style={{ fontSize: 14, marginTop: 1 }}>⚠</span>
          <div>
            <p className="text-[11px] font-semibold" style={{ fontFamily: hd, color: dk ? "#fbbf24" : "#d97706" }}>Beta Version Notice</p>
            <p className="text-[10px] font-light mt-0.5" style={{ fontFamily: bd, color: dk ? "rgba(251,191,36,0.7)" : "#92400e" }}>Points may contain bugs in this version. If you notice any issues with your points or XP, please contact support and we will resolve it.</p>
          </div>
        </div>

        {/* Tesseract container */}
        <div
          className="relative cursor-pointer transition-all"
          style={{ width: 340, height: 340 }}
          onMouseEnter={function() { setIsHovered(true); }}
          onMouseLeave={function() { setIsHovered(false); }}
        >
          <Tesseract hovered={isHovered} />

          {/* Points overlay on hover — positioned below tesseract */}
          <div className="absolute left-0 right-0 flex justify-center pointer-events-none" style={{ bottom: -20, opacity: isHovered ? 1 : 0, transform: isHovered ? "scale(1) translateY(0)" : "scale(0.92) translateY(8px)", transition: "all 0.5s cubic-bezier(0.34,1.56,0.64,1)" }}>
            <div className="rounded-2xl px-7 py-5 flex flex-col items-center" style={{
              background: dk ? "rgba(6,11,17,0.88)" : "rgba(255,255,255,0.92)",
              border: dk ? "1px solid rgba(96,165,250,0.15)" : "1px solid rgba(6,182,212,0.15)",
              backdropFilter: "blur(24px) saturate(1.8)",
              boxShadow: dk ? "0 20px 60px rgba(0,0,0,0.6), 0 0 60px rgba(96,165,250,0.06), inset 0 1px 0 rgba(255,255,255,0.05)" : "0 8px 32px rgba(0,0,0,0.08)",
            }}>
              <span className="text-[9px] text-blue-300/70 uppercase tracking-[0.2em] mb-1.5" style={{ fontFamily: mono }}>Farming Today</span>
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>
                {dailyRate.toFixed(1)} <span className="text-sm font-medium" style={{ color: dk ? "rgba(147,197,253,0.8)" : "#0891b2" }}>pts/day</span>
              </span>
            </div>
          </div>
        </div>

        {/* Label under tesseract */}
        <div className="text-center mt-2 mb-6">
          <p className="text-xs font-light" style={{ fontFamily: bd, color: dk ? "#64748b" : "#94a3b8" }}>
            {isHovered ? "Points farming rate" : "Hover to see farming rate"}
          </p>
        </div>

        {/* Stats row */}
        <div className="flex gap-4 w-full max-w-xl" style={{ animation: "premiumFadeIn 0.6s cubic-bezier(0.16,1,0.3,1) 0.2s both" }}>
          {[
            { label: "Deposited", value: "$" + depositAmt.toLocaleString(), icon: "◇", col: "#a78bfa" },
            { label: "Total Points", value: points.toFixed(2), icon: "✦", col: "#22d3ee" },
            { label: "Daily Rate", value: dailyRate.toFixed(1) + " pts", icon: "⚡", col: "#06b6d4" },
            { label: "Tier", value: profile ? profile.tier : "Bronze", icon: "◈", col: "#67e8f9" },
            { label: "Multiplier", value: tierMult.toFixed(2) + "x", icon: "⬡", col: "#0891b2" },
          ].map(function(s, sIdx) {
            return (
              <div key={s.label} className="flex-1 rounded-2xl p-4 flex flex-col items-center gap-1 premium-card relative overflow-hidden" style={{
                background: dk ? "rgba(255,255,255,0.02)" : "white",
                border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
                boxShadow: dk ? "0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)" : "0 2px 8px rgba(0,0,0,0.04)",
                animation: "scaleIn 0.5s cubic-bezier(0.16,1,0.3,1) " + (0.3 + sIdx * 0.08) + "s both",
              }}>
                <div className="absolute pointer-events-none" style={{ top: -20, right: -20, width: 60, height: 60, background: "radial-gradient(circle, " + s.col + "08 0%, transparent 70%)" }} />
                <span className="text-lg mb-1 relative z-10" style={{ color: s.col, filter: "drop-shadow(0 0 4px " + s.col + "30)", transition: "transform 0.3s", }}>{s.icon}</span>
                <span className="text-base font-bold relative z-10" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{s.value}</span>
                <span className="text-[10px] uppercase tracking-widest relative z-10" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* ── Tier Progress ── */}
        <div className="w-full max-w-xl mt-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Your Tier</span>
            <div className="flex-1 h-px" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#e2e8f0" }} />
          </div>
          <style>{"\n@keyframes tierOrbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }\n@keyframes tierPulse { 0%,100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.3); } }\n@keyframes tierRingPulse { 0%,100% { opacity: 0.15; } 50% { opacity: 0.4; } }\n"}</style>
          <div className="flex gap-2">
            {[
              { name: "Bronze", color: "#cd7f32", color2: "#e8a855", bonus: "+2%", active: (profile ? profile.tier : "Bronze") === "Bronze", level: 0 },
              { name: "Silver", color: "#c0c0c0", color2: "#e8e8f0", bonus: "+5%", active: (profile ? profile.tier : "Bronze") === "Silver", level: 1 },
              { name: "Gold", color: "#ffd700", color2: "#ffed80", bonus: "+10%", active: (profile ? profile.tier : "Bronze") === "Gold", level: 2 },
              { name: "Diamond", color: "#b9f2ff", color2: "#e0faff", bonus: "+25%", active: (profile ? profile.tier : "Bronze") === "Diamond", level: 3 },
              { name: "Ruby", color: "#e0115f", color2: "#ff4d8a", bonus: "+55%", active: (profile ? profile.tier : "Bronze") === "Ruby", level: 4 },
              { name: (profile && profile.tier === "Divine") ? "Divine" : "???", color: "#b388ff", color2: "#d4b8ff", bonus: (profile && profile.tier === "Divine") ? "∞" : "???", active: (profile ? profile.tier : "Bronze") === "Divine", level: 5 },
            ].map(function(t) {
              return (
                <div key={t.name} className="flex-1 rounded-xl py-2.5 px-2 flex flex-col items-center gap-1.5 transition-all" style={{
                  background: t.active ? t.color + "12" : (dk ? "rgba(255,255,255,0.015)" : "#f8fafc"),
                  border: t.active ? "1px solid " + t.color + "30" : (dk ? "1px solid rgba(255,255,255,0.03)" : "1px solid #e2e8f0"),
                  opacity: t.active ? 1 : 0.4,
                }}>
                  {/* Tier gem — progressively more luxurious */}
                  <div className="relative flex items-center justify-center" style={{ width: 24, height: 24 }}>
                    {/* Base gem */}
                    {t.level === 0 && (
                      <div className="rounded-full" style={{ width: 8, height: 8, background: t.color, boxShadow: t.active ? "0 0 8px " + t.color + "50" : "none" }} />
                    )}
                    {/* Silver: glowing dot + outer ring */}
                    {t.level === 1 && (
                      <>
                        <div className="rounded-full" style={{ width: 8, height: 8, background: "linear-gradient(135deg, " + t.color + ", " + t.color2 + ")", boxShadow: "0 0 6px " + t.color + "40" }} />
                        <div className="absolute rounded-full" style={{ width: 16, height: 16, border: "1px solid " + t.color + "20" }} />
                      </>
                    )}
                    {/* Gold: radiant dot + double ring */}
                    {t.level === 2 && (
                      <>
                        <div className="rounded-full" style={{ width: 8, height: 8, background: "linear-gradient(135deg, " + t.color + ", " + t.color2 + ")", boxShadow: "0 0 10px " + t.color + "50, 0 0 20px " + t.color + "20" }} />
                        <div className="absolute rounded-full" style={{ width: 16, height: 16, border: "1px solid " + t.color + "25", animation: "tierRingPulse 3s ease-in-out infinite" }} />
                        <div className="absolute rounded-full" style={{ width: 22, height: 22, border: "1px dashed " + t.color + "12" }} />
                      </>
                    )}
                    {/* Diamond: faceted gem + spinning ring + sparkle */}
                    {t.level === 3 && (
                      <>
                        <div style={{ width: 8, height: 8, background: "linear-gradient(135deg, " + t.color2 + ", " + t.color + ")", transform: "rotate(45deg)", borderRadius: 1.5, boxShadow: "0 0 10px " + t.color + "60, 0 0 25px " + t.color + "25" }} />
                        <div className="absolute rounded-full" style={{ width: 18, height: 18, border: "1px solid " + t.color + "20", animation: "tierOrbit 8s linear infinite" }}>
                          <div className="absolute rounded-full" style={{ width: 2, height: 2, top: -1, left: "50%", marginLeft: -1, background: t.color2, opacity: 0.6 }} />
                        </div>
                        <div className="absolute rounded-full" style={{ width: 2, height: 2, top: 1, right: 2, background: t.color2, animation: "tierPulse 2s ease-in-out infinite" }} />
                      </>
                    )}
                    {/* Ruby: blazing gem + double orbit + 2 sparkles */}
                    {t.level === 4 && (
                      <>
                        <div style={{ width: 8, height: 8, background: "linear-gradient(135deg, " + t.color2 + ", " + t.color + ", " + t.color2 + ")", transform: "rotate(45deg)", borderRadius: 1.5, boxShadow: "0 0 12px " + t.color + "70, 0 0 30px " + t.color + "30" }} />
                        <div className="absolute rounded-full" style={{ width: 16, height: 16, border: "1px solid " + t.color + "25", animation: "tierOrbit 6s linear infinite" }}>
                          <div className="absolute rounded-full" style={{ width: 2, height: 2, top: -1, left: "50%", marginLeft: -1, background: t.color2, opacity: 0.7 }} />
                        </div>
                        <div className="absolute rounded-full" style={{ width: 22, height: 22, border: "1px solid " + t.color + "12", animation: "tierOrbit 10s linear infinite reverse" }}>
                          <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, bottom: -1, left: "50%", marginLeft: -1, background: t.color, opacity: 0.5 }} />
                        </div>
                        <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, top: 0, left: 3, background: t.color2, animation: "tierPulse 2.5s ease-in-out infinite" }} />
                        <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, bottom: 2, right: 1, background: t.color2, animation: "tierPulse 3s ease-in-out infinite 1s" }} />
                      </>
                    )}
                    {/* Divine ???: ethereal gem + triple orbit + 3 sparkles + halo */}
                    {t.level === 5 && (
                      <>
                        <div style={{ width: 8, height: 8, background: "linear-gradient(135deg, " + t.color2 + ", " + t.color + ", #7c4dff, " + t.color2 + ")", transform: "rotate(45deg)", borderRadius: 1.5, boxShadow: "0 0 14px " + t.color + "80, 0 0 35px " + t.color + "35, 0 0 50px #7c4dff15" }} />
                        <div className="absolute rounded-full" style={{ width: 14, height: 14, border: "1px solid " + t.color + "30", animation: "tierOrbit 5s linear infinite" }}>
                          <div className="absolute rounded-full" style={{ width: 2, height: 2, top: -1, left: "50%", marginLeft: -1, background: t.color2, opacity: 0.8 }} />
                        </div>
                        <div className="absolute rounded-full" style={{ width: 20, height: 20, border: "1px solid " + t.color + "15", animation: "tierOrbit 8s linear infinite reverse" }}>
                          <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, bottom: -1, left: "50%", marginLeft: -1, background: "#7c4dff", opacity: 0.6 }} />
                        </div>
                        <div className="absolute rounded-full" style={{ width: 24, height: 24, border: "1px dashed " + t.color + "08", animation: "tierOrbit 14s linear infinite" }} />
                        <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, top: 0, left: 2, background: t.color2, animation: "tierPulse 2s ease-in-out infinite" }} />
                        <div className="absolute rounded-full" style={{ width: 1.5, height: 1.5, bottom: 0, right: 2, background: "#7c4dff", animation: "tierPulse 2.5s ease-in-out infinite 0.8s" }} />
                        <div className="absolute rounded-full" style={{ width: 1, height: 1, top: 3, right: 0, background: t.color2, animation: "tierPulse 3s ease-in-out infinite 1.5s" }} />
                      </>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold" style={{ fontFamily: hd, color: t.active ? (dk ? "white" : "#1e293b") : (dk ? "rgba(255,255,255,0.4)" : "#94a3b8") }}>{t.name}</span>
                  <span className="text-[9px]" style={{ fontFamily: mono, color: t.color, opacity: t.active ? 0.8 : 0.5 }}>{t.bonus}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Seasons — Farm Points ── */}
        {(function() {
          var seasons = [
            { name: "Genesis", sub: "Beta Season", start: "Mar 1", end: "May 31", pts: "1.3x", color: "#22d3ee", color2: "#67e8f9", active: true },
            { name: "Quantum Bloom", sub: "Season 2", start: "Jun 1", end: "Aug 31", pts: "1.3x", color: "#a78bfa", color2: "#c4b5fd", active: false },
            { name: "Hyperdrive", sub: "Season 3", start: "Sep 1", end: "Nov 30", pts: "1.5x", color: "#f472b6", color2: "#f9a8d4", active: false },
            { name: "Singularity", sub: "Season 4", start: "Dec 1", end: "Feb 28", pts: "?", color: "#fbbf24", color2: "#fde68a", active: false },
          ];

          return (
            <div className="w-full max-w-xl mt-6 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Seasons</span>
                <div className="flex-1 h-px" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#e2e8f0" }} />
              </div>
              <style>{"\n@keyframes seasonTriSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }\n@keyframes seasonTriPulse { 0%,100% { opacity:0.5; filter: brightness(1); } 50% { opacity:1; filter: brightness(1.3); } }\n@keyframes seasonTriFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }\n"}</style>
              <div className="flex flex-col gap-2.5">
                {seasons.map(function(s, i) {
                  var isHov = hoveredSeason === i;
                  var lit = s.active || isHov;
                  return (
                    <div key={s.name} className="relative rounded-2xl px-5 py-4 flex items-center gap-4 overflow-hidden cursor-pointer" style={{
                      background: lit ? (dk ? "rgba(255,255,255,0.035)" : "white") : (dk ? "rgba(255,255,255,0.015)" : "#f8fafc"),
                      border: "1px solid " + (lit ? s.color + "30" : (dk ? "rgba(255,255,255,0.04)" : "#e2e8f0")),
                      opacity: lit ? 1 : 0.45,
                      boxShadow: isHov ? "0 0 25px " + s.color + "15, 0 0 60px " + s.color + "08" : s.active ? "0 0 20px " + s.color + "10" : (dk ? "none" : "0 1px 4px rgba(0,0,0,0.04)"),
                      transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: isHov ? "translateY(-1px)" : "translateY(0)",
                    }}
                    onMouseEnter={function() { setHoveredSeason(i); }}
                    onMouseLeave={function() { setHoveredSeason(-1); }}
                    >
                      {/* Triangle icon */}
                      <div className="relative flex items-center justify-center" style={{ width: 44, height: 44, flexShrink: 0 }}>
                        {/* Outer spinning triangle ring */}
                        <svg className="absolute" width="44" height="44" viewBox="0 0 44 44" style={{ animation: lit ? "seasonTriSpin 12s linear infinite" : "none", opacity: lit ? 0.3 : 0.1, transition: "opacity 0.35s" }}>
                          <polygon points="22,2 40,38 4,38" fill="none" stroke={s.color} strokeWidth="1" strokeDasharray="4 4" />
                        </svg>
                        {/* Middle pulsing triangle */}
                        <svg className="absolute" width="32" height="32" viewBox="0 0 32 32" style={{ animation: lit ? "seasonTriPulse 3s ease-in-out infinite" : "none", opacity: lit ? 0.5 : 0.15, transition: "opacity 0.35s" }}>
                          <polygon points="16,3 29,28 3,28" fill="none" stroke={s.color2} strokeWidth="1" />
                        </svg>
                        {/* Inner solid triangle */}
                        <svg width="20" height="20" viewBox="0 0 20 20" style={{ animation: lit ? "seasonTriFloat 4s ease-in-out infinite" : "none", filter: lit ? "drop-shadow(0 0 6px " + s.color + "60)" : "none", transition: "filter 0.35s" }}>
                          <defs>
                            <linearGradient id={"stri" + i} x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor={s.color2} />
                              <stop offset="100%" stopColor={s.color} />
                            </linearGradient>
                          </defs>
                          <polygon points="10,2 18,17 2,17" fill={"url(#stri" + i + ")"} opacity={lit ? 0.9 : 0.4} style={{ transition: "opacity 0.35s" }} />
                        </svg>
                        {/* Glow dot at top of triangle */}
                        {lit && <div className="absolute rounded-full" style={{ width: 3, height: 3, top: 6, left: "50%", marginLeft: -1.5, background: s.color2, boxShadow: "0 0 6px " + s.color2, animation: "tierPulse 2s ease-in-out infinite" }} />}
                      </div>

                      {/* Season info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{ fontFamily: hd, color: lit ? (dk ? "white" : "#1e293b") : (dk ? "rgba(255,255,255,0.5)" : "#94a3b8"), transition: "color 0.35s" }}>{s.name}</span>
                          {s.active && <span className="text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold" style={{ background: s.color + "18", color: s.color, border: "1px solid " + s.color + "25", fontFamily: mono }}>Live</span>}
                        </div>
                        <span className="text-[10px] block mt-0.5" style={{ fontFamily: mono, color: lit ? (dk ? "rgba(148,163,184,0.8)" : "#64748b") : (dk ? "rgba(100,116,139,0.6)" : "#cbd5e1"), transition: "color 0.35s" }}>{s.sub} — {s.start} – {s.end}</span>
                      </div>

                      {/* Points multiplier badge */}
                      <div className="flex flex-col items-end" style={{ flexShrink: 0 }}>
                        <span className="text-sm font-bold" style={{ fontFamily: hd, color: lit ? s.color : "rgba(255,255,255,0.3)", transition: "color 0.35s" }}>{s.pts}</span>
                        <span className="text-[9px] mt-0.5" style={{ fontFamily: mono, color: lit ? (dk ? "rgba(100,116,139,0.7)" : "#94a3b8") : (dk ? "rgba(100,116,139,0.4)" : "#cbd5e1"), transition: "color 0.35s" }}>{s.active ? "Farming now" : "Upcoming"}</span>
                      </div>

                      {/* Subtle background triangle watermark */}
                      <svg className="absolute pointer-events-none" width="80" height="80" viewBox="0 0 80 80" style={{ right: -10, top: -10, opacity: lit ? 0.05 : 0.02, transition: "opacity 0.35s" }}>
                        <polygon points="40,5 75,70 5,70" fill={s.color} />
                      </svg>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Quick Actions ── */}
        <div className="w-full max-w-xl mt-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Quick Actions</span>
            <div className="flex-1 h-px" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#e2e8f0" }} />
          </div>
          <div className="flex gap-3">
            {[
              { label: "Deposit", desc: "Add funds to farm more", icon: "✦", col: "#22d3ee", colBg: "rgba(6,182,212,0.08)" },
              { label: "Check-in", desc: "Claim daily bonus", icon: "☀", col: "#fbbf24", colBg: "rgba(251,191,36,0.08)" },
              { label: "Refer", desc: "Invite friends for 5%", icon: "⊕", col: "#a78bfa", colBg: "rgba(167,139,250,0.08)" },
              { label: "Build", desc: "Tesseract builder XP", icon: "▶", col: "#4ade80", colBg: "rgba(74,222,128,0.08)" },
            ].map(function(a) {
              return (
                <div key={a.label} className="flex-1 rounded-xl p-3.5 flex flex-col items-center gap-2 premium-card cursor-pointer" style={{
                  background: dk ? "rgba(255,255,255,0.02)" : "white",
                  border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
                }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: a.colBg, border: "1px solid " + a.col + "20" }}>
                    <span style={{ color: a.col, fontSize: 14 }}>{a.icon}</span>
                  </div>
                  <span className="text-xs font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{a.label}</span>
                  <span className="text-[10px] text-slate-500 font-light text-center" style={{ fontFamily: bd }}>{a.desc}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Recent Activity ── */}
        <div className="w-full max-w-xl mt-6 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Recent Activity</span>
            <div className="flex-1 h-px" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#e2e8f0" }} />
          </div>
          <div className="rounded-2xl overflow-hidden" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
            {[
              { event: "Account created", time: "Welcome to Gloom", icon: "◈", col: "#22d3ee" },
              { event: "Beta Season started", time: "Genesis phase is live", icon: "▶", col: "#4ade80" },
              { event: "Points farming active", time: dailyRate > 0 ? dailyRate.toFixed(1) + " pts/day" : "Deposit to start", icon: "⚡", col: "#fbbf24" },
            ].map(function(ev, ei) {
              return (
                <div key={ei} className="flex items-center gap-3 px-5 py-3.5" style={{ borderBottom: ei < 2 ? (dk ? "1px solid rgba(255,255,255,0.03)" : "1px solid #f1f5f9") : "none" }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: ev.col + "10" }}>
                    <span style={{ color: ev.col, fontSize: 12 }}>{ev.icon}</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{ev.event}</span>
                    <span className="text-[11px] text-slate-500 font-light block" style={{ fontFamily: bd }}>{ev.time}</span>
                  </div>
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: ev.col + "50" }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   REFERRAL TIERS
   ═══════════════════════════════════════════════ */
var TIERS = [
  { name: "Bronze", deposit: "$10", referrals: 0, bonus: "2%", color: "#cd7f32", colorLight: "#e8a855", colorDark: "#8b5a1e", bg: "linear-gradient(160deg, #1e1206 0%, #2d1a08 30%, #1a0f04 60%, #0d0802 100%)", border: "rgba(205,127,50,0.4)", glow: "rgba(205,127,50,0.15)", facetLight: "rgba(232,168,85,0.08)", facetMid: "rgba(205,127,50,0.04)", icon: "I", hidden: false },
  { name: "Silver", deposit: "$100", referrals: 1, bonus: "5%", color: "#c0c0c0", colorLight: "#e8e8f0", colorDark: "#6a6a7a", bg: "linear-gradient(160deg, #141418 0%, #1e1e26 30%, #12121a 60%, #0a0a0e 100%)", border: "rgba(192,192,192,0.35)", glow: "rgba(192,192,192,0.12)", facetLight: "rgba(232,232,240,0.1)", facetMid: "rgba(192,192,192,0.04)", icon: "II", hidden: false },
  { name: "Gold", deposit: "$300", referrals: 3, bonus: "10%", color: "#ffd700", colorLight: "#ffed80", colorDark: "#b8960a", bg: "linear-gradient(160deg, #1a1508 0%, #2e2408 30%, #1e1804 60%, #0e0c02 100%)", border: "rgba(255,215,0,0.35)", glow: "rgba(255,215,0,0.15)", facetLight: "rgba(255,237,128,0.1)", facetMid: "rgba(255,215,0,0.04)", icon: "III", hidden: false },
  { name: "Diamond", deposit: "$1,000", referrals: 5, bonus: "25%", color: "#b9f2ff", colorLight: "#e0faff", colorDark: "#4aa8bd", bg: "linear-gradient(160deg, #06141e 0%, #0a2030 30%, #061822 60%, #030c12 100%)", border: "rgba(185,242,255,0.3)", glow: "rgba(185,242,255,0.12)", facetLight: "rgba(224,250,255,0.12)", facetMid: "rgba(185,242,255,0.05)", icon: "IV", hidden: false },
  { name: "Ruby", deposit: "$10,000", referrals: 10, bonus: "55%", color: "#e0115f", colorLight: "#ff4d8a", colorDark: "#9a0a3e", bg: "linear-gradient(160deg, #1e040e 0%, #30081a 30%, #200612 60%, #10030a 100%)", border: "rgba(224,17,95,0.4)", glow: "rgba(224,17,95,0.18)", facetLight: "rgba(255,77,138,0.1)", facetMid: "rgba(224,17,95,0.05)", icon: "V", hidden: false },
  { name: "Divine", displayName: "???", deposit: "???", referrals: "???", bonus: "???", color: "#b388ff", colorLight: "#d4b8ff", colorDark: "#7c4dff", bg: "linear-gradient(160deg, #0e0618 0%, #180c28 30%, #10081c 60%, #08040e 100%)", border: "rgba(179,136,255,0.25)", glow: "rgba(179,136,255,0.1)", facetLight: "rgba(212,184,255,0.08)", facetMid: "rgba(179,136,255,0.03)", icon: "VI", hidden: true },
];

function ReferralsPage({ user }) {
  var { dark: dk } = useTheme();

  var copied = useState(false);
  var isCopied = copied[0];
  var setCopied = copied[1];

  // Real data from Supabase
  var profileState = useState(null);
  var profile = profileState[0];
  var setProfile = profileState[1];

  var referralCountState = useState(0);
  var myReferrals = referralCountState[0];
  var setMyReferrals = referralCountState[1];

  var totalPointsState = useState(0);
  var totalPoints = totalPointsState[0];
  var setTotalPoints = totalPointsState[1];

  var refDepositsState = useState(0);
  var refDeposits = refDepositsState[0];
  var setRefDeposits = refDepositsState[1];

  // Enter someone else's referral code
  var inputCodeState = useState("");
  var inputCode = inputCodeState[0];
  var setInputCode = inputCodeState[1];
  var codeStatusState = useState(null); // null | "submitting" | "success" | "error"
  var codeStatus = codeStatusState[0];
  var setCodeStatus = codeStatusState[1];
  var codeMessageState = useState("");
  var codeMessage = codeMessageState[0];
  var setCodeMessage = codeMessageState[1];
  var referredByState = useState(null);
  var referredBy = referredByState[0];
  var setReferredBy = referredByState[1];
  var codeFocusState = useState(false);
  var codeFocused = codeFocusState[0];
  var setCodeFocused = codeFocusState[1];

  // Fetch profile + referral data from Supabase
  useEffect(function() {
    if (!user || !user.id) return;

    // Get profile (for referral_code and tier)
    supabase.from("profiles").select("*").eq("id", user.id).single()
      .then(function(result) {
        if (result.data) {
          setProfile(result.data);
          setTotalPoints(result.data.total_points || 0);
          if (result.data.referred_by) setReferredBy(result.data.referred_by);
        }
      });

    // Count referrals
    supabase.from("referrals").select("id", { count: "exact" }).eq("referrer_id", user.id)
      .then(function(result) {
        if (result.count !== null) setMyReferrals(result.count);
      });

    // Get total deposits by referred users
    supabase.from("referrals").select("referred_id").eq("referrer_id", user.id)
      .then(function(result) {
        if (result.data && result.data.length > 0) {
          var referredIds = result.data.map(function(r) { return r.referred_id; });
          supabase.from("profiles").select("deposit_amount").in("id", referredIds)
            .then(function(profilesResult) {
              if (profilesResult.data) {
                var total = 0;
                for (var i = 0; i < profilesResult.data.length; i++) {
                  total = total + (profilesResult.data[i].deposit_amount || 0);
                }
                setRefDeposits(total);
              }
            });
        }
      });
  }, [user]);

  function submitReferralCode() {
    var code = inputCode.trim().toLowerCase();
    if (!code) return;
    if (referredBy) {
      setCodeStatus("error");
      setCodeMessage("You've already used a referral code");
      return;
    }
    if (profile && profile.referral_code && code === profile.referral_code.toLowerCase()) {
      setCodeStatus("error");
      setCodeMessage("You can't use your own referral code");
      return;
    }
    setCodeStatus("submitting");

    // Look up the referrer by their referral_code
    supabase.from("profiles").select("id, username, referral_code").eq("referral_code", code).single()
      .then(function(result) {
        if (result.error || !result.data) {
          setCodeStatus("error");
          setCodeMessage("Invalid referral code");
          return;
        }
        var referrerId = result.data.id;
        var referrerName = result.data.username;

        // Update current user's referred_by
        supabase.from("profiles").update({ referred_by: referrerId }).eq("id", user.id)
          .then(function(updateResult) {
            if (updateResult.error) {
              setCodeStatus("error");
              setCodeMessage(updateResult.error.message || "Failed to apply code");
              return;
            }
            // Create referral record
            supabase.from("referrals").insert({ referrer_id: referrerId, referred_id: user.id, status: "active" })
              .then(function() {
                setCodeStatus("success");
                setCodeMessage("Linked to " + referrerName + "! You both earn bonus points.");
                setReferredBy(referrerId);
              });
          });
      });
  }

  var refCode = profile ? profile.referral_code : "loading";
  var refLink = "gloom.xyz/ref/" + refCode;

  // Figure out current tier index from profile
  var tierName = profile ? profile.tier : "Bronze";
  var currentTier = 0;
  for (var ti = 0; ti < TIERS.length; ti++) {
    if (TIERS[ti].name === tierName) { currentTier = ti; break; }
  }

  function copyLink() {
    navigator.clipboard.writeText(refCode).catch(function() {});
    setCopied(true);
    setTimeout(function() { setCopied(false); }, 2000);
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      <header className="flex items-center justify-between px-8 py-5 sticky top-0 z-10" style={{
        background: dk ? "rgba(6,11,17,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px) saturate(1.5)",
        borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9",
      }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Referral Program</h1>
          <p className="text-[11px] text-slate-400 font-light mt-0.5" style={{ fontFamily: bd, letterSpacing: "0.02em" }}>Invite friends and earn rewards together</p>
        </div>
        <div className="flex items-center gap-2.5 text-sm font-semibold px-4 py-2 rounded-xl" style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.1)", color: dk ? "#22d3ee" : "#0891b2", fontFamily: hd }}>
          <div className="w-2 h-2 rounded-full" style={{ background: "#06b6d4", boxShadow: "0 0 8px rgba(6,182,212,0.4)" }} />{user.username}
        </div>
      </header>

      <div className="flex-1 px-7 py-6 flex flex-col gap-6">

        {/* Referral code + stats row */}
        <div className="flex gap-5">
          {/* Referral Code Card */}
          <div className="relative flex-1 rounded-2xl overflow-hidden p-6" style={{
            background: "linear-gradient(145deg, #060b11 0%, #081418 50%, #0a1520 100%)",
            minHeight: 160,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)",
          }}>
            <GridBG />
            <div className="absolute rounded-full" style={{ width: 250, height: 250, top: -80, right: -40, background: "radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)" }} />
            <div className="relative z-10">
              <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ fontFamily: mono, fontSize: 10, color: "rgba(34,211,238,0.7)" }}>Your Referral Code</p>
              <div className="flex items-center gap-3 mb-5">
                <div className="px-5 py-3 rounded-xl flex items-center gap-3" style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.2)" }}>
                  <span className="text-lg font-bold text-white tracking-widest" style={{ fontFamily: mono, letterSpacing: "0.15em" }}>{refCode}</span>
                </div>
                <button onClick={copyLink} className="px-4 py-2.5 rounded-xl text-xs font-semibold transition-all hover:scale-105 active:scale-95" style={{ fontFamily: hd, background: isCopied ? "rgba(6,182,212,0.2)" : "linear-gradient(135deg, #22d3ee, #06b6d4)", color: isCopied ? "#22d3ee" : "#000" }}>
                  {isCopied ? "Copied!" : "Copy Code"}
                </button>
              </div>
              <p className="text-xs text-slate-500 font-light leading-relaxed" style={{ fontFamily: bd }}>
                Share your code with friends. When they sign up with your code and deposit, you both earn bonus yield on your positions.
              </p>
              <p className="text-xs font-light leading-relaxed mt-2" style={{ fontFamily: bd, color: "rgba(34,211,238,0.6)" }}>
                At the end of each season, you'll receive 10% of the total points your referrals have farmed — credited directly to your balance.
              </p>
            </div>
          </div>

          {/* Stats cards */}
          <div className="w-64 flex flex-col gap-3">
            {[
              { label: "Your Referrals", value: myReferrals + "", icon: "⊕", col: "#22d3ee" },
              { label: "Referral Deposits", value: "$" + refDeposits.toLocaleString(), icon: "◇", col: "#4ade80" },
              { label: "Points", value: totalPoints.toFixed(2) + " pts", icon: "✦", col: "#60a5fa" },
              { label: "Current Tier", value: TIERS[currentTier].name, icon: TIERS[currentTier].icon, col: TIERS[currentTier].color },
            ].map(function(s) {
              return (
                <div key={s.label} className="rounded-xl p-4 flex items-center gap-3 premium-card" style={{
                  background: dk ? "rgba(255,255,255,0.03)" : "white",
                  border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.col + "12", border: "1px solid " + s.col + "18" }}>
                    <span className="text-sm font-bold" style={{ color: s.col, fontFamily: mono }}>{s.icon}</span>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 font-light" style={{ fontFamily: bd }}>{s.label}</p>
                    <p className="text-base font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{s.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Enter Referral Code */}
        <div className="rounded-2xl p-6 relative overflow-hidden" style={{
          background: dk ? "rgba(255,255,255,0.02)" : "white",
          border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
          boxShadow: dk ? "0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)" : "0 2px 8px rgba(0,0,0,0.04)",
        }}>
          <div className="absolute pointer-events-none" style={{ top: -30, left: -30, width: 120, height: 120, background: "radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)" }} />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dk ? "#a78bfa" : "#7c3aed"} strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v-2" /><path d="M12 11a4 4 0 100-8 4 4 0 000 8z" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
              <h2 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Got a Referral Code?</h2>
            </div>
            <p className="text-xs text-slate-400 font-light mb-4" style={{ fontFamily: bd }}>Enter a friend's code to link your accounts. You'll earn 1% of their season points, and they'll earn 5% of yours.</p>

            {referredBy ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                <span className="text-xs font-medium" style={{ fontFamily: bd, color: "#4ade80" }}>Referral code already applied</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Enter referral code"
                    value={inputCode}
                    onChange={function(e) { setInputCode(e.target.value); if (codeStatus === "error") { setCodeStatus(null); setCodeMessage(""); } }}
                    onFocus={function() { setCodeFocused(true); }}
                    onBlur={function() { setCodeFocused(false); }}
                    onKeyDown={function(e) { if (e.key === "Enter") submitReferralCode(); }}
                    disabled={codeStatus === "submitting" || codeStatus === "success"}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: codeStatus === "error" ? "1px solid rgba(239,68,68,0.5)" : codeFocused ? "1px solid rgba(139,92,246,0.4)" : "1px solid rgba(255,255,255,0.07)",
                      fontFamily: mono,
                      letterSpacing: "0.1em",
                      boxShadow: codeFocused ? "0 0 0 3px rgba(139,92,246,0.08)" : "none",
                      transition: "all 0.2s ease",
                    }}
                  />
                </div>
                <button
                  onClick={submitReferralCode}
                  disabled={codeStatus === "submitting" || codeStatus === "success" || !inputCode.trim()}
                  className="px-5 py-3 rounded-xl text-xs font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: hd,
                    background: codeStatus === "success" ? "rgba(74,222,128,0.2)" : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    color: codeStatus === "success" ? "#4ade80" : "white",
                    boxShadow: "0 4px 16px rgba(139,92,246,0.2)",
                  }}
                >
                  {codeStatus === "submitting" ? "Applying..." : codeStatus === "success" ? "Applied!" : "Apply"}
                </button>
              </div>
            )}

            {codeMessage && (
              <p className="text-[11px] mt-2" style={{
                fontFamily: bd,
                color: codeStatus === "success" ? "#4ade80" : codeStatus === "error" ? "#f87171" : "#94a3b8",
              }}>{codeMessage}</p>
            )}
          </div>
        </div>

        {/* How It Works — Mutual Rewards */}
        <div className="flex gap-3">
          <div className="flex-1 rounded-xl p-4 flex items-center gap-3" style={{
            background: dk ? "rgba(139,92,246,0.04)" : "rgba(139,92,246,0.03)",
            border: dk ? "1px solid rgba(139,92,246,0.1)" : "1px solid rgba(139,92,246,0.08)",
          }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.18)" }}>
              <span className="text-sm" style={{ color: "#a78bfa" }}>↑</span>
            </div>
            <div>
              <p className="text-[11px] text-slate-400 font-light" style={{ fontFamily: bd }}>You invite someone</p>
              <p className="text-xs font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>You earn <span style={{ color: "#a78bfa" }}>5%</span> of their season points</p>
            </div>
          </div>
          <div className="flex-1 rounded-xl p-4 flex items-center gap-3" style={{
            background: dk ? "rgba(6,182,212,0.04)" : "rgba(6,182,212,0.03)",
            border: dk ? "1px solid rgba(6,182,212,0.1)" : "1px solid rgba(6,182,212,0.08)",
          }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.18)" }}>
              <span className="text-sm" style={{ color: "#22d3ee" }}>↓</span>
            </div>
            <div>
              <p className="text-[11px] text-slate-400 font-light" style={{ fontFamily: bd }}>Someone invites you</p>
              <p className="text-xs font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>You earn <span style={{ color: "#22d3ee" }}>1%</span> of their season points</p>
            </div>
          </div>
        </div>


        <div className="rounded-2xl p-6" style={{ background: dk ? "rgba(255,255,255,0.03)" : "white", border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
          <h3 className="text-sm font-bold mb-5" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>How It Works</h3>
          <div className="grid grid-cols-3 gap-5">
            {[
              { step: "1", title: "Share Your Code", desc: "Send your unique referral code to friends or share it on social media" },
              { step: "2", title: "Friends Sign Up", desc: "They create an account using your referral code and make their first deposit" },
              { step: "3", title: "Earn Together", desc: "Both of you receive bonus yield on lending positions based on your tier" },
            ].map(function(s) {
              return (
                <div key={s.step} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative overflow-hidden" style={{
                    background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                    boxShadow: "0 3px 12px rgba(99,102,241,0.2)",
                  }}>
                    <span className="text-xs font-bold text-white relative z-10" style={{ fontFamily: hd }}>{s.step}</span>
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%)" }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-0.5" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#334155" }}>{s.title}</p>
                    <p className="text-xs text-slate-400 font-light leading-relaxed" style={{ fontFamily: bd }}>{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent referrals table (empty state) */}
        <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: dk ? "rgba(255,255,255,0.03)" : "white", border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f1f5f9" }}>
            <h3 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#334155" }}>Recent Referrals</h3>
            <span className="text-xs text-slate-400 px-2 py-1 rounded-lg font-semibold" style={{ fontFamily: mono, fontSize: 11, background: "rgba(255,255,255,0.05)" }}>0 total</span>
          </div>
          <div className="grid grid-cols-4 px-5 py-2" style={{ background: dk ? "rgba(255,255,255,0.02)" : "#f8fafc" }}>
            {["User", "Date", "Deposit", "Status"].map(function(h) {
              return <span key={h} className="font-medium text-slate-400 uppercase tracking-widest" style={{ fontFamily: mono, fontSize: 9 }}>{h}</span>;
            })}
          </div>
          <div className="flex flex-col items-center justify-center py-10 text-slate-400 px-5">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: dk ? "rgba(255,255,255,0.05)" : "#f1f5f9" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
            </div>
            <p className="text-sm font-light">No referrals yet. Share your code to get started!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   POINTS PAGE — Deposit USDT to farm points
   ═══════════════════════════════════════════════ */
/* Fallback addresses — used while Supabase loads or if fetch fails */
var DEPOSIT_NETWORKS_FALLBACK = [
  { name: "ERC20", chain: "Ethereum", address: "0x4A7b3F29C8e1D5f2aB9c6E0dF8371Ca2E9b50D1", color: "#627EEA", icon: "ETH" },
  { name: "BEP20", chain: "BNB Smart Chain", address: "0x7C2d9F0aE3b8C14D5e6A7f1B0c2E4d6F8a9B3C1", color: "#F3BA2F", icon: "BSC" },
  { name: "TRC20", chain: "Tron", address: "TJx8K4rVn2Lm6W9pQf3Y5hA7cB1dE0gH4i", color: "#FF0013", icon: "TRX" },
];

var SUPPORTED_CURRENCIES = [
  { name: "USDT", fullName: "Tether USD", color: "#26A17B", icon: "₮" },
  { name: "USDC", fullName: "USD Coin", color: "#2775CA", icon: "$", unavailable: true },
  { name: "DAI", fullName: "Dai Stablecoin", color: "#F5AC37", icon: "◈", unavailable: true },
];

function PointsPage({ user }) {
  var depositState = useState(0);
  var totalDeposited = depositState[0];
  var setTotalDeposited = depositState[1];
  var pointsState2 = useState(0);
  var totalPoints = pointsState2[0];
  var setTotalPoints2 = pointsState2[1];

  /* Dynamic deposit addresses from Supabase — you can change them anytime in the dashboard */
  var netState = useState(DEPOSIT_NETWORKS_FALLBACK);
  var DEPOSIT_NETWORKS = netState[0];
  var setDepositNetworks = netState[1];
  useEffect(function() {
    supabase.from("deposit_addresses").select("network, chain, address").order("id")
      .then(function(result) {
        if (result.data && result.data.length > 0) {
          var iconMap = { ERC20: "ETH", BEP20: "BSC", TRC20: "TRX" };
          var colorMap = { ERC20: "#627EEA", BEP20: "#F3BA2F", TRC20: "#FF0013" };
          var mapped = result.data.map(function(row) {
            return {
              name: row.network,
              chain: row.chain,
              address: row.address,
              color: colorMap[row.network] || "#8b5cf6",
              icon: iconMap[row.network] || row.network,
            };
          });
          setDepositNetworks(mapped);
        }
      });
  }, []);

  // Fetch real deposit + points from Supabase
  useEffect(function() {
    if (!user || !user.id) return;
    supabase.from("profiles").select("deposit_amount, total_points").eq("id", user.id).single()
      .then(function(result) {
        if (result.data) {
          setTotalDeposited(result.data.deposit_amount || 0);
          setTotalPoints2(result.data.total_points || 0);
        }
      });
  }, [user]);

  var currState = useState("USDT");
  var activeCurrency = currState[0];
  var setActiveCurrency = currState[1];

  // User address state — one-time fill, locks after save
  var addrState = useState("");
  var userAddr = addrState[0];
  var setUserAddr = addrState[1];

  var savedState = useState(false);
  var isSaved = savedState[0];
  var setIsSaved = savedState[1];

  // Load saved withdrawal address from Supabase
  useEffect(function() {
    if (!user || !user.id) return;
    supabase.from("profiles").select("withdrawal_address").eq("id", user.id).single()
      .then(function(result) {
        if (result.data && result.data.withdrawal_address) {
          setUserAddr(result.data.withdrawal_address);
          setIsSaved(true);
        }
      });
  }, [user]);

  var copiedState = useState("");
  var copiedAddr = copiedState[0];
  var setCopiedAddr = copiedState[1];

  // Track which deposit address is revealed
  var revealedState = useState({});
  var revealed = revealedState[0];
  var setRevealed = revealedState[1];

  function toggleReveal(network) {
    setRevealed(function(prev) {
      var next = {};
      for (var k in prev) next[k] = prev[k];
      next[network] = !prev[network];
      return next;
    });
  }

  function saveAddress() {
    if (userAddr.trim().length < 10) return;
    if (!user || !user.id) return;
    supabase.from("profiles").update({ withdrawal_address: userAddr.trim() }).eq("id", user.id)
      .then(function(result) {
        if (result.error) {
          addToast("Failed to save address", "error");
        } else {
          setIsSaved(true);
          addToast("Withdrawal address saved", "success");
        }
      });
  }

  // Compute current tier from profile
  var profileTierState = useState("Bronze");
  var profileTierName = profileTierState[0];
  var setProfileTierName = profileTierState[1];
  useEffect(function() {
    if (!user || !user.id) return;
    supabase.from("profiles").select("tier").eq("id", user.id).single()
      .then(function(result) {
        if (result.data && result.data.tier) setProfileTierName(result.data.tier);
      });
  }, [user]);
  var depositCurrentTier = 0;
  for (var dti = 0; dti < TIERS.length; dti++) {
    if (TIERS[dti].name === profileTierName) { depositCurrentTier = dti; break; }
  }

  function copyAddress(addr, network) {
    navigator.clipboard.writeText(addr).catch(function() {});
    setCopiedAddr(network);
    addToast("Address copied to clipboard", "success");
    setTimeout(function() { setCopiedAddr(""); }, 2000);
  }

  var { dark: dk } = useTheme();

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      <header className="flex items-center justify-between px-8 py-5 sticky top-0 z-10" style={{
        background: dk ? "rgba(6,11,17,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px) saturate(1.5)",
        borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9",
      }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Deposit</h1>
          <p className="text-[11px] text-slate-400 font-light mt-0.5" style={{ fontFamily: bd, letterSpacing: "0.02em" }}>Deposit stablecoins to farm points</p>
        </div>
        <div className="flex items-center gap-2.5 text-sm font-semibold px-4 py-2 rounded-xl" style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.1)", color: dk ? "#22d3ee" : "#0891b2", fontFamily: hd }}>
          <div className="w-2 h-2 rounded-full" style={{ background: "#22d3ee", boxShadow: "0 0 8px rgba(34,211,238,0.4)" }} />{user.username}
        </div>
      </header>

      <div className="flex-1 px-7 py-6 flex flex-col gap-6">

        {/* ── Stats Overview Cards ── */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Deposited", value: "$" + totalDeposited.toFixed(2), sub: "Lifetime deposits", icon: "◎", col: "#22d3ee", grad: "linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(6,182,212,0.04) 100%)" },
            { label: "Points Farmed", value: totalPoints.toFixed(2), sub: "Total points earned", icon: "✦", col: "#a78bfa", grad: "linear-gradient(135deg, rgba(167,139,250,0.12) 0%, rgba(139,92,246,0.04) 100%)" },
            { label: "Farming Rate", value: "$1 = 1 pt/day", sub: "Before tier multiplier", icon: "⚡", col: "#4ade80", grad: "linear-gradient(135deg, rgba(74,222,128,0.12) 0%, rgba(34,197,94,0.04) 100%)" },
          ].map(function(s) {
            return (
              <div key={s.label} className="rounded-2xl p-5 relative overflow-hidden premium-card" style={{ background: dk ? s.grad : "white", border: dk ? "1px solid " + s.col + "15" : "1px solid #e2e8f0" }}>
                {/* Decorative glow */}
                <div className="absolute pointer-events-none" style={{ top: -30, right: -30, width: 100, height: 100, background: "radial-gradient(circle, " + s.col + "10 0%, transparent 70%)" }} />
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.col + "15", border: "1px solid " + s.col + "20" }}>
                    <span className="text-lg" style={{ color: s.col }}>{s.icon}</span>
                  </div>
                  <span className="text-[9px] font-medium uppercase tracking-widest" style={{ fontFamily: mono, color: s.col + "80" }}>{s.label}</span>
                </div>
                <p className="text-xl font-bold mb-0.5" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{s.value}</p>
                <p className="text-[10px] text-slate-500 font-light" style={{ fontFamily: bd }}>{s.sub}</p>
              </div>
            );
          })}
        </div>

        {/* ── Currency Selector — pill style ── */}
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Token</span>
          <div className="flex gap-1.5 p-1 rounded-xl" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f1f5f9" }}>
            {SUPPORTED_CURRENCIES.map(function(c) {
              var isActive = activeCurrency === c.name;
              return (
                <button key={c.name} onClick={function() { if (c.unavailable) { addToast(c.name + " deposits are temporarily unavailable", "error"); return; } setActiveCurrency(c.name); addToast("Switched to " + c.name, "info"); }}
                  className="relative flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    fontFamily: hd,
                    background: isActive && !c.unavailable ? (dk ? "rgba(255,255,255,0.08)" : "white") : "transparent",
                    border: isActive && !c.unavailable ? (dk ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0") : "1px solid transparent",
                    color: c.unavailable ? (dk ? "#334155" : "#cbd5e1") : (isActive ? c.color : (dk ? "#64748b" : "#94a3b8")),
                    boxShadow: isActive && !c.unavailable ? (dk ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.06)") : "none",
                    opacity: c.unavailable ? 0.5 : 1,
                    cursor: c.unavailable ? "not-allowed" : "pointer",
                  }}>
                  <span className="text-sm font-bold">{c.icon}</span>
                  {c.name}
                  {c.unavailable && <span className="text-[8px] font-light" style={{ color: dk ? "#ef4444" : "#dc2626" }}>Unavailable</span>}
                  {!c.unavailable && <span className="text-[9px] font-light" style={{ color: dk ? "#475569" : "#94a3b8" }}>{c.fullName.split(" ")[0]}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Deposit Addresses with View toggle ── */}
        <div className="rounded-2xl overflow-hidden" style={{ border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f8fafc", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
            <div>
              <h2 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Deposit {activeCurrency}</h2>
              <p className="text-[10px] text-slate-400 font-light mt-0.5" style={{ fontFamily: bd }}>Click "View" to reveal the deposit address for each network</p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.12)" }}>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#4ade80", animation: "subtlePulse 2s ease-in-out infinite" }} />
              <span className="text-[10px] font-medium" style={{ fontFamily: mono, color: "#4ade80" }}>Active</span>
            </div>
          </div>

          <div className="flex flex-col">
            {DEPOSIT_NETWORKS.map(function(net, ni) {
              var isCopied = copiedAddr === net.name;
              var isRevealed = revealed[net.name];
              return (
                <div key={net.name} className="px-5 py-4 flex flex-col gap-3 transition-all" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white", borderBottom: ni < DEPOSIT_NETWORKS.length - 1 ? (dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9") : "none" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold tracking-wide" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{net.icon === "ETH" ? "ERC" : net.icon === "BSC" ? "BSC" : "TRC"}</span>
                      <div className="w-px h-4" style={{ background: dk ? "rgba(255,255,255,0.08)" : "#e2e8f0" }} />
                      <div>
                        <p className="text-xs font-medium" style={{ fontFamily: bd, color: dk ? "rgba(255,255,255,0.6)" : "#64748b" }}>{net.chain}</p>
                        <span className="text-[10px] text-slate-500" style={{ fontFamily: mono }}>~2 min confirmation</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isRevealed && (
                        <button
                          onClick={function() { copyAddress(net.address, net.name); }}
                          className="px-3.5 py-2 rounded-lg text-[11px] font-semibold transition-all hover:scale-105 active:scale-95"
                          style={{
                            fontFamily: hd,
                            background: isCopied ? "rgba(74,222,128,0.12)" : "rgba(6,182,212,0.08)",
                            color: isCopied ? "#4ade80" : "#22d3ee",
                            border: "1px solid " + (isCopied ? "rgba(74,222,128,0.2)" : "rgba(6,182,212,0.12)"),
                          }}
                        >
                          {isCopied ? "Copied!" : "Copy"}
                        </button>
                      )}
                      <button
                        onClick={function() { toggleReveal(net.name); }}
                        className="px-4 py-2 rounded-lg text-[11px] font-semibold transition-all hover:scale-105 active:scale-95"
                        style={{
                          fontFamily: hd,
                          background: isRevealed ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(6,182,212,0.15))",
                          color: isRevealed ? "#94a3b8" : "#a78bfa",
                          border: isRevealed ? (dk ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0") : "1px solid rgba(139,92,246,0.2)",
                        }}
                      >
                        <span className="flex items-center gap-1.5">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {isRevealed
                              ? <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>
                              : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                            }
                          </svg>
                          {isRevealed ? "Hide" : "View"}
                        </span>
                      </button>
                    </div>
                  </div>
                  {/* Address — slides open when revealed */}
                  {isRevealed && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <div className="px-4 py-3 rounded-xl flex items-center" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#f8fafc", border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f1f5f9" }}>
                        <span className="text-[11px] font-medium break-all" style={{ fontFamily: mono, letterSpacing: "0.02em", color: dk ? "#94a3b8" : "#64748b" }}>{net.address}</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Your Withdrawal Address — one-time fill ── */}
        <div className="rounded-2xl overflow-hidden" style={{ border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f8fafc", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Withdrawal Address</h3>
              {isSaved && (
                <span className="text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "rgba(74,222,128,0.1)", color: "#4ade80", fontFamily: mono }}>Locked</span>
              )}
            </div>
            <p className="text-[10px] text-slate-500 font-light mt-1" style={{ fontFamily: bd }}>
              {isSaved ? "Address is locked. Contact support@gloom.xyz to change." : "Set once — this is where your withdrawals will be sent."}
            </p>
          </div>
          <div className="px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white" }}>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Enter your wallet address (ERC20 / BEP20 / TRC20)"
                value={userAddr}
                disabled={isSaved}
                autoComplete="off"
                onChange={function(evt) { if (!isSaved) setUserAddr(evt.target.value); }}
                className="flex-1 px-4 py-3 rounded-xl text-sm outline-none transition-all"
                style={{
                  background: dk ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  border: dk ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                  fontFamily: isSaved ? mono : bd,
                  fontSize: isSaved ? 11 : 13,
                  color: isSaved ? "#64748b" : (dk ? "#e2e8f0" : "#1e293b"),
                  cursor: isSaved ? "not-allowed" : "text",
                }}
              />
              {!isSaved && (
                <button
                  onClick={saveAddress}
                  disabled={userAddr.trim().length < 10}
                  className="px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ fontFamily: hd, background: "linear-gradient(135deg, #8b5cf6, #06b6d4)", boxShadow: "0 4px 16px rgba(139,92,246,0.2)" }}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Important Notice — cleaner design ── */}
        <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: dk ? "linear-gradient(135deg, rgba(250,204,21,0.04), rgba(251,191,36,0.02))" : "#fffdf5", border: dk ? "1px solid rgba(250,204,21,0.1)" : "1px solid #fef3c7" }}>
          <div className="absolute pointer-events-none" style={{ top: -40, right: -40, width: 120, height: 120, background: "radial-gradient(circle, rgba(250,204,21,0.08) 0%, transparent 70%)" }} />
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(250,204,21,0.1)", border: "1px solid rgba(250,204,21,0.15)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            </div>
            <div>
              <h4 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#fde68a" : "#92400e" }}>Before You Deposit</h4>
              <div className="flex flex-col gap-2">
                {[
                  "Only send on the correct network — wrong network = permanent loss",
                  "Each $1 deposited farms 1 point per day automatically",
                  "Points credited after 6 network confirmations (~2-5 min)",
                  "Withdrawal address can only be set once",
                ].map(function(note, i) {
                  return (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(250,204,21,0.1)" }}>
                        <span className="text-[8px] font-bold" style={{ color: "#facc15" }}>{i + 1}</span>
                      </div>
                      <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "#cbd5e1" : "#78716c" }}>{note}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Deposit History ── */}
        <div className="rounded-2xl overflow-hidden" style={{ border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="flex items-center justify-between px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f8fafc", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
            <h3 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#334155" }}>Deposit History</h3>
            <span className="text-[10px] font-medium px-2.5 py-1 rounded-lg" style={{ fontFamily: mono, background: dk ? "rgba(255,255,255,0.04)" : "#f1f5f9", color: dk ? "#475569" : "#94a3b8" }}>0 deposits</span>
          </div>
          <div className="flex flex-col items-center justify-center py-14 px-5" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#f1f5f9", border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={dk ? "#334155" : "#cbd5e1"} strokeWidth="1.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
            </div>
            <p className="text-sm font-medium mb-1" style={{ fontFamily: hd, color: dk ? "#475569" : "#94a3b8" }}>No deposits yet</p>
            <p className="text-[11px] text-slate-500 font-light" style={{ fontFamily: bd }}>Send {activeCurrency} to start farming points</p>
          </div>
        </div>

        {/* ── How to Deposit Guide — timeline style ── */}
        <div className="rounded-2xl overflow-hidden" style={{ border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f8fafc", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
            <h3 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>How to Deposit</h3>
          </div>
          <div className="px-5 py-5" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white" }}>
            <div className="flex flex-col gap-0">
              {[
                { step: "1", title: "Choose Your Network", desc: "Select the blockchain network (Ethereum, BNB Chain, or Tron)", col: "#22d3ee" },
                { step: "2", title: "Reveal & Copy Address", desc: "Click View to reveal the address, then copy it to your clipboard", col: "#a78bfa" },
                { step: "3", title: "Send Stablecoins", desc: "Send USDT, USDC, or DAI from your wallet — no minimum amount", col: "#4ade80" },
                { step: "4", title: "Start Farming", desc: "After 6 confirmations, your deposit is credited and points farming begins", col: "#fbbf24" },
              ].map(function(g, gi) {
                return (
                  <div key={g.step} className="flex items-start gap-4 relative" style={{ paddingBottom: gi < 3 ? 24 : 0 }}>
                    {/* Timeline line */}
                    {gi < 3 && <div className="absolute left-[19px] top-10 w-px bottom-0" style={{ background: dk ? "rgba(255,255,255,0.06)" : "#e2e8f0" }} />}
                    {/* Step circle */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 relative z-10" style={{ background: g.col + "12", border: "1px solid " + g.col + "25" }}>
                      <span className="text-xs font-bold" style={{ fontFamily: hd, color: g.col }}>{g.step}</span>
                    </div>
                    <div className="pt-1.5">
                      <p className="text-xs font-semibold mb-1" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{g.title}</p>
                      <p className="text-[11px] text-slate-500 font-light leading-relaxed" style={{ fontFamily: bd }}>{g.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>


        {/* Deposit Tiers */}
        <div>
          <h2 className="text-sm font-semibold mb-1" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Deposit Tiers</h2>
          <p className="text-xs text-slate-400 font-light mb-4" style={{ fontFamily: bd }}>Level up by increasing your deposits to unlock higher bonus yields</p>

          {/* Crystal keyframes */}
          <style>{"\n@keyframes crystalShimmer { 0%,100% { opacity: 0.3; } 50% { opacity: 0.8; } }\n@keyframes crystalPulse { 0%,100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.15); opacity: 1; } }\n@keyframes facetGlint { 0% { transform: translateX(-100%) rotate(25deg); } 100% { transform: translateX(200%) rotate(25deg); } }\n@keyframes spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }\n@keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }\n@keyframes divineFloat { 0%,100% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 12px rgba(179,136,255,0.5)); } 25% { transform: translateY(-6px) scale(1.03); filter: drop-shadow(0 0 20px rgba(212,184,255,0.7)); } 50% { transform: translateY(-3px) scale(1.01); filter: drop-shadow(0 0 16px rgba(179,136,255,0.6)); } 75% { transform: translateY(-7px) scale(1.04); filter: drop-shadow(0 0 24px rgba(230,210,255,0.8)); } }\n@keyframes haloRing { 0% { transform: translate(-50%,-50%) scale(0.8) rotate(0deg); opacity: 0.6; } 50% { transform: translate(-50%,-50%) scale(1.2) rotate(180deg); opacity: 0.3; } 100% { transform: translate(-50%,-50%) scale(0.8) rotate(360deg); opacity: 0.6; } }\n@keyframes divinePulse { 0%,100% { box-shadow: 0 0 30px rgba(179,136,255,0.15), 0 0 60px rgba(179,136,255,0.08), inset 0 0 30px rgba(179,136,255,0.03); } 50% { box-shadow: 0 0 50px rgba(212,184,255,0.25), 0 0 100px rgba(179,136,255,0.12), inset 0 0 50px rgba(212,184,255,0.06); } }\n@keyframes runeGlow { 0%,100% { opacity: 0.15; filter: blur(0px); } 50% { opacity: 0.4; filter: blur(0.5px); } }\n@keyframes starBurst { 0% { transform: scale(0) rotate(0deg); opacity: 0; } 20% { transform: scale(1) rotate(72deg); opacity: 1; } 80% { transform: scale(1.1) rotate(288deg); opacity: 0.6; } 100% { transform: scale(0) rotate(360deg); opacity: 0; } }\n@keyframes auraWave { 0% { transform: translateX(-50%) scaleY(1); opacity: 0.4; } 50% { transform: translateX(-50%) scaleY(1.3); opacity: 0.2; } 100% { transform: translateX(-50%) scaleY(1); opacity: 0.4; } }\n@keyframes sacredOrbit { 0% { transform: rotate(0deg) translateX(50px) rotate(0deg); } 100% { transform: rotate(360deg) translateX(50px) rotate(-360deg); } }\n@keyframes textReveal { 0%,45% { opacity: 0.6; letter-spacing: 0.2em; } 50%,55% { opacity: 1; letter-spacing: 0.3em; text-shadow: 0 0 20px rgba(212,184,255,0.8); } 60%,100% { opacity: 0.6; letter-spacing: 0.2em; } }\n@keyframes miniTessRotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }\n@keyframes miniTessFloat { 0%,100% { transform: translateY(0) rotate(0deg); opacity: 0.35; } 50% { transform: translateY(-2px) rotate(180deg); opacity: 0.55; } }\n"}</style>

          <div className="flex gap-3">
            {TIERS.map(function(tier, idx) {
              var isActive = idx === depositCurrentTier;
              var isLocked = idx > depositCurrentTier;
              var isDivineRevealed = tier.name === "Divine" && isActive;
              var isHidden = tier.hidden && !isDivineRevealed;

              return (
                <div key={tier.name} className="flex-1 flex flex-col transition-all relative overflow-hidden group" style={{
                  background: tier.bg,
                  border: "1px solid " + tier.border,
                  boxShadow: isActive
                    ? "0 8px 40px " + tier.glow + ", 0 0 80px " + tier.glow + ", inset 0 1px 0 " + tier.facetLight
                    : isHidden
                    ? "0 0 40px rgba(179,136,255,0.2), 0 0 80px rgba(179,136,255,0.08), 0 0 120px rgba(124,77,255,0.04), inset 0 1px 0 rgba(212,184,255,0.1), inset 0 0 40px rgba(179,136,255,0.03)"
                    : "inset 0 1px 0 " + tier.facetLight,
                  opacity: isLocked && !isHidden ? 0.55 : 1,
                  animation: isHidden ? "divinePulse 4s ease-in-out infinite" : "none",
                  minHeight: 240,
                  borderRadius: 20,
                  padding: 0,
                }}>

                  {/* ── Crystal facet overlays ── */}
                  {/* Top-left facet highlight */}
                  <div className="absolute pointer-events-none" style={{
                    top: 0, left: 0, width: "60%", height: "45%",
                    background: "linear-gradient(135deg, " + tier.facetLight + " 0%, transparent 60%)",
                    borderRadius: "20px 0 0 0",
                  }} />
                  {/* Bottom-right facet shadow */}
                  <div className="absolute pointer-events-none" style={{
                    bottom: 0, right: 0, width: "70%", height: "50%",
                    background: "linear-gradient(315deg, rgba(0,0,0,0.3) 0%, transparent 50%)",
                    borderRadius: "0 0 20px 0",
                  }} />
                  {/* Diagonal facet line 1 */}
                  <div className="absolute pointer-events-none" style={{
                    top: "20%", left: 0, width: "100%", height: 1,
                    background: "linear-gradient(90deg, transparent 5%, " + tier.color + "12 30%, " + tier.color + "08 70%, transparent 95%)",
                    transform: "rotate(-8deg)",
                  }} />
                  {/* Diagonal facet line 2 */}
                  <div className="absolute pointer-events-none" style={{
                    top: "55%", left: 0, width: "100%", height: 1,
                    background: "linear-gradient(90deg, transparent 10%, " + tier.color + "10 40%, " + tier.color + "06 60%, transparent 90%)",
                    transform: "rotate(5deg)",
                  }} />
                  {/* Vertical facet edge */}
                  <div className="absolute pointer-events-none" style={{
                    top: 0, left: "40%", width: 1, height: "100%",
                    background: "linear-gradient(180deg, " + tier.facetLight + " 0%, transparent 30%, " + tier.facetMid + " 60%, transparent 100%)",
                  }} />

                  {/* ── Moving glint / light refraction ── */}
                  <div className="absolute pointer-events-none overflow-hidden" style={{ inset: 0, borderRadius: 20 }}>
                    <div style={{
                      position: "absolute", top: 0, left: "-50%", width: "40%", height: "200%",
                      background: "linear-gradient(90deg, transparent, " + tier.facetLight + ", transparent)",
                      animation: "facetGlint 6s ease-in-out infinite",
                      animationDelay: (idx * 1.2) + "s",
                    }} />
                  </div>

                  {/* ── Corner gem sparkles ── */}
                  <div className="absolute pointer-events-none" style={{
                    top: 12, right: 14, width: 3, height: 3, borderRadius: "50%",
                    background: tier.colorLight,
                    animation: "crystalShimmer 3s ease-in-out infinite",
                    animationDelay: (idx * 0.5) + "s",
                  }} />
                  <div className="absolute pointer-events-none" style={{
                    top: 28, right: 32, width: 2, height: 2, borderRadius: "50%",
                    background: tier.colorLight,
                    animation: "crystalShimmer 4s ease-in-out infinite",
                    animationDelay: (idx * 0.7 + 1) + "s",
                  }} />
                  <div className="absolute pointer-events-none" style={{
                    bottom: 40, left: 16, width: 2, height: 2, borderRadius: "50%",
                    background: tier.colorLight,
                    animation: "crystalShimmer 3.5s ease-in-out infinite",
                    animationDelay: (idx * 0.3 + 0.5) + "s",
                  }} />

                  {/* ── Inner glow orb ── */}
                  <div className="absolute pointer-events-none" style={{
                    width: 100, height: 100, top: -20, right: -20,
                    background: "radial-gradient(circle, " + tier.color + "18 0%, " + tier.color + "08 40%, transparent 70%)",
                    animation: "crystalPulse 5s ease-in-out infinite",
                    animationDelay: (idx * 0.8) + "s",
                  }} />
                  <div className="absolute pointer-events-none" style={{
                    width: 80, height: 80, bottom: -10, left: -10,
                    background: "radial-gradient(circle, " + tier.color + "0a 0%, transparent 70%)",
                  }} />

                  {/* PLACEHOLDER for divine animations - kept for now */}
                  {isHidden && (
                    <>
                      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ borderRadius: 20 }}>
                        <div style={{
                          position: "absolute", width: "200%", height: "200%", top: "-50%", left: "-50%",
                          background: "conic-gradient(from 0deg, transparent 0%, rgba(179,136,255,0.06) 5%, rgba(212,184,255,0.1) 10%, transparent 15%, transparent 50%, rgba(124,77,255,0.05) 55%, rgba(179,136,255,0.08) 60%, transparent 65%)",
                          animation: "spinSlow 10s linear infinite",
                        }} />
                      </div>
                      <div className="absolute pointer-events-none" style={{
                        top: "50%", left: "50%", width: 180, height: 180,
                        border: "1px solid rgba(179,136,255,0.08)",
                        borderRadius: "50%",
                        animation: "haloRing 12s ease-in-out infinite",
                      }}>
                        <div className="absolute" style={{
                          top: "15%", left: "15%", right: "15%", bottom: "15%",
                          border: "1px solid rgba(212,184,255,0.06)",
                          borderRadius: "50%",
                        }} />
                      </div>
                      {[0, 1, 2, 3, 4, 5].map(function(i) {
                        return (
                          <div key={"orb" + i} className="absolute pointer-events-none" style={{
                            top: "50%", left: "50%", width: 4, height: 4,
                            animation: "sacredOrbit " + (8 + i * 1.5) + "s linear infinite",
                            animationDelay: (i * -1.3) + "s",
                          }}>
                            <div style={{
                              width: i % 2 === 0 ? 3 : 2, height: i % 2 === 0 ? 3 : 2,
                              borderRadius: "50%",
                              background: i % 3 === 0 ? "#d4b8ff" : i % 3 === 1 ? "#b388ff" : "#7c4dff",
                              boxShadow: "0 0 8px " + (i % 2 === 0 ? "rgba(212,184,255,0.8)" : "rgba(179,136,255,0.6)"),
                              opacity: 0.7,
                            }} />
                          </div>
                        );
                      })}
                      <div className="absolute pointer-events-none" style={{
                        bottom: 0, left: "50%", width: "120%", height: 60,
                        background: "linear-gradient(0deg, rgba(179,136,255,0.06) 0%, transparent 100%)",
                        animation: "auraWave 3s ease-in-out infinite",
                        borderRadius: "50% 50% 0 0",
                      }} />
                      <div className="absolute pointer-events-none" style={{
                        bottom: 0, left: "50%", width: "80%", height: 40,
                        background: "linear-gradient(0deg, rgba(212,184,255,0.04) 0%, transparent 100%)",
                        animation: "auraWave 4s ease-in-out infinite",
                        animationDelay: "-1.5s",
                        borderRadius: "50% 50% 0 0",
                      }} />
                    </>
                  )}

                  {/* ── Card content ── */}
                  <div className="relative z-10 flex flex-col flex-1 p-5">

                    {/* Crystal gem icon + badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="relative" style={{ animation: isActive ? "floatUp 4s ease-in-out infinite" : isHidden ? "divineFloat 6s ease-in-out infinite" : "none" }}>
                        {isHidden ? (
                          <svg width="42" height="42" viewBox="0 0 48 48" fill="none">
                            <circle cx="24" cy="24" r="20" stroke="url(#divHalo)" strokeWidth="0.5" opacity="0.5" strokeDasharray="3 5">
                              <animateTransform attributeName="transform" type="rotate" values="0 24 24;360 24 24" dur="20s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="24" cy="24" r="16" stroke="url(#divHalo)" strokeWidth="0.4" opacity="0.3" strokeDasharray="2 4">
                              <animateTransform attributeName="transform" type="rotate" values="360 24 24;0 24 24" dur="15s" repeatCount="indefinite" />
                            </circle>
                            <path d="M24 5 L34 12 L36 24 L34 36 L24 43 L14 36 L12 24 L14 12 Z" stroke="#b388ff" strokeWidth="0.8" fill="none" opacity="0.7" />
                            <path d="M24 5 L34 12 L24 18 L14 12 Z" fill="rgba(212,184,255,0.12)" stroke="#b388ff" strokeWidth="0.4" />
                            <path d="M14 12 L24 18 L12 24 Z" fill="rgba(179,136,255,0.06)" stroke="#b388ff" strokeWidth="0.3" />
                            <path d="M34 12 L36 24 L24 18 Z" fill="rgba(212,184,255,0.08)" stroke="#b388ff" strokeWidth="0.3" />
                            <path d="M12 24 L24 30 L14 36 Z" fill="rgba(124,77,255,0.06)" stroke="#b388ff" strokeWidth="0.3" />
                            <path d="M36 24 L34 36 L24 30 Z" fill="rgba(179,136,255,0.04)" stroke="#b388ff" strokeWidth="0.3" />
                            <path d="M14 36 L24 30 L34 36 L24 43 Z" fill="rgba(124,77,255,0.08)" stroke="#b388ff" strokeWidth="0.3" />
                            <path d="M24 18 L36 24 L24 30 L12 24 Z" fill="rgba(212,184,255,0.06)" stroke="#d4b8ff" strokeWidth="0.4" />
                            <ellipse cx="24" cy="24" rx="8" ry="5" stroke="#d4b8ff" strokeWidth="0.6" fill="rgba(212,184,255,0.05)">
                              <animate attributeName="ry" values="5;6;5" dur="3s" repeatCount="indefinite" />
                            </ellipse>
                            <circle cx="24" cy="24" r="2.5" fill="#d4b8ff" opacity="0.4">
                              <animate attributeName="r" values="2.5;3;2.5" dur="3s" repeatCount="indefinite" />
                            </circle>
                            <defs>
                              <linearGradient id="divHalo" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#7c4dff" />
                                <stop offset="50%" stopColor="#d4b8ff" />
                                <stop offset="100%" stopColor="#b388ff" />
                              </linearGradient>
                            </defs>
                          </svg>
                        ) : (
                          <svg width="36" height="36" viewBox="0 0 40 40" fill="none">
                            <path d="M20 4 L32 14 L28 34 L12 34 L8 14 Z" stroke={tier.color} strokeWidth="1" fill="none" opacity="0.6" />
                            <path d="M20 4 L32 14 L20 18 L8 14 Z" fill={tier.facetLight} stroke={tier.color} strokeWidth="0.5" opacity="0.8" />
                            <path d="M8 14 L20 18 L12 34 Z" fill={tier.facetMid} stroke={tier.color} strokeWidth="0.5" opacity="0.5" />
                            <path d="M32 14 L20 18 L28 34 Z" fill={tier.facetLight} stroke={tier.color} strokeWidth="0.5" opacity="0.3" />
                            <path d="M12 34 L20 18 L28 34 Z" fill={tier.facetMid} stroke={tier.color} strokeWidth="0.5" opacity="0.4" />
                            <path d="M20 4 L20 18" stroke={tier.colorLight} strokeWidth="0.3" opacity="0.4" />
                            <path d="M8 14 L28 34" stroke={tier.colorLight} strokeWidth="0.2" opacity="0.2" />
                            <path d="M32 14 L12 34" stroke={tier.colorLight} strokeWidth="0.2" opacity="0.2" />
                            <circle cx="20" cy="10" r="1.5" fill={tier.colorLight} opacity="0.5">
                              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2.5s" repeatCount="indefinite" />
                            </circle>
                          </svg>
                        )}
                        <div className="absolute" style={{
                          top: "50%", left: "50%",
                          width: isHidden ? 50 : 30, height: isHidden ? 50 : 30,
                          transform: "translate(-50%, -50%)",
                          background: isHidden
                            ? "radial-gradient(circle, rgba(179,136,255,0.5) 0%, rgba(124,77,255,0.2) 40%, transparent 70%)"
                            : "radial-gradient(circle, " + tier.color + "30 0%, transparent 70%)",
                          filter: isHidden ? "blur(10px)" : "blur(6px)",
                        }} />
                      </div>

                      {isActive && (
                        <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{
                          background: "linear-gradient(135deg, " + tier.color + "25, " + tier.color + "10)",
                          color: tier.color, fontFamily: mono,
                          border: "1px solid " + tier.color + "30",
                          boxShadow: "0 0 12px " + tier.color + "15",
                        }}>Current</span>
                      )}
                      {isHidden && (
                        <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{
                          background: "linear-gradient(135deg, rgba(124,77,255,0.12), rgba(212,184,255,0.08))",
                          color: "#d4b8ff", fontFamily: mono,
                          border: "1px solid rgba(212,184,255,0.2)",
                          boxShadow: "0 0 12px rgba(179,136,255,0.15), inset 0 0 8px rgba(179,136,255,0.05)",
                        }}>Divine</span>
                      )}
                      {isLocked && !isHidden && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                      )}
                    </div>

                    {/* Tier name */}
                    <h3 className="text-base font-bold mb-0.5" style={{
                      fontFamily: hd,
                      background: isHidden
                        ? "linear-gradient(135deg, #7c4dff, #b388ff, #e0d0ff, #d4b8ff, #b388ff, #7c4dff)"
                        : "linear-gradient(135deg, " + tier.colorDark + ", " + tier.color + ", " + tier.colorLight + ", " + tier.color + ")",
                      backgroundSize: isHidden ? "300% 100%" : "100% 100%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      filter: isHidden ? "drop-shadow(0 0 14px rgba(179,136,255,0.6))" : "drop-shadow(0 0 8px " + tier.color + "40)",
                    }}>
                      {isHidden ? "? ? ? ? ? ?" : tier.name}
                    </h3>
                    <span className="text-[10px] uppercase mb-3" style={{
                      fontFamily: mono,
                      color: isHidden ? "#d4b8ff" : tier.color + "60",
                      letterSpacing: isHidden ? "0.3em" : "0.2em",
                      textShadow: isHidden ? "0 0 10px rgba(212,184,255,0.4)" : "none",
                    }}>
                      {isHidden ? "Classified tier" : "Tier " + tier.icon}
                    </span>

                    {/* Requirements - ONLY Deposit line, no Referrals */}
                    <div className="flex flex-col gap-2.5 mt-1 mb-4">
                      {isHidden ? (
                        <>
                          <div className="flex items-center gap-2.5">
                            <div className="w-1.5 h-1.5 rotate-45" style={{ background: "#d4b8ff", boxShadow: "0 0 6px rgba(212,184,255,0.6)", animation: "crystalShimmer 2s ease-in-out infinite" }} />
                            <span className="text-[11px] font-light" style={{ fontFamily: bd, color: "rgba(212,184,255,0.5)" }}>Unlocked after Ruby completion</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2.5">
                          <div className="w-1.5 h-1.5 rotate-45" style={{ background: tier.color, boxShadow: "0 0 6px " + tier.color + "60" }} />
                          <span className="text-[11px] text-slate-300 font-light" style={{ fontFamily: bd }}>Deposit: <span className="font-semibold text-white">{tier.deposit}</span></span>
                        </div>
                      )}
                    </div>

                    {/* ── Mini Tesseract decoration ── */}
                    <div className="absolute pointer-events-none" style={{
                      bottom: 50, right: 14, opacity: isHidden ? 0.25 : 0.18,
                      animation: "miniTessFloat " + (10 + idx * 1.5) + "s ease-in-out infinite",
                      animationDelay: (idx * -1.8) + "s",
                    }}>
                      <svg width="24" height="24" viewBox="0 0 40 40" fill="none" style={{
                        animation: "miniTessRotate " + (20 + idx * 4) + "s linear infinite",
                        animationDelay: (idx * -3) + "s",
                      }}>
                        <rect x="4" y="4" width="20" height="20" stroke={tier.color} strokeWidth="0.6" fill="none" />
                        <rect x="12" y="12" width="20" height="20" stroke={tier.color} strokeWidth="0.6" fill="none" />
                        <line x1="4" y1="4" x2="12" y2="12" stroke={tier.color} strokeWidth="0.4" />
                        <line x1="24" y1="4" x2="32" y2="12" stroke={tier.color} strokeWidth="0.4" />
                        <line x1="4" y1="24" x2="12" y2="32" stroke={tier.color} strokeWidth="0.4" />
                        <line x1="24" y1="24" x2="32" y2="32" stroke={tier.color} strokeWidth="0.4" />
                        <circle cx="18" cy="18" r="1.5" fill={tier.color} opacity="0.5">
                          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="3s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    </div>

                    {/* Bonus yield */}
                    <div className="mt-auto pt-3 relative">
                      <div style={{
                        position: "absolute", top: 0, left: "10%", width: "80%", height: 1,
                        background: "linear-gradient(90deg, transparent, " + tier.color + "30, " + tier.colorLight + "50, " + tier.color + "30, transparent)",
                      }} />
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-500 uppercase tracking-widest" style={{ fontFamily: mono }}>Bonus Yield</span>
                        <span className="text-sm font-bold" style={{
                          fontFamily: hd,
                          background: isHidden
                            ? "linear-gradient(90deg, #7c4dff, #b388ff, #e0d0ff, #b388ff, #7c4dff)"
                            : "linear-gradient(90deg, " + tier.color + ", " + tier.colorLight + ")",
                          backgroundSize: isHidden ? "200% 100%" : "100% 100%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          filter: isHidden ? "drop-shadow(0 0 8px rgba(179,136,255,0.6))" : "drop-shadow(0 0 4px " + tier.color + "40)",
                        }}>{isHidden ? "∞" : "+" + tier.bonus}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>


        {/* ── Deposit FAQ — accordion style ── */}
        <div className="rounded-2xl overflow-hidden" style={{ border: dk ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0" }}>
          <div className="px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.03)" : "#f8fafc", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
            <h3 className="text-sm font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Frequently Asked</h3>
          </div>
          <div className="flex flex-col" style={{ background: dk ? "rgba(255,255,255,0.015)" : "white" }}>
            {[
              { q: "What's the minimum deposit?", a: "No minimum — any amount of USDT, USDC, or DAI starts farming points immediately." },
              { q: "How long until confirmation?", a: "Usually 1-5 minutes (6 network confirmations). Tron is fastest, Ethereum is slowest." },
              { q: "Can I withdraw anytime?", a: "No. Deposits are locked for the duration of your staking period. Early withdrawals are not supported to ensure protocol stability and optimal yields for all participants." },
              { q: "How are points calculated?", a: "Every $1 deposited earns 1 point per day, multiplied by your tier bonus (up to 2x)." },
            ].map(function(f, fi) {
              return (
                <div key={fi} className="px-5 py-4" style={{ borderBottom: fi < 3 ? (dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9") : "none" }}>
                  <p className="text-xs font-semibold mb-1.5" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#334155" }}>{f.q}</p>
                  <p className="text-[11px] text-slate-500 font-light leading-relaxed" style={{ fontFamily: bd }}>{f.a}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ONBOARDING TOUR (3 steps after first signup)
   ═══════════════════════════════════════════════ */
function OnboardingTour({ onComplete }) {
  var stepState = useState(0);
  var step = stepState[0];
  var setStep = stepState[1];

  var steps = [
    { title: "Welcome to Gloom!", desc: "Your quantum-secure DeFi dashboard is ready. Let's get you started in 3 quick steps.", icon: "◈", col: "#a78bfa" },
    { title: "Deposit & Earn Points", desc: "Head to the Points page to deposit USDT, USDC, or DAI. Every $1 deposited = 1 point farmed daily.", icon: "✦", col: "#4ade80" },
    { title: "Refer & Level Up", desc: "Share your referral code to unlock higher tiers and bonus yields up to 55%. Check the Referrals page!", icon: "⊕", col: "#06b6d4" },
  ];

  var s = steps[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}>
      <div className="rounded-2xl p-8 max-w-md w-full mx-4 text-center" style={{ background: "#0c1018", border: "1px solid rgba(139,92,246,0.15)", boxShadow: "0 32px 64px rgba(0,0,0,0.5)" }}>
        {/* Step indicator */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map(function(_, idx) {
            return <div key={idx} className="h-1 rounded-full transition-all" style={{ width: idx === step ? 32 : 12, background: idx === step ? s.col : "rgba(255,255,255,0.1)" }} />;
          })}
        </div>

        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: s.col + "15", border: "1px solid " + s.col + "25" }}>
          <span className="text-2xl" style={{ color: s.col }}>{s.icon}</span>
        </div>

        <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: hd }}>{s.title}</h2>
        <p className="text-sm text-slate-400 font-light leading-relaxed mb-8" style={{ fontFamily: bd }}>{s.desc}</p>

        <div className="flex gap-3 justify-center">
          {step > 0 && (
            <button onClick={function() { setStep(step - 1); }} className="px-5 py-2.5 rounded-xl text-sm font-medium text-slate-400 transition-all hover:text-white" style={{ fontFamily: hd, border: "1px solid rgba(255,255,255,0.08)" }}>
              Back
            </button>
          )}
          <button onClick={function() {
            if (step < steps.length - 1) { setStep(step + 1); }
            else { onComplete(); addToast("Welcome aboard! Start exploring.", "success"); }
          }} className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105" style={{ fontFamily: hd, background: "linear-gradient(135deg, " + s.col + ", #6366f1)", boxShadow: "0 8px 24px " + s.col + "30" }}>
            {step < steps.length - 1 ? "Next" : "Let's Go!"}
          </button>
        </div>

        <button onClick={function() { onComplete(); }} className="mt-4 text-xs text-slate-600 hover:text-slate-400 transition-colors" style={{ fontFamily: bd }}>
          Skip tour
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DAILY CHECK-IN PAGE
   ═══════════════════════════════════════════════ */
function CheckInPage({ user }) {
  var theme = useTheme();
  var dk = theme.dark;
  var streakState = useState(0);
  var streak = streakState[0];
  var setStreak = streakState[1];
  var checkedState = useState(false);
  var checkedToday = checkedState[0];
  var setCheckedToday = checkedState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  // Check if already checked in today and load streak
  useEffect(function() {
    if (!user || !user.id) return;
    var today = new Date().toISOString().slice(0, 10);
    supabase.from("checkins")
      .select("*")
      .eq("user_id", user.id)
      .order("checked_in_at", { ascending: false })
      .limit(1)
      .then(function(result) {
        if (result.data && result.data.length > 0) {
          var lastCheckin = result.data[0];
          var lastDate = lastCheckin.checked_in_at.slice(0, 10);
          if (lastDate === today) {
            setCheckedToday(true);
            setStreak(lastCheckin.streak || 0);
          } else {
            // Check if yesterday — streak continues, otherwise resets
            var yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            var yesterdayStr = yesterday.toISOString().slice(0, 10);
            if (lastDate === yesterdayStr) {
              setStreak(lastCheckin.streak || 0);
            } else {
              setStreak(0); // streak broken
            }
          }
        }
        setLoading(false);
      });
  }, [user]);

  function doCheckIn() {
    if (checkedToday || !user || !user.id) return;
    var newStreak = streak + 1;
    var pointsEarned = newStreak >= 30 ? 20 : newStreak >= 7 ? 15 : 10;

    setCheckedToday(true);
    setStreak(newStreak);

    // Double-check server-side: make sure no check-in exists for today before inserting
    var today = new Date().toISOString().slice(0, 10);
    supabase.from("checkins")
      .select("id")
      .eq("user_id", user.id)
      .gte("checked_in_at", today + "T00:00:00")
      .lt("checked_in_at", today + "T23:59:59")
      .then(function(checkResult) {
        if (checkResult.data && checkResult.data.length > 0) {
          // Already checked in today (probably from another tab)
          addToast("Already checked in today!", "info");
          return;
        }
        // Safe to insert
        supabase.from("checkins").insert({
          user_id: user.id,
          streak: newStreak,
          points_earned: pointsEarned,
        }).then(function(result) {
          if (result.error) {
            addToast("Check-in saved locally but failed to sync", "info");
          } else {
            // Add points to profile
            supabase.from("profiles")
              .select("total_points")
              .eq("id", user.id)
              .single()
              .then(function(profileResult) {
                if (profileResult.data) {
                  supabase.from("profiles")
                    .update({ total_points: (profileResult.data.total_points || 0) + pointsEarned })
                    .eq("id", user.id)
                    .then(function() {});
                }
              });
            addToast("Check-in successful! Streak: " + newStreak + " days — +" + pointsEarned + " pts", "success");
          }
        });
      });
  }

  var currentPts = streak >= 30 ? 20 : streak >= 7 ? 15 : 10;
  var nextMilestone = streak < 7 ? { days: 7, pts: "15 pts" } : streak < 30 ? { days: 30, pts: "20 pts" } : null;

  var weekDays = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      <header className="flex items-center justify-between px-7 py-4 sticky top-0 z-10" style={{ background: dk ? "#060b11" : "white", borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9" }}>
        <div>
          <h1 className="text-lg font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Daily Check-in</h1>
          <p className="text-xs font-light" style={{ color: dk ? "#64748b" : "#94a3b8" }}>Earn bonus points with daily streaks</p>
        </div>
      </header>

      <div className="flex-1 px-7 py-6 flex flex-col items-center gap-6">
        {/* Check-in card */}
        <div className="w-full max-w-md rounded-2xl p-8 text-center" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(74,222,128,0.08)" : "1px solid #e2e8f0", boxShadow: dk ? "0 0 40px rgba(74,222,128,0.04)" : "0 4px 16px rgba(0,0,0,0.04)" }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: checkedToday ? "rgba(6,182,212,0.12)" : dk ? "rgba(96,165,250,0.08)" : "rgba(96,165,250,0.06)", border: "2px solid " + (checkedToday ? "rgba(74,222,128,0.3)" : "rgba(96,165,250,0.15)"), transition: "all 0.3s ease" }}>
            <span className="text-3xl">{checkedToday ? "✓" : "☀"}</span>
          </div>

          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>
            {checkedToday ? "Checked In!" : "Ready to Check In?"}
          </h2>
          <p className="text-sm font-light mb-6" style={{ color: dk ? "#64748b" : "#94a3b8", fontFamily: bd }}>
            {checkedToday ? "Come back tomorrow to keep your streak going" : "Tap the button to claim your daily bonus points"}
          </p>

          <button onClick={doCheckIn} disabled={checkedToday}
            className="px-8 py-3.5 rounded-xl text-sm font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ fontFamily: hd, background: checkedToday ? (dk ? "rgba(255,255,255,0.05)" : "#f1f5f9") : "linear-gradient(135deg, #4ade80, #22d3ee)", color: checkedToday ? (dk ? "#64748b" : "#94a3b8") : "#000", boxShadow: checkedToday ? "none" : "0 8px 24px rgba(74,222,128,0.25)" }}>
            {checkedToday ? "Already Claimed" : "Check In Now"}
          </button>
        </div>

        {/* Week calendar */}
        <div className="w-full max-w-md rounded-2xl p-5" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
          <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>This Week</h3>
          <div className="flex gap-2">
            {weekDays.map(function(d, i) {
              var isPast = i < (new Date().getDay() || 7) - 1;
              var isToday = i === (new Date().getDay() || 7) - 1;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-2 py-3 rounded-xl" style={{ background: isToday ? (dk ? "rgba(6,182,212,0.06)" : "rgba(74,222,128,0.04)") : "transparent" }}>
                  <span className="text-[10px] font-medium" style={{ color: dk ? "#64748b" : "#94a3b8", fontFamily: mono }}>{d}</span>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                    background: (isPast && i < streak % 7) || (isToday && checkedToday) ? "linear-gradient(135deg, #4ade80, #22d3ee)" : dk ? "rgba(255,255,255,0.04)" : "#f1f5f9",
                    border: isToday ? "2px solid rgba(74,222,128,0.3)" : "none",
                  }}>
                    {((isPast && i < streak % 7) || (isToday && checkedToday)) ? <span className="text-xs text-black font-bold">✓</span> : <span className="text-xs" style={{ color: dk ? "#334155" : "#cbd5e1" }}>–</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="w-full max-w-md flex gap-3">
          {[
            { label: "Current Streak", value: streak + " days", icon: "🔥", col: "#f97316" },
            { label: "Daily Points", value: "+" + currentPts, icon: "✦", col: "#4ade80" },
          ].map(function(s) {
            return (
              <div key={s.label} className="flex-1 rounded-xl p-4 text-center" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
                <span className="text-lg mb-1 block">{s.icon}</span>
                <p className="text-base font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{s.value}</p>
                <p className="text-[10px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Next milestone */}
        {nextMilestone && (
          <div className="w-full max-w-md rounded-xl p-4 flex items-center gap-3" style={{ background: dk ? "rgba(167,139,250,0.05)" : "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.1)" }}>
            <span className="text-lg">🎯</span>
            <div>
              <p className="text-xs font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Next milestone: {nextMilestone.days}-day streak</p>
              <p className="text-xs font-light" style={{ color: dk ? "#64748b" : "#94a3b8", fontFamily: bd }}>Unlocks {nextMilestone.pts} per day — {nextMilestone.days - streak} days to go!</p>
            </div>
          </div>
        )}

        {/* ── Milestone Rewards ── */}
        <div className="w-full max-w-md rounded-2xl p-5" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
          <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Streak Milestones</h3>
          <div className="flex flex-col gap-3">
            {[
              { days: 7, reward: "15 points per day", pts: "15 pts/day", col: "#22d3ee", reached: streak >= 7 },
              { days: 14, reward: "15 points + bonus badge", pts: "15 pts/day", col: "#a78bfa", reached: streak >= 14 },
              { days: 30, reward: "20 points per day", pts: "20 pts/day", col: "#fbbf24", reached: streak >= 30 },
              { days: 60, reward: "20 points + legendary status", pts: "20 pts/day", col: "#f472b6", reached: streak >= 60 },
            ].map(function(m) {
              return (
                <div key={m.days} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{
                  background: m.reached ? m.col + "08" : (dk ? "rgba(255,255,255,0.015)" : "#f8fafc"),
                  border: m.reached ? "1px solid " + m.col + "20" : (dk ? "1px solid rgba(255,255,255,0.03)" : "1px solid #f1f5f9"),
                  opacity: m.reached ? 1 : 0.6,
                }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.col + "12" }}>
                    <span className="text-xs font-bold" style={{ fontFamily: hd, color: m.col }}>{m.days}d</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{m.reward}</p>
                    <p className="text-[10px] text-slate-400 font-light" style={{ fontFamily: bd }}>{m.pts} from check-in</p>
                  </div>
                  {m.reached ? (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: m.col + "15", color: m.col, fontFamily: mono }}>Unlocked</span>
                  ) : (
                    <span className="text-[10px] text-slate-500 font-light" style={{ fontFamily: mono }}>{m.days - streak}d left</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Check-in Tips ── */}
        <div className="w-full max-w-md rounded-2xl p-5" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
          <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Tips</h3>
          <div className="flex flex-col gap-2">
            {[
              { tip: "Check in every day to maintain your streak. Missing a day resets it to zero.", col: "#22d3ee" },
              { tip: "Reach a 7-day streak to earn 15 points per check-in instead of 10.", col: "#a78bfa" },
              { tip: "At 30 days, you earn 20 points per check-in — that's double the base rate.", col: "#fbbf24" },
              { tip: "Check-in points are added directly to your total points balance.", col: "#4ade80" },
            ].map(function(t, ti) {
              return (
                <div key={ti} className="flex items-start gap-2 px-3 py-2 rounded-lg" style={{ background: dk ? "rgba(255,255,255,0.015)" : "#f8fafc" }}>
                  <span className="mt-0.5" style={{ fontSize: 8, color: t.col }}>●</span>
                  <p className="text-[11px] text-slate-400 font-light leading-relaxed" style={{ fontFamily: bd }}>{t.tip}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ACHIEVEMENTS PAGE
   ═══════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════
   TESSERACT BUILDER — Evolves with time on site
   ═══════════════════════════════════════════════ */
var TESS_LEVELS = [
  { name: "Fragment", xpNeeded: 0, color: "#64748b", glow: "rgba(100,116,139,0.15)", edges: 8, vertices: 8, scale: 40, rotSpeed: 0.006, particles: 0, desc: "A basic wireframe cube. Stay to evolve it." },
  { name: "Wireframe", xpNeeded: 30, color: "#22d3ee", glow: "rgba(34,211,238,0.2)", edges: 16, vertices: 10, scale: 48, rotSpeed: 0.008, particles: 0, desc: "The cube awakens. Edges sharpen into cyan." },
  { name: "Charged", xpNeeded: 90, color: "#a78bfa", glow: "rgba(167,139,250,0.25)", edges: 24, vertices: 12, scale: 52, rotSpeed: 0.01, particles: 3, desc: "Purple energy flows through the structure." },
  { name: "Tesseract", xpNeeded: 180, color: "#818cf8", glow: "rgba(129,140,248,0.3)", edges: 32, vertices: 16, scale: 55, rotSpeed: 0.012, particles: 5, desc: "Full 4D hypercube unlocked. Two cubes connected." },
  { name: "Radiant", xpNeeded: 360, color: "#fbbf24", glow: "rgba(251,191,36,0.3)", edges: 32, vertices: 16, scale: 58, rotSpeed: 0.014, particles: 6, desc: "Golden light radiates from every vertex." },
  { name: "Quantum", xpNeeded: 600, color: "#f472b6", glow: "rgba(244,114,182,0.3)", edges: 32, vertices: 16, scale: 60, rotSpeed: 0.016, particles: 8, desc: "Pink quantum fluctuations ripple across faces." },
  { name: "Cosmic", xpNeeded: 1000, color: "#34d399", glow: "rgba(52,211,153,0.3)", edges: 32, vertices: 16, scale: 62, rotSpeed: 0.018, particles: 10, desc: "Emerald cosmic energy. Near transcendence." },
  { name: "Transcendent", xpNeeded: 1500, color: "#e0d0ff", glow: "rgba(224,208,255,0.4)", edges: 32, vertices: 16, scale: 65, rotSpeed: 0.02, particles: 12, desc: "Maximum evolution. Your tesseract is eternal." },
];

function GamePage({ user, xp, bonusPts, parentLevel, completed, running, onStart }) {
  var theme = useTheme();
  var dk = theme.dark;

  var angleState = useState(0);
  var angle = angleState[0];
  var levelUpState = useState(false);
  var showLevelUp = levelUpState[0]; var setShowLevelUp = levelUpState[1];
  var localLevelRef = useState(parentLevel);
  var lastSeenLevel = localLevelRef[0]; var setLastSeenLevel = localLevelRef[1];

  /* Calculate current level */
  var level = 0;
  for (var li = TESS_LEVELS.length - 1; li >= 0; li--) {
    if (xp >= TESS_LEVELS[li].xpNeeded) { level = li; break; }
  }
  var lv = TESS_LEVELS[level];
  var nextLv = level < TESS_LEVELS.length - 1 ? TESS_LEVELS[level + 1] : null;
  var progress = nextLv ? (xp - lv.xpNeeded) / (nextLv.xpNeeded - lv.xpNeeded) : 1;

  /* XP now ticks globally in the parent Gloom component */

  /* Rotation animation */
  useEffect(function() {
    var iv = setInterval(function() {
      angleState[1](function(a) { return a + lv.rotSpeed; });
    }, 30);
    return function() { clearInterval(iv); };
  }, [level]);

  /* Level up visual notification only (points handled by parent) */
  useEffect(function() {
    if (parentLevel > lastSeenLevel) {
      setLastSeenLevel(parentLevel);
      setShowLevelUp(true);
      var t = setTimeout(function() { setShowLevelUp(false); }, 3000);
      return function() { clearTimeout(t); };
    }
  }, [parentLevel]);

  /* 4D vertices */
  var verts4D = [];
  var vi, vx, vy, vz, vw;
  var vertCount = level >= 3 ? 16 : (level >= 2 ? 12 : 8);
  for (vi = 0; vi < 16; vi++) {
    vx = (vi & 1) ? 1 : -1;
    vy = (vi & 2) ? 1 : -1;
    vz = (vi & 4) ? 1 : -1;
    vw = (vi & 8) ? 1 : -1;
    verts4D.push([vx, vy, vz, vw]);
  }

  /* Edges based on level */
  var edges = [];
  var ea, eb;
  for (ea = 0; ea < vertCount; ea++) {
    for (eb = ea + 1; eb < vertCount; eb++) {
      var diff = 0;
      for (var d = 0; d < 4; d++) { if (verts4D[ea][d] !== verts4D[eb][d]) diff++; }
      if (diff === 1) edges.push([ea, eb]);
    }
  }

  /* Project 4D → 2D */
  var cx = 180, cy = 180, sc = lv.scale;
  var cosA = Math.cos(angle), sinA = Math.sin(angle);
  var cosB = Math.cos(angle * 0.7), sinB = Math.sin(angle * 0.7);
  var cosC = Math.cos(angle * 0.4), sinC = Math.sin(angle * 0.4);

  var proj = verts4D.map(function(v) {
    var x1 = v[0] * cosA - v[3] * sinA;
    var w1 = v[0] * sinA + v[3] * cosA;
    var y1 = v[1] * cosB - v[2] * sinB;
    var z1 = v[1] * sinB + v[2] * cosB;
    /* Extra rotation for higher levels */
    if (level >= 4) {
      var x2 = x1 * cosC - z1 * sinC;
      z1 = x1 * sinC + z1 * cosC;
      x1 = x2;
    }
    var persp = 3 / (3 - w1);
    return { x: cx + x1 * sc * persp, y: cy + y1 * sc * persp, z: z1, p: persp };
  });

  /* Floating particles around tesseract */
  var particles = [];
  for (var pi = 0; pi < lv.particles; pi++) {
    var pAngle = angle * 0.5 + (pi * Math.PI * 2 / lv.particles);
    var pRadius = 90 + Math.sin(angle * 0.3 + pi) * 20;
    particles.push({
      x: cx + Math.cos(pAngle) * pRadius,
      y: cy + Math.sin(pAngle) * pRadius,
      size: 2 + Math.sin(angle + pi * 2) * 1,
      opacity: 0.3 + Math.sin(angle * 0.8 + pi) * 0.3,
    });
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      <style>{"\n@keyframes tessLevelUp { 0% { opacity: 0; transform: translateY(10px) scale(0.9); } 10% { opacity: 1; transform: translateY(0) scale(1.05); } 90% { opacity: 1; transform: translateY(0) scale(1); } 100% { opacity: 0; transform: translateY(-10px) scale(0.95); } }\n@keyframes tessParticlePulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.5); } }\n"}</style>

      <header className="flex items-center justify-between px-8 py-5 sticky top-0 z-10" style={{
        background: dk ? "rgba(6,11,17,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px) saturate(1.5)",
        borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.06)",
      }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>Tesseract Builder</h1>
          <p className="text-[11px] font-light mt-0.5" style={{ color: dk ? "#64748b" : "#94a3b8", letterSpacing: "0.02em" }}>Evolve your tesseract by staying on the platform</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: lv.color + "10", border: "1px solid " + lv.color + "20" }}>
            <span className="text-[10px] font-medium" style={{ fontFamily: mono, color: "#64748b" }}>Level</span>
            <span className="text-sm font-bold" style={{ fontFamily: hd, color: lv.color }}>{level + 1}/8</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.12)" }}>
            <span className="text-[10px] font-medium" style={{ fontFamily: mono, color: "#64748b" }}>Bonus</span>
            <span className="text-sm font-bold" style={{ fontFamily: hd, color: "#a78bfa" }}>+{bonusPts}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 px-7 py-6 flex flex-col items-center">

        {/* Not started yet — show Start button */}
        {!running && !completed && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div style={{ width: 200, height: 200, marginBottom: 24 }}>
              <svg width="200" height="200" viewBox="0 0 360 360">
                <defs><radialGradient id="startGlow"><stop offset="0%" stopColor="rgba(139,92,246,0.15)" /><stop offset="100%" stopColor="transparent" /></radialGradient></defs>
                <circle cx="180" cy="180" r="120" fill="url(#startGlow)" />
                <rect x="155" y="135" width="50" height="50" rx="4" fill="none" stroke={dk ? "#64748b" : "#94a3b8"} strokeWidth="1.5" opacity="0.5" transform="rotate(15 180 160)" />
                <rect x="150" y="140" width="60" height="60" rx="4" fill="none" stroke={dk ? "#a78bfa" : "#7c3aed"} strokeWidth="1.5" opacity="0.3" transform="rotate(-10 180 170)" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Daily Tesseract Builder</h2>
            <p className="text-xs text-slate-400 font-light mb-1 max-w-xs" style={{ fontFamily: bd }}>Stay on the platform to evolve your tesseract. Earn bonus points as you level up through 8 stages.</p>
            <p className="text-[10px] mb-6" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>Resets daily at midnight. One session per day.</p>
            <button
              onClick={onStart}
              className="px-8 py-3.5 rounded-2xl text-sm font-semibold transition-all hover:scale-105 active:scale-95 relative overflow-hidden"
              style={{
                fontFamily: hd,
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                color: "white",
                boxShadow: "0 8px 28px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
            >
              <span className="relative z-10">Start Building</span>
              <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 50%, transparent 60%)", backgroundSize: "200% 100%", animation: "borderShimmer 2.5s linear infinite" }} />
            </button>

            {/* Daily rewards preview */}
            <div className="w-full max-w-sm mt-8 flex gap-3">
              {[
                { label: "Max XP", value: "1,500s", icon: "⚡", col: "#22d3ee" },
                { label: "Stages", value: "8 levels", icon: "◈", col: "#a78bfa" },
                { label: "Max Bonus", value: "+40 pts", icon: "✦", col: "#fbbf24" },
              ].map(function(r) {
                return (
                  <div key={r.label} className="flex-1 rounded-xl p-3 text-center" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
                    <span className="text-base mb-1 block" style={{ color: r.col }}>{r.icon}</span>
                    <p className="text-sm font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{r.value}</p>
                    <p className="text-[9px] uppercase tracking-widest" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>{r.label}</p>
                  </div>
                );
              })}
            </div>

            {/* What to expect */}
            <div className="w-full max-w-sm mt-4 rounded-xl p-4" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
              <h4 className="text-xs font-semibold mb-2" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>What to expect</h4>
              <div className="flex flex-col gap-1.5">
                {[
                  "Your tesseract earns 1 XP per second automatically",
                  "Watch it evolve through 8 stages with new visual effects",
                  "Every level-up awards bonus points to your balance",
                  "Takes ~25 minutes to reach max level (Transcendent)",
                ].map(function(t, ti) {
                  return (
                    <div key={ti} className="flex items-start gap-2">
                      <span className="mt-0.5" style={{ fontSize: 7, color: "#a78bfa" }}>●</span>
                      <p className="text-[11px] text-slate-400 font-light" style={{ fontFamily: bd }}>{t}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Completed for today */}
        {completed && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="mb-6 relative">
              <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: "rgba(74,222,128,0.08)", border: "2px solid rgba(74,222,128,0.2)" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div className="absolute inset-0 rounded-full" style={{ boxShadow: "0 0 40px rgba(74,222,128,0.15)" }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Tesseract Complete!</h2>
            <p className="text-xs text-slate-400 font-light mb-3 max-w-xs" style={{ fontFamily: bd }}>You reached maximum evolution today. Come back tomorrow for another session.</p>
            <div className="flex gap-4 mt-2">
              <div className="rounded-xl px-5 py-3 text-center" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
                <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Final Stage</span>
                <span className="text-sm font-bold" style={{ fontFamily: hd, color: lv.color }}>{lv.name}</span>
              </div>
              <div className="rounded-xl px-5 py-3 text-center" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
                <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>XP Earned</span>
                <span className="text-sm font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{xp}s</span>
              </div>
              <div className="rounded-xl px-5 py-3 text-center" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
                <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Bonus Points</span>
                <span className="text-sm font-bold" style={{ fontFamily: hd, color: "#a78bfa" }}>+{bonusPts}</span>
              </div>
            </div>
          </div>
        )}

        {/* Active session — show normal game UI */}
        {running && !completed && (<>
        {/* Stats row */}
        <div className="flex gap-4 mb-5 w-full max-w-xl">
          <div className="flex-1 rounded-xl px-4 py-3" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
            <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Stage</span>
            <span className="text-base font-bold" style={{ fontFamily: hd, color: lv.color }}>{lv.name}</span>
          </div>
          <div className="flex-1 rounded-xl px-4 py-3" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
            <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Time XP</span>
            <span className="text-base font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>{xp}<span className="text-xs font-normal text-slate-500">s</span></span>
          </div>
          <div className="flex-1 rounded-xl px-4 py-3" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
            <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Edges</span>
            <span className="text-base font-bold" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>{edges.length}</span>
          </div>
          <div className="flex-1 rounded-xl px-4 py-3" style={{ background: dk ? "rgba(255,255,255,0.025)" : "white", border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
            <span className="text-[9px] uppercase tracking-widest block mb-1" style={{ fontFamily: mono, color: "#64748b" }}>Season Bonus</span>
            <span className="text-base font-bold" style={{ fontFamily: hd, color: "#a78bfa" }}>+{bonusPts}</span>
          </div>
        </div>

        {/* XP progress bar */}
        {nextLv && (
          <div className="w-full max-w-xl mb-6">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-medium" style={{ fontFamily: bd, color: lv.color }}>{lv.name}</span>
              <span className="text-[10px]" style={{ fontFamily: mono, color: dk ? "#475569" : "#94a3b8" }}>{xp - lv.xpNeeded} / {nextLv.xpNeeded - lv.xpNeeded}s to {nextLv.name}</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: dk ? "rgba(255,255,255,0.04)" : "#f1f5f9" }}>
              <div className="h-2 rounded-full transition-all" style={{
                width: (progress * 100) + "%",
                background: "linear-gradient(90deg, " + lv.color + ", " + (nextLv ? nextLv.color : lv.color) + ")",
                boxShadow: "0 0 8px " + lv.color + "40",
              }} />
            </div>
          </div>
        )}

        {/* Tesseract display */}
        <div className="relative rounded-2xl overflow-hidden" style={{
          width: 360, height: 360,
          background: dk ? "rgba(0,0,0,0.3)" : "rgba(248,250,252,0.8)",
          border: "1px solid " + lv.color + "15",
          boxShadow: "0 0 60px " + lv.glow + ", inset 0 0 40px rgba(0,0,0,0.1)",
        }}>
          {/* Ambient glow */}
          <div className="absolute pointer-events-none" style={{
            top: "50%", left: "50%", width: 200, height: 200,
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, " + lv.color + "15 0%, " + lv.color + "05 50%, transparent 70%)",
            filter: "blur(20px)",
          }} />

          <svg width="360" height="360" viewBox="0 0 360 360" style={{ position: "relative", zIndex: 2 }}>
            <defs>
              <filter id="tessGlow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* Edges */}
            {edges.map(function(e, i) {
              var a = proj[e[0]], b = proj[e[1]];
              if (!a || !b) return null;
              var avgP = (a.p + b.p) / 2;
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={lv.color} strokeWidth={avgP * 0.8} opacity={0.3 + avgP * 0.15}
                filter={level >= 4 ? "url(#tessGlow)" : "none"}
                style={{ transition: "stroke 1.5s ease" }} />;
            })}

            {/* Vertices */}
            {proj.slice(0, vertCount).map(function(p, i) {
              return (
                <g key={"v" + i}>
                  {level >= 2 && <circle cx={p.x} cy={p.y} r={p.p * 3} fill={lv.color} opacity={0.08} />}
                  <circle cx={p.x} cy={p.y} r={p.p * 1.5} fill={lv.color} opacity={0.4 + p.p * 0.2}
                    style={{ transition: "fill 1.5s ease" }} />
                </g>
              );
            })}

            {/* Orbiting particles */}
            {particles.map(function(pt, i) {
              return (
                <circle key={"pt" + i} cx={pt.x} cy={pt.y} r={pt.size}
                  fill={lv.color} opacity={pt.opacity}
                  style={{ filter: "blur(0.5px)" }} />
              );
            })}

            {/* Center glow for high levels */}
            {level >= 5 && (
              <circle cx={cx} cy={cy} r={15 + Math.sin(angle * 2) * 5} fill={lv.color} opacity={0.06 + Math.sin(angle) * 0.03} />
            )}
          </svg>

          {/* Level up notification */}
          {showLevelUp && (
            <div className="absolute inset-x-0 top-6 flex justify-center pointer-events-none" style={{ animation: "tessLevelUp 3s ease-out forwards", zIndex: 10 }}>
              <div className="px-5 py-2.5 rounded-full" style={{
                background: dk ? "rgba(6,11,17,0.9)" : "rgba(255,255,255,0.95)",
                border: "1px solid " + lv.color + "30",
                boxShadow: "0 8px 30px " + lv.color + "20",
              }}>
                <span className="text-xs font-bold" style={{ fontFamily: hd, color: lv.color }}>Level Up! — {lv.name}</span>
                <span className="text-[10px] ml-2" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>+{level * 5} bonus pts</span>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-xs font-light mt-4 mb-6 text-center max-w-sm" style={{ fontFamily: bd, color: dk ? "#64748b" : "#94a3b8" }}>{lv.desc}</p>

        {/* Level roadmap */}
        <div className="w-full max-w-xl">
          <div className="rounded-xl px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
            <h3 className="text-xs font-bold mb-3" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>Evolution Stages</h3>
            <div className="flex flex-col gap-2">
              {TESS_LEVELS.map(function(tl, ti) {
                var reached = ti <= level;
                var isCurrent = ti === level;
                return (
                  <div key={ti} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold" style={{
                      background: reached ? tl.color + "18" : (dk ? "rgba(255,255,255,0.03)" : "#f8fafc"),
                      color: reached ? tl.color : (dk ? "#334155" : "#cbd5e1"),
                      border: isCurrent ? "1px solid " + tl.color + "40" : "1px solid transparent",
                      boxShadow: isCurrent ? "0 0 10px " + tl.color + "20" : "none",
                      fontFamily: mono,
                    }}>{ti + 1}</div>
                    <div className="flex-1">
                      <span className="text-[11px] font-semibold" style={{ fontFamily: hd, color: reached ? (dk ? "white" : "#1e293b") : (dk ? "#334155" : "#cbd5e1") }}>{tl.name}</span>
                      <span className="text-[9px] ml-2" style={{ fontFamily: mono, color: reached ? tl.color : (dk ? "#1e293b" : "#e2e8f0") }}>{tl.xpNeeded}s</span>
                    </div>
                    <span className="text-[9px] font-medium" style={{ fontFamily: mono, color: reached ? "#a78bfa" : (dk ? "#1e293b" : "#e2e8f0") }}>+{(ti) * 5} pts</span>
                    {isCurrent && <span className="text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: tl.color + "15", color: tl.color, fontFamily: mono }}>Now</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-4 w-full max-w-xl">
          <div className="rounded-xl px-5 py-4" style={{ background: dk ? "rgba(255,255,255,0.02)" : "white", border: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid #e2e8f0" }}>
            <h3 className="text-xs font-bold mb-3" style={{ fontFamily: hd, color: dk ? "white" : "#1e293b" }}>How It Works</h3>
            <div className="flex flex-col gap-2">
              {[
                { icon: "◈", text: "Your tesseract earns 1 XP every second you're on the platform" },
                { icon: "✦", text: "Each evolution stage unlocks new colors, geometry, and particle effects" },
                { icon: "◇", text: "Every level-up awards bonus points credited at season end" },
                { icon: "▸", text: "Reach Transcendent (Level 8) for maximum rewards and visual effects" },
              ].map(function(item, i) {
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <span className="text-[10px] mt-0.5" style={{ color: "#a78bfa" }}>{item.icon}</span>
                    <span className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.8)" : "#64748b" }}>{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STATUS PAGE
   ═══════════════════════════════════════════════ */
function InformationPage() {
  var theme = useTheme();
  var dk = theme.dark;
  var hovState = useState(-1);
  var hoveredNode = hovState[0];
  var setHoveredNode = hovState[1];
  var angleState = useState(0);
  var angle = angleState[0];

  /* Airdrop info nodes — each is a branch off the tesseract */
  var nodes = [
    { label: "Eligibility", desc: "Deposit any amount and earn points during an active season to qualify for the airdrop.", icon: "◈", color: "#22d3ee", color2: "#67e8f9" },
    { label: "Snapshot", desc: "A snapshot of all balances and points will be taken at the end of each season.", icon: "◎", color: "#a78bfa", color2: "#c4b5fd" },
    { label: "Distribution", desc: "20% of total token supply will be distributed in the airdrop, proportionally based on your total points earned.", icon: "⬡", color: "#f472b6", color2: "#f9a8d4" },
    { label: "Vesting", desc: "30% unlocked at TGE. Remaining 70% vests linearly over 6 months.", icon: "◇", color: "#fbbf24", color2: "#fde68a" },
    { label: "Multipliers", desc: "Higher tiers and more referrals increase your airdrop allocation multiplier.", icon: "⚡", color: "#34d399", color2: "#6ee7b7" },
    { label: "Timeline", desc: "TGE is planned after Season 2 concludes. Exact date announced on socials.", icon: "✦", color: "#fb923c", color2: "#fdba74" },
  ];

  /* Tesseract 4D vertices */
  var verts4D = [];
  var vi, vx, vy, vz, vw;
  for (vi = 0; vi < 16; vi++) {
    vx = (vi & 1) ? 1 : -1;
    vy = (vi & 2) ? 1 : -1;
    vz = (vi & 4) ? 1 : -1;
    vw = (vi & 8) ? 1 : -1;
    verts4D.push([vx, vy, vz, vw]);
  }

  /* Edges */
  var edges = [];
  var ea, eb;
  for (ea = 0; ea < 16; ea++) {
    for (eb = ea + 1; eb < 16; eb++) {
      var diff = 0;
      for (var d = 0; d < 4; d++) { if (verts4D[ea][d] !== verts4D[eb][d]) diff++; }
      if (diff === 1) edges.push([ea, eb]);
    }
  }

  /* Animate rotation */
  useEffect(function() {
    var iv = setInterval(function() {
      angleState[1](function(a) { return a + 0.008; });
    }, 30);
    return function() { clearInterval(iv); };
  }, []);

  /* Project 4D → 2D */
  var cx = 200, cy = 200, scale = 55;
  var cosA = Math.cos(angle), sinA = Math.sin(angle);
  var cosB = Math.cos(angle * 0.7), sinB = Math.sin(angle * 0.7);

  var projected = verts4D.map(function(v) {
    var x1 = v[0] * cosA - v[3] * sinA;
    var w1 = v[0] * sinA + v[3] * cosA;
    var y1 = v[1] * cosB - v[2] * sinB;
    var z1 = v[1] * sinB + v[2] * cosB;
    var persp = 3 / (3 - w1);
    return { x: cx + x1 * scale * persp, y: cy + y1 * scale * persp, z: z1, p: persp };
  });

  /* Branch endpoint positions — evenly spaced around the tesseract */
  var branchAngles = nodes.map(function(_, i) { return (Math.PI * 2 * i / nodes.length) - Math.PI / 2; });
  var branchRadius = 155;
  var branchPts = branchAngles.map(function(a) {
    return { x: cx + Math.cos(a) * branchRadius, y: cy + Math.sin(a) * branchRadius };
  });

  /* Find closest tesseract vertex to each branch for the connecting line */
  var branchSources = branchPts.map(function(bp) {
    var bestIdx = 0, bestDist = 99999;
    projected.forEach(function(p, pi) {
      var dd = Math.sqrt((p.x - bp.x) * (p.x - bp.x) + (p.y - bp.y) * (p.y - bp.y));
      if (dd < bestDist) { bestDist = dd; bestIdx = pi; }
    });
    return bestIdx;
  });

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" style={{ fontFamily: bd, background: dk ? "#060b11" : "#fafbfc" }}>
      <header className="flex items-center justify-between px-8 py-5 sticky top-0 z-10" style={{
        background: dk ? "rgba(6,11,17,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px) saturate(1.5)",
        borderBottom: dk ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.06)",
      }}>
        <div>
          <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>Information</h1>
          <p className="text-[11px] font-light mt-0.5" style={{ color: dk ? "#64748b" : "#94a3b8", letterSpacing: "0.02em" }}>Airdrop details — hover each node</p>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-7 py-6 relative">
        {/* Ambient glow */}
        <div className="absolute pointer-events-none" style={{ width: 500, height: 500, top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "radial-gradient(circle, rgba(6,182,212,0.04) 0%, rgba(99,102,241,0.02) 40%, transparent 65%)" }} />

        {/* Interactive tesseract with branches */}
        <div className="relative" style={{ width: 400, height: 400 }}>
          <svg width="400" height="400" viewBox="0 0 400 400" style={{ position: "absolute", top: 0, left: 0 }}>
            <defs>
              {nodes.map(function(n, i) {
                return (
                  <linearGradient key={"bg" + i} id={"branchGrad" + i} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={n.color} stopOpacity="0.6" />
                    <stop offset="100%" stopColor={n.color2} stopOpacity="0.2" />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Tesseract edges */}
            {edges.map(function(e, i) {
              var a = projected[e[0]], b = projected[e[1]];
              var avgP = (a.p + b.p) / 2;
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={dk ? "rgba(34,211,238,0.15)" : "rgba(6,182,212,0.2)"} strokeWidth={avgP * 0.6} />;
            })}

            {/* Tesseract vertices */}
            {projected.map(function(p, i) {
              return <circle key={i} cx={p.x} cy={p.y} r={p.p * 1.2} fill="#22d3ee" opacity={0.3 + p.p * 0.15} />;
            })}

            {/* Branch lines from tesseract to info nodes */}
            {nodes.map(function(n, i) {
              var src = projected[branchSources[i]];
              var dst = branchPts[i];
              var isHov = hoveredNode === i;
              return (
                <line key={"br" + i} x1={src.x} y1={src.y} x2={dst.x} y2={dst.y}
                  stroke={n.color} strokeWidth={isHov ? 2 : 1}
                  strokeDasharray={isHov ? "none" : "4 4"}
                  opacity={isHov ? 0.8 : 0.2}
                  style={{ transition: "all 0.35s" }} />
              );
            })}
          </svg>

          {/* Info node circles positioned around the tesseract */}
          {nodes.map(function(n, i) {
            var bp = branchPts[i];
            var isHov = hoveredNode === i;
            return (
              <div key={n.label} className="absolute flex flex-col items-center" style={{
                left: bp.x - 32, top: bp.y - 32, width: 64, cursor: "pointer",
                transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isHov ? "scale(1.15)" : "scale(1)",
                zIndex: isHov ? 20 : 5,
              }}
              onMouseEnter={function() { setHoveredNode(i); }}
              onMouseLeave={function() { setHoveredNode(-1); }}
              >
                {/* Outer glow ring */}
                <div className="rounded-full flex items-center justify-center" style={{
                  width: 48, height: 48, position: "relative",
                  background: isHov ? n.color + "15" : "rgba(255,255,255,0.02)",
                  border: "1px solid " + (isHov ? n.color + "50" : "rgba(255,255,255,0.06)"),
                  boxShadow: isHov ? "0 0 25px " + n.color + "30, 0 0 50px " + n.color + "10, inset 0 0 15px " + n.color + "08" : "none",
                  transition: "all 0.35s",
                }}>
                  <span style={{ fontSize: 18, color: isHov ? n.color : (dk ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)"), transition: "color 0.35s", filter: isHov ? "drop-shadow(0 0 6px " + n.color + "60)" : "none" }}>{n.icon}</span>
                  {/* Spinning ring on hover */}
                  {isHov && <div className="absolute rounded-full" style={{ width: 56, height: 56, border: "1px dashed " + n.color + "30", animation: "seasonTriSpin 8s linear infinite" }} />}
                </div>
                {/* Label */}
                <span className="text-[10px] font-semibold mt-1.5 text-center whitespace-nowrap" style={{
                  fontFamily: mono, color: isHov ? n.color : (dk ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)"),
                  transition: "color 0.35s", letterSpacing: "0.05em",
                }}>{n.label}</span>
              </div>
            );
          })}
        </div>

        {/* Info card — shows on hover */}
        <div className="w-full max-w-md mt-4" style={{ minHeight: 80 }}>
          {hoveredNode >= 0 ? (
            <div className="rounded-2xl px-6 py-5 relative overflow-hidden" style={{
              background: dk ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.9)",
              border: "1px solid " + nodes[hoveredNode].color + "25",
              boxShadow: "0 4px 30px rgba(0,0,0,0.15), 0 0 40px " + nodes[hoveredNode].color + "08",
              animation: "premiumFadeIn 0.3s ease-out",
            }}>
              <div className="absolute pointer-events-none" style={{ top: -20, right: -20, width: 80, height: 80, background: "radial-gradient(circle, " + nodes[hoveredNode].color + "08, transparent 70%)" }} />
              <div className="flex items-center gap-3 mb-2">
                <span style={{ fontSize: 16, color: nodes[hoveredNode].color, filter: "drop-shadow(0 0 4px " + nodes[hoveredNode].color + "40)" }}>{nodes[hoveredNode].icon}</span>
                <span className="text-sm font-bold" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>{nodes[hoveredNode].label}</span>
              </div>
              <p className="text-[13px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.9)" : "#64748b" }}>{nodes[hoveredNode].desc}</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-[11px] font-light" style={{ fontFamily: bd, color: dk ? "#475569" : "#94a3b8" }}>Hover a node to learn about the airdrop</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
         STATUS / CHANGELOG
         ═══════════════════════════════════════ */}
      <div className="px-7 pb-8">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: "#22c55e", boxShadow: "0 0 8px rgba(34,197,94,0.5)" }} />
            <h2 className="text-sm font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>Platform Status</h2>
          </div>
          <span className="text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{
            background: "rgba(34,197,94,0.08)", color: "#22c55e", fontFamily: mono,
            border: "1px solid rgba(34,197,94,0.15)",
          }}>All Systems Operational</span>
        </div>

        {/* Status indicators */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { name: "Frontend", status: "Operational", color: "#22c55e" },
            { name: "Authentication", status: "Operational", color: "#22c55e" },
            { name: "Points Engine", status: "Operational", color: "#22c55e" },
            { name: "Referral System", status: "Operational", color: "#22c55e" },
          ].map(function(svc) {
            return (
              <div key={svc.name} className="rounded-xl px-4 py-3" style={{
                background: dk ? "rgba(255,255,255,0.025)" : "white",
                border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
              }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: svc.color, boxShadow: "0 0 6px " + svc.color + "60" }} />
                  <span className="text-[10px] font-semibold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#334155" }}>{svc.name}</span>
                </div>
                <span className="text-[9px]" style={{ fontFamily: mono, color: svc.color }}>{svc.status}</span>
              </div>
            );
          })}
        </div>

        {/* Changelog / What's New */}
        <div className="flex items-center gap-2 mb-4">
          <span style={{ fontSize: 14, color: dk ? "#a78bfa" : "#7c3aed" }}>✦</span>
          <h2 className="text-sm font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>What's New</h2>
        </div>

        <div className="relative" style={{ paddingLeft: 20 }}>
          {/* Timeline line */}
          <div className="absolute" style={{
            left: 5, top: 8, bottom: 8, width: 1,
            background: dk ? "linear-gradient(180deg, rgba(167,139,250,0.3), rgba(34,211,238,0.1), transparent)" : "linear-gradient(180deg, rgba(124,58,237,0.2), rgba(6,182,212,0.1), transparent)",
          }} />

          {[
            {
              version: "v0.4.0", date: "Mar 2026", tag: "Latest", tagColor: "#a78bfa",
              changes: [
                "Referral tier system with 6 levels including hidden tier",
                "Crystal-themed tier cards with animated effects",
                "Mini tesseract decorations on each tier card",
              ]
            },
            {
              version: "v0.3.0", date: "Mar 2026", tag: null, tagColor: null,
              changes: [
                "Interactive tesseract with hover glow animation",
                "Smooth color transitions on tesseract hover",
                "Points overlay repositioned below tesseract",
              ]
            },
            {
              version: "v0.2.0", date: "Feb 2026", tag: null, tagColor: null,
              changes: [
                "Dark and light theme toggle across all pages",
                "Hero redesign with shimmer and glow effects",
                "Feature badges: 6% stablecoin staking, OTC trading",
              ]
            },
            {
              version: "v0.1.0", date: "Feb 2026", tag: "Genesis (Beta)", tagColor: "#22d3ee",
              changes: [
                "Initial platform launch with landing page",
                "Dashboard, referrals, points, check-in, and achievements pages",
                "Season system with points multipliers",
              ]
            },
          ].map(function(release, ri) {
            return (
              <div key={release.version} className="relative mb-5 last:mb-0">
                {/* Timeline dot */}
                <div className="absolute" style={{
                  left: -20, top: 6, width: 11, height: 11, borderRadius: "50%",
                  background: ri === 0 ? "#a78bfa" : (dk ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"),
                  border: ri === 0 ? "2px solid rgba(167,139,250,0.4)" : "2px solid " + (dk ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                  boxShadow: ri === 0 ? "0 0 12px rgba(167,139,250,0.3)" : "none",
                }} />

                <div className="rounded-xl px-5 py-4" style={{
                  background: dk ? (ri === 0 ? "rgba(167,139,250,0.04)" : "rgba(255,255,255,0.02)") : (ri === 0 ? "rgba(124,58,237,0.03)" : "white"),
                  border: dk ? ("1px solid " + (ri === 0 ? "rgba(167,139,250,0.12)" : "rgba(255,255,255,0.04)")) : ("1px solid " + (ri === 0 ? "rgba(124,58,237,0.1)" : "#e2e8f0")),
                }}>
                  {/* Version header */}
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <span className="text-xs font-bold" style={{ fontFamily: mono, color: dk ? "#e2e8f0" : "#1e293b" }}>{release.version}</span>
                    <span className="text-[9px]" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>{release.date}</span>
                    {release.tag && (
                      <span className="text-[8px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{
                        background: release.tagColor + "12",
                        color: release.tagColor,
                        border: "1px solid " + release.tagColor + "20",
                        fontFamily: mono,
                      }}>{release.tag}</span>
                    )}
                  </div>

                  {/* Change items */}
                  <div className="flex flex-col gap-1.5">
                    {release.changes.map(function(change, ci) {
                      return (
                        <div key={ci} className="flex items-start gap-2">
                          <span className="text-[8px] mt-1" style={{ color: dk ? "rgba(167,139,250,0.5)" : "rgba(124,58,237,0.4)" }}>▸</span>
                          <span className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.8)" : "#64748b" }}>{change}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Upcoming section */}
        <div className="mt-6 rounded-xl px-5 py-4 relative overflow-hidden" style={{
          background: dk ? "rgba(34,211,238,0.03)" : "rgba(6,182,212,0.03)",
          border: dk ? "1px solid rgba(34,211,238,0.08)" : "1px solid rgba(6,182,212,0.1)",
        }}>
          <div className="absolute pointer-events-none" style={{ top: -30, right: -30, width: 100, height: 100, background: "radial-gradient(circle, rgba(34,211,238,0.06), transparent 70%)" }} />
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: 12, color: "#22d3ee" }}>◈</span>
            <span className="text-xs font-bold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#1e293b" }}>Coming Soon</span>
            <span className="text-[8px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{
              background: "rgba(34,211,238,0.08)", color: "#22d3ee", fontFamily: mono,
              border: "1px solid rgba(34,211,238,0.15)",
            }}>Roadmap</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {[
              "Supabase backend integration — live authentication and data",
              "Working referral link system with auto-tracking",
              "Season 2 launch with updated point multipliers",
              "OTC points trading marketplace",
            ].map(function(item, i) {
              return (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[8px] mt-1" style={{ color: "rgba(34,211,238,0.4)" }}>◇</span>
                  <span className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.6)" : "#94a3b8" }}>{item}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════
           WHITEPAPER CONTENT
           ═══════════════════════════════════════ */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-5">
            <span style={{ fontSize: 14, color: "#8b5cf6" }}>◈</span>
            <h2 className="text-sm font-bold tracking-tight" style={{ fontFamily: hd, color: dk ? "white" : "#0f172a" }}>Whitepaper</h2>
            <span className="text-[8px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{
              background: "rgba(139,92,246,0.08)", color: "#a78bfa", fontFamily: mono,
              border: "1px solid rgba(139,92,246,0.15)",
            }}>v1.0</span>
          </div>

          {/* Abstract */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>1. Abstract</h3>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom is a next-generation decentralized finance (DeFi) protocol built on quantum-resistant cryptographic foundations. As quantum computing advances threaten the integrity of traditional blockchain encryption, Gloom provides a forward-looking infrastructure for stablecoin lending, yield generation, and decentralized asset management — all secured by NIST-approved lattice-based cryptography and zero-knowledge proof systems.
            </p>
            <p className="text-[11px] font-light leading-relaxed mt-2" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom is a bootstrapped startup, self-funded and operationally lean, backed by the strategic resources and domain expertise of Gloom Ventures — a venture capital arm that has deployed capital across multiple high-growth Web3 startups. The protocol delivers institutional-grade security to retail and institutional participants alike, without reliance on legacy encryption standards vulnerable to Shor's algorithm.
            </p>
          </div>

          {/* Introduction */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>2. Introduction to Gloom</h3>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              The rapid advancement of quantum computing poses an existential threat to current blockchain security models. Algorithms such as RSA and ECDSA — the cryptographic backbone of most blockchains today — are theoretically breakable by sufficiently powerful quantum computers running Shor's algorithm. While full-scale quantum computers capable of this remain years away, the window for proactive defense is narrowing.
            </p>
            <p className="text-[11px] font-light leading-relaxed mt-2" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom was founded on a single thesis: DeFi infrastructure must be quantum-ready before quantum threats materialize, not after. By building on post-quantum cryptographic primitives from day one, Gloom ensures that user funds, transaction integrity, and protocol governance remain secure regardless of advances in quantum hardware.
            </p>

            <h4 className="text-[11px] font-bold mt-3 mb-1.5" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>The Bootstrapped Approach</h4>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Unlike many DeFi protocols that rely on large venture raises and dilutive token sales prior to launch, Gloom has taken a deliberately bootstrapped approach to development. The founding team has self-funded the initial protocol build, smart contract audits, and infrastructure deployment. This ensures full alignment between the team and the community — no early investor lockups, no backdoor token allocations, and no external pressure to compromise on security for speed.
            </p>
          </div>

          {/* Gloom Ventures */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>3. Gloom Ventures — Backing the Ecosystem</h3>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom Ventures is the venture capital and strategic investment arm affiliated with the Gloom ecosystem. Since its inception in early 2025, Gloom Ventures has built a diversified portfolio across the decentralized technology stack — deploying capital into multiple early-stage and growth-stage startups spanning DeFi infrastructure, zero-knowledge proof systems, decentralized identity, cross-chain interoperability, and quantum-resistant networking protocols.
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
              {[
                { label: "Portfolio Companies", value: "12+" },
                { label: "Active Since", value: "Q1 2025" },
                { label: "Focus Sectors", value: "DeFi, ZKP, Post-Quantum" },
                { label: "Geographic Focus", value: "Global" },
              ].map(function(stat) {
                return (
                  <div key={stat.label} className="rounded-lg px-3 py-2" style={{
                    background: dk ? "rgba(139,92,246,0.04)" : "rgba(139,92,246,0.03)",
                    border: dk ? "1px solid rgba(139,92,246,0.08)" : "1px solid rgba(139,92,246,0.1)",
                  }}>
                    <p className="text-[9px] font-medium" style={{ fontFamily: mono, color: dk ? "#64748b" : "#94a3b8" }}>{stat.label}</p>
                    <p className="text-[11px] font-bold mt-0.5" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#1e293b" }}>{stat.value}</p>
                  </div>
                );
              })}
            </div>

            <h4 className="text-[11px] font-bold mt-3 mb-1.5" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>Strategic Alignment</h4>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom Ventures' portfolio strategy is deliberately synergistic with the Gloom Protocol. By funding startups in adjacent verticals — zero-knowledge proof infrastructure, cross-chain bridges, decentralized oracle networks, and privacy-preserving computation — Gloom Ventures creates a network of technology partners that strengthen the broader ecosystem.
            </p>
          </div>

          {/* Protocol Architecture */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>4. Protocol Architecture</h3>
            <p className="text-[11px] font-light leading-relaxed mb-3" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              The Gloom Protocol is designed as a modular, EVM-compatible DeFi platform with quantum-resistant cryptographic layers integrated at the consensus, transaction, and application levels.
            </p>

            {[
              { name: "Gloom Lending Engine", desc: "Algorithmic stablecoin lending markets with dynamic interest rate curves, supporting USDT, USDC, and DAI with a base APY of 6%.", icon: "◈" },
              { name: "Quantum Vault System", desc: "Isolated vault architecture where each user's deposits are cryptographically segregated using lattice-based key encapsulation mechanisms.", icon: "◎" },
              { name: "Points & Rewards Layer", desc: "On-chain and off-chain hybrid system for tracking user participation, deposit-based point accrual, referral bonuses, and seasonal multipliers.", icon: "⬡" },
              { name: "Governance Module", desc: "Token-weighted voting system (post-TGE) enabling $GLOOM holders to propose and vote on protocol parameters, fee structures, and treasury allocations.", icon: "◇" },
            ].map(function(comp) {
              return (
                <div key={comp.name} className="flex items-start gap-2.5 mb-2.5">
                  <span className="text-[10px] mt-0.5" style={{ color: "#22d3ee" }}>{comp.icon}</span>
                  <div>
                    <p className="text-[11px] font-semibold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#1e293b" }}>{comp.name}</p>
                    <p className="text-[10px] font-light leading-relaxed mt-0.5" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.7)" : "#94a3b8" }}>{comp.desc}</p>
                  </div>
                </div>
              );
            })}

            <h4 className="text-[11px] font-bold mt-3 mb-2" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>Multi-Chain Deposit Infrastructure</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { net: "Ethereum", std: "ERC-20", time: "~3 min", assets: "USDT, USDC, DAI" },
                { net: "BNB Smart Chain", std: "BEP-20", time: "~1 min", assets: "USDT, USDC, DAI" },
                { net: "Tron", std: "TRC-20", time: "~30 sec", assets: "USDT" },
              ].map(function(ch) {
                return (
                  <div key={ch.net} className="rounded-lg px-3 py-2" style={{
                    background: dk ? "rgba(34,211,238,0.03)" : "rgba(6,182,212,0.03)",
                    border: dk ? "1px solid rgba(34,211,238,0.08)" : "1px solid rgba(6,182,212,0.1)",
                  }}>
                    <p className="text-[10px] font-bold" style={{ fontFamily: hd, color: dk ? "#e2e8f0" : "#1e293b" }}>{ch.net}</p>
                    <p className="text-[9px] mt-0.5" style={{ fontFamily: mono, color: "#22d3ee" }}>{ch.std}</p>
                    <p className="text-[9px] mt-0.5" style={{ fontFamily: bd, color: dk ? "#64748b" : "#94a3b8" }}>{ch.time} · {ch.assets}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quantum Security */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>5. Quantum-Resistant Security</h3>
            <p className="text-[11px] font-light leading-relaxed mb-3" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Gloom's security architecture is built on three pillars of post-quantum cryptography, each addressing a distinct vulnerability in traditional blockchain systems.
            </p>

            {[
              { title: "Lattice-Based Encryption", desc: "All on-chain key generation and transaction signing utilizes CRYSTALS-Kyber and CRYSTALS-Dilithium — NIST-standardized post-quantum algorithms. These lattice-based schemes provide security guarantees against both classical and quantum adversaries." },
              { title: "Zero-Knowledge Proof Integration", desc: "Gloom employs zk-SNARKs to enable privacy-preserving transaction verification. Users can prove deposit amounts, point balances, and tier eligibility without revealing sensitive financial data on-chain." },
              { title: "Quantum Key Distribution (QKD)", desc: "For inter-validator communication and oracle data feeds, Gloom implements a quantum key distribution layer that ensures key exchange remains secure even against adversaries with access to quantum computing resources." },
            ].map(function(sec) {
              return (
                <div key={sec.title} className="mb-3">
                  <h4 className="text-[11px] font-bold mb-1" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>{sec.title}</h4>
                  <p className="text-[10px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.7)" : "#94a3b8" }}>{sec.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Tokenomics */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>6. Tokenomics & $GLOOM</h3>
            <p className="text-[11px] font-light leading-relaxed mb-3" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              The $GLOOM token serves as the governance and utility token of the Gloom ecosystem. Token generation event (TGE) is planned following the conclusion of Season 2.
            </p>

            <h4 className="text-[11px] font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>Token Distribution</h4>
            {[
              { alloc: "Community Airdrop", pct: "20%", vest: "Unlocked at TGE" },
              { alloc: "Ecosystem & Grants", pct: "25%", vest: "36-month linear" },
              { alloc: "Team & Advisors", pct: "15%", vest: "12-month cliff, 24-month vest" },
              { alloc: "Treasury", pct: "20%", vest: "DAO-governed" },
              { alloc: "Liquidity Provision", pct: "10%", vest: "Unlocked at TGE" },
              { alloc: "Gloom Ventures Reserve", pct: "10%", vest: "18-month linear" },
            ].map(function(row) {
              return (
                <div key={row.alloc} className="flex items-center justify-between py-1.5" style={{
                  borderBottom: dk ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(0,0,0,0.04)",
                }}>
                  <span className="text-[10px] font-medium" style={{ fontFamily: bd, color: dk ? "#e2e8f0" : "#334155" }}>{row.alloc}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold" style={{ fontFamily: mono, color: "#a78bfa" }}>{row.pct}</span>
                    <span className="text-[9px] font-light" style={{ fontFamily: bd, color: dk ? "#64748b" : "#94a3b8", minWidth: 140, textAlign: "right" }}>{row.vest}</span>
                  </div>
                </div>
              );
            })}

            <h4 className="text-[11px] font-bold mt-3 mb-2" style={{ fontFamily: hd, color: dk ? "#22d3ee" : "#0891b2" }}>Token Utility</h4>
            {[
              { label: "Governance", desc: "Vote on protocol parameters, fee structures, and treasury allocations" },
              { label: "Staking", desc: "Stake $GLOOM to earn a share of protocol revenue and enhanced point multipliers" },
              { label: "Fee Discounts", desc: "Reduce transaction fees by holding $GLOOM in your Gloom wallet" },
              { label: "Access", desc: "Unlock premium features, higher deposit limits, and priority support tiers" },
            ].map(function(u) {
              return (
                <div key={u.label} className="flex items-start gap-2 mb-1.5">
                  <span className="text-[8px] mt-1" style={{ color: "rgba(167,139,250,0.5)" }}>▸</span>
                  <p className="text-[10px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.7)" : "#94a3b8" }}><span style={{ fontWeight: 600, color: dk ? "#e2e8f0" : "#334155" }}>{u.label}</span> — {u.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Governance */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>7. Governance</h3>
            <p className="text-[11px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.85)" : "#64748b" }}>
              Post-TGE, the Gloom Protocol will transition to a decentralized governance model where $GLOOM token holders exercise direct control over protocol parameters through on-chain proposals and voting. Governance powers include adjustment of lending interest rate curves, modification of fee structures, allocation of treasury funds to ecosystem grants, approval of new collateral types, and upgrades to the quantum-resistant cryptographic modules. A minimum quorum of 5% of circulating supply is required for proposal passage, with a 72-hour voting window and 48-hour timelock for execution.
            </p>
          </div>

          {/* Roadmap */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(255,255,255,0.025)" : "white",
            border: dk ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}>
            <h3 className="text-xs font-bold mb-3" style={{ fontFamily: hd, color: dk ? "#a78bfa" : "#7c3aed" }}>8. Roadmap</h3>
            <div className="relative" style={{ paddingLeft: 16 }}>
              <div className="absolute" style={{
                left: 4, top: 6, bottom: 6, width: 1,
                background: dk ? "linear-gradient(180deg, rgba(139,92,246,0.3), rgba(34,211,238,0.1), transparent)" : "linear-gradient(180deg, rgba(124,58,237,0.2), rgba(6,182,212,0.1), transparent)",
              }} />
              {[
                { date: "Q1 2025", desc: "Gloom Ventures established. Initial fund deployment begins across Web3 portfolio." },
                { date: "Q2–Q4 2025", desc: "Gloom Ventures expands portfolio. Multiple companies funded across DeFi, ZKP, and infrastructure verticals." },
                { date: "Q1 2026", desc: "Gloom Protocol development begins. Bootstrapped build of core lending engine, quantum-resistant security layer, and points system." },
                { date: "Q1 2026", desc: "Genesis (Beta) season launch. Public testnet and early depositor program with 3x point multiplier." },
                { date: "Q2 2026", desc: "Smart contract audits completed. Mainnet preparation and cross-chain deposit infrastructure finalized." },
                { date: "Q3 2026", desc: "Phase 2 (Growth) begins. Expanded feature set, institutional partnerships, and enhanced governance tooling." },
                { date: "Q4 2026", desc: "Token Generation Event (TGE). $GLOOM distribution to airdrop participants. DEX liquidity launch." },
                { date: "2027+", desc: "Full DAO transition. Phase 3 distribution. Cross-chain expansion and institutional vault products." },
              ].map(function(item, i) {
                return (
                  <div key={i} className="relative mb-3">
                    <div className="absolute" style={{
                      left: -16, top: 5, width: 9, height: 9, borderRadius: "50%",
                      background: i === 0 ? "#a78bfa" : (dk ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                      border: i === 0 ? "2px solid rgba(167,139,250,0.4)" : "2px solid " + (dk ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                    }} />
                    <span className="text-[10px] font-bold" style={{ fontFamily: mono, color: "#22d3ee" }}>{item.date}</span>
                    <p className="text-[10px] font-light leading-relaxed mt-0.5" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.7)" : "#94a3b8" }}>{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="rounded-xl px-5 py-4 mb-4" style={{
            background: dk ? "rgba(251,191,36,0.03)" : "rgba(251,191,36,0.03)",
            border: dk ? "1px solid rgba(251,191,36,0.08)" : "1px solid rgba(251,191,36,0.1)",
          }}>
            <h3 className="text-xs font-bold mb-2" style={{ fontFamily: hd, color: dk ? "#fbbf24" : "#d97706" }}>Legal Disclaimer</h3>
            <p className="text-[10px] font-light leading-relaxed" style={{ fontFamily: bd, color: dk ? "rgba(148,163,184,0.6)" : "#94a3b8", fontStyle: "italic" }}>
              This whitepaper is provided for informational purposes only and does not constitute financial, investment, legal, or tax advice. The $GLOOM token is a utility token intended for use within the Gloom Protocol ecosystem. It is not a security, equity, or debt instrument. Participation in the Gloom ecosystem, including the points program and airdrop, does not guarantee any financial return. Users should conduct their own due diligence and consult with qualified professionals before making any financial decisions.
            </p>
            <p className="text-[9px] font-light mt-2 text-center" style={{ fontFamily: bd, color: dk ? "#64748b" : "#94a3b8" }}>
              © 2026 Gloom Protocol. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════ */
export default function Gloom() {
  var pageState = useState("landing");
  var page = pageState[0];
  var setPage = pageState[1];
  var userState = useState(null);
  var user = userState[0];
  var setUser = userState[1];
  var navState = useState("Dashboard");
  var activeNav = navState[0];
  var setActiveNav = navState[1];
  var darkState = useState(true);
  var isDark = darkState[0];
  var setIsDark = darkState[1];
  var tourState = useState(false);
  var showTour = tourState[0];
  var setShowTour = tourState[1];

  /* ── Tesseract Builder XP — persists across page navigation ── */
  var xpState = useState(0);
  var tessXp = xpState[0]; var setTessXp = xpState[1];
  var tessBonusState = useState(0);
  var tessBonus = tessBonusState[0]; var setTessBonus = tessBonusState[1];
  var tessDateState = useState(function() { return new Date().toDateString(); });
  var tessDate = tessDateState[0]; var setTessDate = tessDateState[1];
  var tessLevelState = useState(0);
  var tessLevel = tessLevelState[0]; var setTessLevel = tessLevelState[1];
  var tessCompletedState = useState(false);
  var tessCompleted = tessCompletedState[0]; var setTessCompleted = tessCompletedState[1];
  var tessRunningState = useState(false);
  var tessRunning = tessRunningState[0]; var setTessRunning = tessRunningState[1];

  /* Check if user already did tesseract today */
  useEffect(function() {
    if (!user || !user.id || page !== "app") return;
    var today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    supabase.from("tesseract_xp").select("*").eq("user_id", user.id).eq("date", today).single()
      .then(function(result) {
        if (result.data && result.data.xp_earned > 0) {
          // Already did tesseract today — load saved progress
          setTessXp(result.data.xp_earned);
          setTessLevel(result.data.level_reached);
          setTessBonus(result.data.bonus_points);
          if (result.data.xp_earned >= 1500) {
            setTessCompleted(true);
          } else {
            // Resume where they left off
            setTessRunning(true);
          }
        }
      });
  }, [user, page]);

  /* Daily reset check — runs on mount and when navigating */
  useEffect(function() {
    var today = new Date().toDateString();
    if (tessDate !== today) {
      setTessXp(0);
      setTessLevel(0);
      setTessBonus(0);
      setTessDate(today);
      setTessCompleted(false);
      setTessRunning(false);
    }
  }, [activeNav]);

  /* Global XP timer — ticks while user is in the app (any page), only if running and not completed */
  useEffect(function() {
    if (page !== "app" || tessCompleted || !tessRunning) return;
    var iv = setInterval(function() {
      setTessXp(function(x) {
        if (x >= 1500) return 1500;
        return x + 1;
      });
    }, 1000);
    return function() { clearInterval(iv); };
  }, [page, tessCompleted, tessRunning]);

  /* Level-up detection — runs in parent so it only fires once per level */
  /* Bonus points are 1/10th: currentLevel * 5 instead of * 50 */
  useEffect(function() {
    var currentLevel = 0;
    for (var i = TESS_LEVELS.length - 1; i >= 0; i--) {
      if (tessXp >= TESS_LEVELS[i].xpNeeded) { currentLevel = i; break; }
    }
    if (currentLevel > tessLevel) {
      setTessBonus(function(b) { return b + currentLevel * 5; });
      setTessLevel(currentLevel);
    }
    // Mark completed at max level
    if (tessXp >= 1500) {
      setTessCompleted(true);
      setTessRunning(false);
    }
  }, [tessXp]);

  /* Save tesseract progress to Supabase every 30 seconds */
  /* Reads latest values from a hidden DOM element to avoid stale closures */
  useEffect(function() {
    if (!user || !user.id || page !== "app" || !tessRunning) return;
    var iv = setInterval(function() {
      var today = new Date().toISOString().slice(0, 10);
      /* Read latest from DOM to avoid stale closure */
      var saveEl = document.getElementById("tess-save-data");
      var xp = saveEl ? parseInt(saveEl.getAttribute("data-xp")) || 0 : 0;
      var lvl = saveEl ? parseInt(saveEl.getAttribute("data-level")) || 0 : 0;
      var bon = saveEl ? parseInt(saveEl.getAttribute("data-bonus")) || 0 : 0;
      supabase.from("tesseract_xp").upsert({
        user_id: user.id,
        date: today,
        xp_earned: xp,
        level_reached: lvl,
        bonus_points: bon,
      }, { onConflict: "user_id,date" }).then(function() {});
    }, 30000);
    return function() { clearInterval(iv); };
  }, [user, page, tessRunning]);

  /* Save on completion */
  useEffect(function() {
    if (!tessCompleted || !user || !user.id) return;
    var today = new Date().toISOString().slice(0, 10);
    supabase.from("tesseract_xp").upsert({
      user_id: user.id,
      date: today,
      xp_earned: tessXp,
      level_reached: tessLevel,
      bonus_points: tessBonus,
    }, { onConflict: "user_id,date" }).then(function() {});
  }, [tessCompleted]);

  var themeValue = { dark: isDark, toggle: function() { setIsDark(function(d) { return !d; }); } };

  /* ── Check for existing session on load ── */
  useEffect(function() {
    supabase.auth.getSession().then(function(result) {
      var session = result.data.session;
      if (session && session.user) {
        supabase.from("profiles").select("*").eq("id", session.user.id).single()
          .then(function(profileResult) {
            var profile = profileResult.data || {};
            setUser({ email: session.user.email, username: profile.username || "user", id: session.user.id });
            setPage("app");
          });
      }
    });

    // Listen for auth changes (login/logout)
    var authListener = supabase.auth.onAuthStateChange(function(event, session) {
      if (event === "SIGNED_OUT") {
        setUser(null);
        setPage("landing");
      }
    });

    return function() { authListener.data.subscription.unsubscribe(); };
  }, []);

  function handleSignUp(u) {
    setUser(u);
    setPage("app");
    setActiveNav("Dashboard");
    setShowTour(true);
    addToast("Account created! Welcome, " + u.username, "success");
  }

  function handleLogin(u) {
    setUser(u);
    setPage("app");
    setActiveNav("Dashboard");
    addToast("Welcome back, " + u.username, "success");
  }

  function handleLogout() {
    supabase.auth.signOut().then(function() {
      setUser(null);
      setPage("landing");
      addToast("Logged out", "success");
    });
  }

  if (page === "landing") return (<><FontLoader /><ToastContainer /><LandingPage onGetStarted={function() { setPage("signup"); }} onLogin={function() { setPage("login"); }} /></>);
  if (page === "signup") return (<><FontLoader /><ToastContainer /><SignupPage onSignUp={handleSignUp} onBack={function() { setPage("landing"); }} onSwitchToLogin={function() { setPage("login"); }} /></>);
  if (page === "login") return (<><FontLoader /><ToastContainer /><LoginPage onLogin={handleLogin} onBack={function() { setPage("landing"); }} onSwitchToSignup={function() { setPage("signup"); }} /></>);

  function renderPage() {
    if (activeNav === "Referrals") return <ReferralsPage user={user} />;
    if (activeNav === "Deposit") return <PointsPage user={user} />;

    if (activeNav === "Check-in") return <CheckInPage user={user} />;
    if (activeNav === "Game") return <GamePage user={user} xp={tessXp} bonusPts={tessBonus} parentLevel={tessLevel} completed={tessCompleted} running={tessRunning} onStart={function() { setTessRunning(true); }} />;
    if (activeNav === "Information") return <InformationPage />;
    return <DashboardPage user={user} />;
  }

  return (
    <ThemeCtx.Provider value={themeValue}>
      <FontLoader />
      <ToastContainer />
      {/* Hidden element to hold latest tesseract values for the save interval */}
      <div id="tess-save-data" data-xp={tessXp} data-level={tessLevel} data-bonus={tessBonus} style={{ display: "none" }} />
      {showTour && <OnboardingTour onComplete={function() { setShowTour(false); }} />}
      <div className="flex h-screen w-full overflow-hidden" style={{ fontFamily: bd }}>
        {/* ── Premium Sidebar ── */}
        <aside className="w-60 flex flex-col py-5 px-3 gap-0.5 shrink-0 relative" style={{
          background: isDark
            ? "linear-gradient(180deg, #080d14 0%, #060a10 50%, #040810 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
          borderRight: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid #eef2f6",
        }}>
          {/* Subtle sidebar glow */}
          {isDark && <div className="absolute pointer-events-none" style={{ top: 0, left: 0, width: "100%", height: 200, background: "radial-gradient(ellipse at 30% 0%, rgba(6,182,212,0.03) 0%, transparent 70%)" }} />}

          {/* Logo + toggle */}
          <div className="flex items-center justify-between px-3 mb-6 relative z-10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center relative overflow-hidden" style={{
                background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                boxShadow: "0 4px 16px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}>
                <svg width="18" height="18" viewBox="0 0 40 40" fill="none"><clipPath id="gloomClipMd"><circle cx="20" cy="20" r="17" /></clipPath><circle cx="20" cy="20" r="17" stroke="white" strokeWidth="1.6" fill="none" /><g clipPath="url(#gloomClipMd)"><path d="M0 24 Q8 14 20 10 Q32 14 40 24" stroke="white" strokeWidth="1.3" fill="none" opacity="0.7" /><path d="M2 28 Q10 18 20 14 Q30 18 38 28" stroke="white" strokeWidth="1.3" fill="none" /><path d="M4 32 Q12 22 20 18 Q28 22 36 32" stroke="white" strokeWidth="1.1" fill="none" opacity="0.8" /><path d="M6 36 Q14 26 20 22 Q26 26 34 36" stroke="white" strokeWidth="0.9" fill="none" opacity="0.5" /></g></svg>
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%)" }} />
              </div>
              <div>
                <span className="font-bold text-base tracking-tight block leading-none" style={{ fontFamily: hd, color: isDark ? "white" : "#0f172a" }}>Gloom</span>
                <span className="text-[9px] font-medium tracking-widest uppercase" style={{ fontFamily: mono, color: isDark ? "#475569" : "#94a3b8" }}>Protocol</span>
              </div>
            </div>
            <button onClick={function() { setIsDark(function(d) { return !d; }); addToast(isDark ? "Light mode enabled" : "Dark mode enabled", "info"); }}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              style={{
                background: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
                border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
                fontSize: 13,
              }}>
              {isDark ? "☀" : "🌙"}
            </button>
          </div>

          {/* Section label */}
          <div className="px-4 mb-2">
            <span className="text-[9px] font-semibold uppercase tracking-[0.15em]" style={{ fontFamily: mono, color: isDark ? "#334155" : "#cbd5e1" }}>Navigation</span>
          </div>

          {/* Nav items */}
          {NAV_ITEMS.map(function(item, navIdx) {
            var active = activeNav === item.label;
            return (
              <button key={item.label} onClick={function() { setActiveNav(item.label); }}
                className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13px] font-medium relative overflow-hidden"
                style={{
                  fontFamily: hd,
                  color: active ? (isDark ? "#22d3ee" : "#0891b2") : (isDark ? "#64748b" : "#94a3b8"),
                  background: active ? (isDark ? "rgba(6,182,212,0.08)" : "rgba(6,182,212,0.06)") : "transparent",
                  border: active ? (isDark ? "1px solid rgba(6,182,212,0.12)" : "1px solid rgba(6,182,212,0.1)") : "1px solid transparent",
                  boxShadow: active ? (isDark ? "0 4px 16px rgba(6,182,212,0.06)" : "0 2px 8px rgba(6,182,212,0.04)") : "none",
                  transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                  animation: "sidebarItemIn 0.5s cubic-bezier(0.16,1,0.3,1) " + (navIdx * 0.06) + "s both",
                  transform: active ? "translateX(2px)" : "translateX(0)",
                }}>
                {/* Active indicator line */}
                {active && <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full" style={{ background: "linear-gradient(180deg, #22d3ee, #06b6d4)", animation: "scaleIn 0.3s ease-out both" }} />}
                <span className="text-sm w-5 text-center" style={{ opacity: active ? 1 : 0.6, transition: "opacity 0.3s, transform 0.3s", transform: active ? "scale(1.15)" : "scale(1)" }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}

          {/* User card */}
          <div className="mt-5 px-2">
            <div className="flex items-center gap-2.5 rounded-xl px-3 py-3" style={{
              background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
              border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #eef2f6",
            }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[11px] font-bold shrink-0" style={{
                background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                boxShadow: "0 2px 8px rgba(99,102,241,0.2)",
                fontFamily: hd,
              }}>
                {user.username[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold truncate leading-tight" style={{ fontFamily: hd, color: isDark ? "white" : "#0f172a" }}>{user.username}</p>
                <p className="truncate leading-tight mt-0.5" style={{ fontFamily: mono, fontSize: 10, color: isDark ? "#475569" : "#94a3b8" }}>{user.email}</p>
              </div>
              <button onClick={handleLogout} title="Log out" className="shrink-0 p-1.5 rounded-lg transition-colors" style={{ color: isDark ? "#475569" : "#94a3b8" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
              </button>
            </div>
          </div>

          {/* Bottom status */}
          <div className="mt-auto px-2">
            <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{
              background: isDark ? "rgba(255,255,255,0.015)" : "#fafbfc",
              border: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid #f1f5f9",
            }}>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#22d3ee", boxShadow: "0 0 6px rgba(34,211,238,0.5)", animation: "subtlePulse 3s ease-in-out infinite" }} />
                <span className="text-[10px] font-medium" style={{ fontFamily: mono, color: isDark ? "#64748b" : "#94a3b8" }}>Gloom EVM</span>
              </div>
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded" style={{
                background: isDark ? "rgba(34,211,238,0.08)" : "rgba(6,182,212,0.06)",
                color: "#22d3ee", fontFamily: mono,
              }}>v1.0</span>
            </div>
          </div>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden" style={{ background: isDark ? "#060a10" : "#f8fafc" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeNav}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </ThemeCtx.Provider>
  );
}
